import React, { useState, useEffect, useContext, createContext, useCallback, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { ToastProvider, useToast } from './components/Toast';
import ForgotPassword from './pages/ForgotPassword';
import EmployeeSetup from './pages/EmployeeSetup';
import ResetPassword from './pages/ResetPassword';
import CheckEmail from './pages/CheckEmail';
import VerifyEmail from './pages/VerifyEmail';
import api, { getErrorMessage } from './api';
import OnboardingChecklist from './components/OnboardingChecklist';
import Schedule from './pages/Schedule';
import AffiliatePortal from './pages/AffiliatePortal';
import { LayoutGrid, Hammer, BookCopy, Clock, CalendarDays, BookUser, ChartColumnBig, Link, Cog, BarChart3, HardHat, Layers, FileText, DollarSign, Users, RefreshCw, Wrench, Smartphone } from 'lucide-react';

// ─── Global CSS ───────────────────────────────────────────────────────────────
const G = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
button,a,[role="button"]{touch-action:manipulation}
:root{--navy:#1a2535;--accent:#4a9edd;--muted:#7aa3c0;--surface:#f9fafb;--border:#e5e7eb;--divider:#f3f4f6;--sub:#6b7280;--green:#10b981;--red:#ef4444;--amber:#f59e0b}
html,body,#root{height:100%;overflow:hidden}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;font-size:14px;background:var(--surface);color:#111827}
button,input,select,textarea{font-family:inherit;-webkit-appearance:none}
::-webkit-scrollbar{width:5px;height:5px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:#d1d5db;border-radius:3px}
@keyframes slideUp{from{transform:translateY(22px);opacity:0}to{transform:translateY(0);opacity:1}}
@keyframes popIn{from{transform:scale(.88);opacity:0}to{transform:scale(1);opacity:1}}
@keyframes fadeIn{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
@keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}
.card{background:#fff;border:1px solid var(--border);border-radius:14px;box-shadow:0 1px 4px rgba(0,0,0,.06)}
.badge{display:inline-flex;align-items:center;gap:4px;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700;white-space:nowrap}
table{width:100%;border-collapse:collapse;font-size:13px}
th{text-align:left;padding:6px 10px;color:var(--sub);font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:.04em;white-space:nowrap;border-bottom:1px solid var(--divider)}
td{padding:11px 10px;border-bottom:1px solid var(--divider)}
tr:last-child td{border-bottom:none}
tr.ch:hover td{background:var(--surface);cursor:pointer}
.tbl-wrap{overflow-x:auto}
.field{width:100%;padding:11px 14px;border-radius:10px;border:1.5px solid var(--border);font-size:14px;color:var(--navy);outline:none;background:#fff;font-family:inherit;transition:border-color .15s}
.field:focus{border-color:var(--accent)}
.fl{font-size:12px;font-weight:700;color:var(--sub);text-transform:uppercase;letter-spacing:.05em;margin-bottom:6px;display:block}
.sh{font-weight:700;color:var(--navy);font-size:14px}
.ss{font-size:12px;color:var(--sub);margin-top:2px;margin-bottom:14px}
.mc{background:var(--surface);border-radius:10px;padding:14px;border:1px solid var(--border);margin-bottom:10px}
.mc:last-child{margin-bottom:0}
.mc.tap{cursor:pointer;transition:border-color .15s}
.mc.tap:hover,.mc.tap:active,.mc.tap:focus-visible{border-color:#388FFF;outline:none}
@media(max-width:767px){table{min-width:500px}}
`;

// ─── Auth ─────────────────────────────────────────────────────────────────────
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);
export { useAuth };

function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem('jobhubToken'));
  const [company, setCompany] = useState(() => JSON.parse(localStorage.getItem('jobhubCompany') || 'null'));
  const login  = (t, c) => { localStorage.setItem('jobhubToken', t); localStorage.setItem('jobhubCompany', JSON.stringify(c)); setToken(t); setCompany(c); };
  const logout = ()     => { localStorage.removeItem('jobhubToken'); localStorage.removeItem('jobhubCompany'); setToken(null); setCompany(null); };

  // Refresh company (incl. plan) from the server on load/token change —
  // localStorage can be stale after a plan change on another tab/device.
  useEffect(() => {
    if (!token) return;
    api.get('/auth/me').then(r => {
      setCompany(prev => {
        const merged = { ...prev, ...r.data };
        localStorage.setItem('jobhubCompany', JSON.stringify(merged));
        return merged;
      });
    }).catch(() => {}); // stale cached company is an acceptable fallback
  }, [token]);

  return <AuthCtx.Provider value={{ token, company, login, logout }}>{children}</AuthCtx.Provider>;
}

// ─── Toast (from components/Toast.js) ────────────────────────────────────────
// Imported below with other page imports

// ─── Status picker overlay ────────────────────────────────────────────────────
function Picker({ type, id, current, onClose, onApply }) {
  const mobile = window.innerWidth < 768;
  const opts = type === 'job' ? ['active', 'completed', 'on_hold'] : ['lead', 'active', 'vip', 'inactive'];
  const SC = { active: { bg: '#dbeafe', color: '#1e40af' }, completed: { bg: '#d1fae5', color: '#065f46' }, on_hold: { bg: '#fef3c7', color: '#92400e' }, lead: { bg: '#fef3c7', color: '#92400e' }, vip: { bg: '#fce7f3', color: '#9d174d' }, inactive: { bg: '#f3f4f6', color: '#374151' } };
  const lbl = s => s === 'on_hold' ? 'On Hold' : s.charAt(0).toUpperCase() + s.slice(1);
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(26,37,53,.5)', backdropFilter: 'blur(3px)', zIndex: 3000, display: 'flex', alignItems: mobile ? 'flex-end' : 'center', justifyContent: 'center', padding: mobile ? 0 : 24 }}>
      <div style={{ background: '#fff', borderRadius: mobile ? '20px 20px 0 0' : 16, width: '100%', maxWidth: mobile ? '100%' : 360, padding: mobile ? '24px 22px 40px' : 24, boxShadow: '0 16px 48px rgba(26,37,53,.25)', animation: mobile ? 'slideUp .3s ease' : 'popIn .25s ease' }}>
        {mobile && <div style={{ width: 36, height: 4, background: 'var(--border)', borderRadius: 2, margin: '0 auto 18px' }} />}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 15 }}>Update Status</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--sub)', cursor: 'pointer', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        {opts.map(o => {
          const sc = SC[o] || {};
          const sel = o === current;
          return (
            <button key={o} onClick={() => onApply(o)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${sel ? sc.color + '66' : 'var(--border)'}`, background: sel ? sc.bg : 'var(--surface)', marginBottom: 8, width: '100%', textAlign: 'left', transition: 'all .15s' }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: sc.color, flex: 1 }}>{lbl(o)}</span>
              {sel && <span style={{ fontWeight: 800, color: sc.color }}>✓</span>}
            </button>
          );
        })}
        <p style={{ fontSize: 12, color: 'var(--sub)', textAlign: 'center', marginTop: 14 }}>Tap a status to update — changes save instantly</p>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt     = n => { if (!n && n !== 0) return '$0'; if (n >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M'; if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K'; return '$' + Math.round(n).toLocaleString(); };
const fmtFull = n => { if (!n && n !== 0) return '$0.00'; return (n < 0 ? '-$' : '$') + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); };
const SC  = { active: { bg: '#dbeafe', color: '#1e40af' }, completed: { bg: '#d1fae5', color: '#065f46' }, on_hold: { bg: '#fef3c7', color: '#92400e' }, paid: { bg: '#d1fae5', color: '#065f46' }, overdue: { bg: '#fee2e2', color: '#991b1b' }, sent: { bg: '#ede9fe', color: '#5b21b6' }, draft: { bg: '#f3f4f6', color: '#374151' }, partial: { bg: '#fef3c7', color: '#92400e' } };
function Badge({ status }) {
  const s = SC[status] || { bg: '#f3f4f6', color: '#374151' };
  const l = status === 'on_hold' ? 'On Hold' : status.charAt(0).toUpperCase() + status.slice(1);
  return <span className="badge" style={{ background: s.bg, color: s.color }}>{l}</span>;
}
function Skel({ w = '100%', h = 16, mb = 0 }) {
  return <div style={{ width: w, height: h, borderRadius: 6, marginBottom: mb, background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', backgroundSize: '800px 100%', animation: 'shimmer 1.4s infinite linear' }} />;
}

// ─── Plan pricing (display only — Stripe price IDs/amounts are the real
// source of truth server-side, see Backend/config/plans.js) ──────────────────
const PLAN_PRICES = {
  solo:     { monthly: '$99/mo',  annual: '$990/yr' },
  founder:  { monthly: '$199/mo', annual: '$1,990/yr' },
  standard: { monthly: '$249/mo', annual: '$2,490/yr' },
};

// ─── Nav ──────────────────────────────────────────────────────────────────────
const NAV = [
  { key: 'dashboard',    label: 'Dashboard',      short: 'Home',     icon: LayoutGrid,      path: '/' },
  { key: 'jobs',         label: 'Job Costing',     short: 'Jobs',     icon: Hammer,          path: '/jobs' },
  { key: 'costs',        label: 'Cost Matching',   short: 'Costs',    icon: BookCopy,        path: '/costs' },
  { key: 'time',         label: 'Time Tracking',   short: 'Time',     icon: Clock,           path: '/time' },
  { key: 'schedule',     label: 'Schedule',        short: 'Schedule', icon: CalendarDays,    path: '/schedule' },
  { key: 'clients',      label: 'Clients',         short: 'Clients',  icon: BookUser,        path: '/clients' },
  { key: 'reports',      label: 'Reports',         short: 'Reports',  icon: ChartColumnBig,  path: '/reports' },
  { key: 'integrations', label: 'Integrations',    short: 'Connect',  icon: Link,            path: '/integrations' },
];
const TITLES = { '/': 'Dashboard', '/jobs': 'Job Costing', '/costs': 'Cost Matching', '/jobcost': 'Job Cost Setup', '/time': 'Time Tracking', '/clients': 'Clients', '/reports': 'Reports', '/integrations': 'Integrations', '/settings': 'Settings', '/onboarding': 'Setup Guide' };

// ─── Shell ────────────────────────────────────────────────────────────────────
// ─── TOUR CONTEXT ─────────────────────────────────────────────────────────────
const TourCtx = createContext({ startTour: () => {} });
const useTour = () => useContext(TourCtx);

// ─── INDUSTRY CONTEXT ─────────────────────────────────────────────────────────
const IndustryCtx = createContext({ roles: [], costCategories: [], industry: 'electrical', reload: () => {} });
const useIndustry = () => useContext(IndustryCtx);

function IndustryProvider({ children }) {
  const [config, setConfig] = useState({ roles: [], costCategories: [], industry: 'electrical', allIndustries: {} });
  const { token } = useAuth();
  const load = () => {
    if (!token) return;
    api.get('/auth/industry').then(r => setConfig(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [token]);
  return (
    <IndustryCtx.Provider value={{ ...config, reload: load }}>
      {children}
    </IndustryCtx.Provider>
  );
}

// Scrolls the nearest scrollable parent to the top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Immediate scroll
    const el = document.getElementById('main-scroll');
    if (el) el.scrollTop = 0;
    // Also fire after async content loads (onboarding checklist etc)
    const t = setTimeout(() => {
      const el2 = document.getElementById('main-scroll');
      if (el2) el2.scrollTop = 0;
    }, 300);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
}

// ─── Admin dashboard — permanent dark theme (scoped to /admin only) ───────────
const AC = {
  bg: '#0a0d12',
  card: '#12161d',
  cardBorder: '#1e232c',
  text: '#f5f6f7',
  sub: '#7d838f',
  label: '#5b93c9',
  accent: '#4a9edd',
  success: '#10b981',
  successCardBg: '#0d1a16',
  successCardBorder: '#17352b',
  danger: '#d97878',
  dangerBg: '#241213',
  amber: '#d9a441',
  amberBg: '#241d0f',
};

function adminStatusStyle(status) {
  if (status === 'active') return { color: AC.success, bg: 'rgba(16,185,129,.12)' };
  if (status === 'trialing') return { color: AC.amber, bg: AC.amberBg };
  if (status === 'cancelled' || status === 'past_due') return { color: AC.danger, bg: AC.dangerBg };
  return { color: AC.sub, bg: '#1b2028' };
}

function AdminSectionLabel({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: AC.label, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
      {children}
    </div>
  );
}

function AffiliatesSection({ toast }) {
  const [affiliates, setAffiliates] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showAdd, setShowAdd] = React.useState(false);
  const [form, setForm] = React.useState({ name: '', business_name: '', email: '', referral_code: '' });
  const [codeEdited, setCodeEdited] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    api.get('/admin/affiliates')
      .then(r => setAffiliates(r.data.affiliates || []))
      .catch(() => toast.error('Failed to load affiliates'))
      .finally(() => setLoading(false));
  }, [toast]);

  React.useEffect(() => { load(); }, [load]);

  // Live referral-code suggestion while typing name/business name, unless the
  // admin has already hand-edited the code field.
  React.useEffect(() => {
    if (codeEdited || !showAdd) return;
    if (!form.name && !form.business_name) { setForm(f => ({ ...f, referral_code: '' })); return; }
    const t = setTimeout(() => {
      api.get('/admin/affiliates/suggest-code', { params: { name: form.name, business_name: form.business_name } })
        .then(r => setForm(f => (codeEdited ? f : { ...f, referral_code: r.data.referral_code })))
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [form.name, form.business_name, codeEdited, showAdd]);

  const resetForm = () => { setForm({ name: '', business_name: '', email: '', referral_code: '' }); setCodeEdited(false); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSaving(true);
    try {
      const res = await api.post('/admin/affiliates', form);
      if (res.data.email_sent) {
        toast.success('Affiliate added — login link sent');
      } else {
        toast.error('Affiliate added, but the login link email failed to send' + (res.data.email_error ? ': ' + res.data.email_error : '') + '. They won\'t be able to log in until this is resolved.');
      }
      resetForm();
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to add affiliate'));
    } finally { setSaving(false); }
  };

  const toggleFlag = async (aff, field) => {
    try {
      await api.patch('/admin/affiliates/' + aff.id, { [field]: !aff[field] });
      load();
    } catch {
      toast.error('Update failed');
    }
  };

  const copyLink = (aff) => {
    const url = `https://jobhubco.com/?ref=${aff.referral_code}`;
    navigator.clipboard.writeText(url).then(() => { setCopiedId(aff.id); setTimeout(() => setCopiedId(null), 1500); });
  };

  return (
    <div>
      <AdminSectionLabel>Affiliates</AdminSectionLabel>
      <div style={{ background: AC.card, border: '0.5px solid ' + AC.cardBorder, borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '0.5px solid ' + AC.cardBorder, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: AC.text, flex: 1 }}>Affiliates ({affiliates.length})</div>
          <button onClick={() => setShowAdd(s => !s)} style={{ background: AC.accent, color: '#0a0d12', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {showAdd ? 'Cancel' : '+ Add Affiliate'}
          </button>
        </div>

        {showAdd && (
          <form onSubmit={submit} style={{ padding: '16px 20px', borderBottom: '0.5px solid ' + AC.cardBorder, display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: AC.sub, display: 'block', marginBottom: 4 }}>Name *</label>
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + AC.cardBorder, background: AC.bg, color: AC.text, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: AC.sub, display: 'block', marginBottom: 4 }}>Business name</label>
              <input value={form.business_name} onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + AC.cardBorder, background: AC.bg, color: AC.text, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: AC.sub, display: 'block', marginBottom: 4 }}>Email *</label>
              <input required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + AC.cardBorder, background: AC.bg, color: AC.text, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: AC.sub, display: 'block', marginBottom: 4 }}>Referral code</label>
              <input value={form.referral_code} onChange={e => { setCodeEdited(true); setForm(f => ({ ...f, referral_code: e.target.value })); }}
                placeholder="auto-suggested"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + AC.cardBorder, background: AC.bg, color: AC.text, fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <button type="submit" disabled={saving} style={{ background: AC.success, color: '#0a0d12', border: 'none', borderRadius: 6, padding: '9px 0', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              {saving ? 'Saving...' : 'Save & send link'}
            </button>
          </form>
        )}

        {loading ? (
          <div style={{ padding: 20, color: AC.sub, fontSize: 13 }}>Loading affiliates...</div>
        ) : !affiliates.length ? (
          <div style={{ padding: 20, color: AC.sub, fontSize: 13 }}>No affiliates yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: AC.bg }}>
                  {['Name', 'Business', 'Email', 'Referral Link', 'Active Clients', 'W-9', 'Contract'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: AC.sub, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {affiliates.map(a => (
                  <tr key={a.id} style={{ borderTop: '0.5px solid ' + AC.cardBorder }}>
                    <td style={{ padding: '10px 16px', color: AC.text, fontWeight: 600 }}>{a.name}</td>
                    <td style={{ padding: '10px 16px', color: AC.sub }}>{a.business_name || '—'}</td>
                    <td style={{ padding: '10px 16px', color: AC.sub }}>{a.email}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <code style={{ fontSize: 11, color: AC.accent }}>?ref={a.referral_code}</code>
                        <button onClick={() => copyLink(a)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, border: '1px solid ' + AC.cardBorder, background: AC.bg, cursor: 'pointer', fontWeight: 700, color: AC.text }}>
                          {copiedId === a.id ? 'Copied ✓' : 'Copy'}
                        </button>
                      </div>
                    </td>
                    <td style={{ padding: '10px 16px', color: AC.sub }}>{a.active_client_count}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <button onClick={() => toggleFlag(a, 'w9_received')}
                        style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, border: 'none', fontWeight: 700, cursor: 'pointer', background: a.w9_received ? 'rgba(16,185,129,.12)' : AC.dangerBg, color: a.w9_received ? AC.success : AC.danger }}>
                        {a.w9_received ? '✓ Received' : 'Missing'}
                      </button>
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      <button onClick={() => toggleFlag(a, 'contract_signed')}
                        style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, border: 'none', fontWeight: 700, cursor: 'pointer', background: a.contract_signed ? 'rgba(16,185,129,.12)' : AC.dangerBg, color: a.contract_signed ? AC.success : AC.danger }}>
                        {a.contract_signed ? '✓ Signed' : 'Missing'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AdminPanel({ company, logout }) {
  const [stats, setStats] = React.useState(null);
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [acting, setActing] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);
  const [menuOpenId, setMenuOpenId] = React.useState(null);
  const toast = useToast();
  React.useEffect(() => {
    Promise.all([api.get('/admin/dashboard'), api.get('/admin/clients')])
      .then(([s, c]) => { setStats(s.data); setClients(Array.isArray(c.data) ? c.data : (c.data.clients || [])); })
      .catch(() => toast.error('Failed to load admin data'))
      .finally(() => setLoading(false));
  }, []);
  const doAction = async (id, type) => {
    setMenuOpenId(null);
    setActing(id + type);
    try {
      if (type === 'delete') {
        if (!true) { setActing(null); return; }
        await api.delete('/admin/clients/' + id);
      } else if (type === 'extend_trial') {
        await api.patch('/admin/clients/' + id + '/extend-trial');
      } else {
        const body = type === 'activate'
          ? { subscription_status: 'active', trial_ends_at: null }
          : { subscription_status: 'cancelled' };
        await api.patch('/admin/clients/' + id, body);
      }
      const c = await api.get('/admin/clients');
      setClients(Array.isArray(c.data) ? c.data : (c.data.clients || []));
      toast.success('Updated');
    } catch(e) {
      toast.error((e.response && e.response.data && e.response.data.error) || 'Action failed');
    } finally {
      setActing(null);
    }
  };
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => { const fn = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', fn); return () => window.removeEventListener('resize', fn); }, []);
  const filtered = clients.filter(c => !search || c.email.includes(search) || (c.name || '').toLowerCase().includes(search.toLowerCase()));
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: 18, background: AC.bg, color: AC.text }}>Loading admin...</div>;
  const s = stats || {};
  const counts = s.counts || {};
  const mrr = s.mrr || 0;
  const payoutsOwed = s.affiliatePayoutsOwed || 0;
  const netAfterPayouts = mrr - payoutsOwed;
  const fmtMoney = (n) => '$' + Math.round(n).toLocaleString();

  const snapshotCards = [
    { label: 'MRR', val: fmtMoney(mrr), color: AC.accent },
    { label: 'Total Accounts', val: counts.total || 0, color: AC.text },
    { label: 'Active', val: counts.active || 0, color: AC.success },
    { label: 'Trialing', val: counts.trialing || 0, color: AC.amber },
    { label: 'Churn Rate', val: (s.churn ? s.churn.rate || 0 : 0) + '%', color: AC.text },
    { label: 'Founding Spots', val: s.foundingSpots || 0, color: AC.text },
    { label: 'Affiliate payouts owed', val: fmtMoney(payoutsOwed), color: AC.success, variant: 'payout' },
    { label: 'Net after payouts', val: fmtMoney(netAfterPayouts), color: AC.accent },
  ];

  const displayedClients = search ? filtered : (expanded ? filtered : filtered.slice(0, 3));
  const hiddenCount = filtered.length - 3;

  const mobileActions = [
    { key: 'activate', label: 'Activate', color: AC.success },
    { key: 'extend_trial', label: '+7 days', color: AC.amber },
    { key: 'lock', label: 'Lock', color: AC.danger },
    { key: 'delete', label: 'Delete', color: AC.text },
  ];

  return (
    <div style={{ height: '100dvh', background: AC.bg, padding: isMobile ? 12 : 32, overflowY: 'auto', boxSizing: 'border-box', paddingBottom: isMobile ? 80 : 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: AC.text }}>JobHub Admin</div>
          <div style={{ fontSize: 12, color: AC.sub }}>{company && company.email}</div>
        </div>
        <button onClick={logout} style={{ background: 'none', border: '1px solid ' + AC.cardBorder, borderRadius: 8, padding: '8px 16px', color: AC.sub, cursor: 'pointer', fontSize: 13 }}>Sign out</button>
      </div>

      <div style={{ marginBottom: 28 }}>
        <AdminSectionLabel>Financial snapshot</AdminSectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 12 }}>
          {snapshotCards.map(({ label, val, color, variant }) => (
            <div key={label} style={{
              background: variant === 'payout' ? AC.successCardBg : AC.card,
              border: '0.5px solid ' + (variant === 'payout' ? AC.successCardBorder : AC.cardBorder),
              borderRadius: 10, padding: '18px 20px', minWidth: 0,
            }}>
              <div style={{ fontSize: 11, color: AC.sub, marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color }}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 28 }}>
        <AffiliatesSection toast={toast} />
      </div>

      <div>
        <AdminSectionLabel>Accounts</AdminSectionLabel>
        <div style={{ background: AC.card, border: '0.5px solid ' + AC.cardBorder, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '0.5px solid ' + AC.cardBorder, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: AC.text, flex: 1 }}>Accounts ({filtered.length})</div>
            <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ border: '1px solid ' + AC.cardBorder, borderRadius: 8, padding: '6px 12px', fontSize: 13, width: isMobile ? '100%' : 220, maxWidth: isMobile ? 160 : 220, background: AC.bg, color: AC.text }} />
          </div>
          {isMobile ? (
            <div>
              {displayedClients.map(c => {
                const st = adminStatusStyle(c.subscription_status);
                return (
                  <div key={c.id} style={{ borderTop: '0.5px solid ' + AC.cardBorder, padding: '14px 16px', position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: AC.text, fontSize: 14 }}>{c.name || '-'}</div>
                        <div style={{ color: AC.sub, fontSize: 12 }}>{c.email}</div>
                        <span style={{ background: st.bg, color: st.color, borderRadius: 6, padding: '3px 8px', fontWeight: 700, fontSize: 11, textTransform: 'capitalize', whiteSpace: 'nowrap', alignSelf: 'flex-start', marginTop: 2 }}>{c.subscription_status}</span>
                      </div>
                      <button onClick={() => setMenuOpenId(m => m === c.id ? null : c.id)} style={{ background: AC.bg, border: '1px solid ' + AC.cardBorder, color: AC.text, borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 16, fontWeight: 700, flexShrink: 0 }}>⋯</button>
                    </div>
                    <div style={{ fontSize: 12, color: AC.sub, marginTop: 8 }}>
                      Joined {new Date(c.created_at).toLocaleDateString()}{c.trial_ends_at ? ' · Trial ' + new Date(c.trial_ends_at).toLocaleDateString() : ''}
                    </div>
                    {menuOpenId === c.id && (
                      <>
                        <div onClick={() => setMenuOpenId(null)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                        <div style={{ position: 'absolute', right: 16, top: 48, background: AC.card, border: '0.5px solid ' + AC.cardBorder, borderRadius: 8, overflow: 'hidden', zIndex: 50, minWidth: 140, boxShadow: '0 8px 24px rgba(0,0,0,.5)' }}>
                          {mobileActions.map((a, i) => (
                            <button key={a.key} onClick={() => doAction(c.id, a.key)} disabled={!!acting}
                              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', borderTop: i === 0 ? 'none' : '0.5px solid ' + AC.cardBorder, color: a.color, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                              {a.label}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: AC.bg }}>
                    {['Name','Email','Plan','Status','Trial Ends','Joined','Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: AC.sub, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayedClients.map(c => {
                    const st = adminStatusStyle(c.subscription_status);
                    return (
                      <tr key={c.id} style={{ borderTop: '0.5px solid ' + AC.cardBorder }}>
                        <td style={{ padding: '10px 16px', color: AC.text, fontWeight: 600 }}>{c.name || '-'}</td>
                        <td style={{ padding: '10px 16px', color: AC.sub }}>{c.email}</td>
                        <td style={{ padding: '10px 16px', color: AC.sub, textTransform: 'capitalize' }}>{c.subscription_plan || '-'}</td>
                        <td style={{ padding: '10px 16px' }}><span style={{ background: st.bg, color: st.color, borderRadius: 6, padding: '2px 8px', fontWeight: 700, fontSize: 11, textTransform: 'capitalize' }}>{c.subscription_status}</span></td>
                        <td style={{ padding: '10px 16px', color: AC.sub }}>{c.trial_ends_at ? new Date(c.trial_ends_at).toLocaleDateString() : '-'}</td>
                        <td style={{ padding: '10px 16px', color: AC.sub }}>{new Date(c.created_at).toLocaleDateString()}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => doAction(c.id, 'activate')} disabled={!!acting} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: 'none', background: 'rgba(16,185,129,.12)', color: AC.success, cursor: 'pointer', fontWeight: 700 }}>Activate</button>
                            <button onClick={() => doAction(c.id, 'extend_trial')} disabled={!!acting} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: 'none', background: AC.amberBg, color: AC.amber, cursor: 'pointer', fontWeight: 700 }}>+7d</button>
                            <button onClick={() => doAction(c.id, 'lock')} disabled={!!acting} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: 'none', background: AC.dangerBg, color: AC.danger, cursor: 'pointer', fontWeight: 700 }}>Lock</button>
                            <button onClick={() => doAction(c.id, 'delete')} disabled={!!acting} style={{ fontSize: 11, padding: '4px 8px', borderRadius: 6, border: 'none', background: '#000', color: AC.text, cursor: 'pointer', fontWeight: 700 }}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!search && hiddenCount > 0 && (
            <div style={{ padding: '12px 20px', borderTop: '0.5px solid ' + AC.cardBorder }}>
              <button onClick={() => setExpanded(e => !e)} style={{ background: 'none', border: 'none', color: AC.accent, fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: 0 }}>
                {expanded ? 'Show less' : `+${hiddenCount} more`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}function Shell({ children }) {
  const { company, logout } = useAuth(); if (company?.email === 'peyton@jobhubco.com') return <AdminPanel company={company} logout={logout} />;
  const navigate = useNavigate();
  const location = useLocation();
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  const [tourActive, setTourActive] = useState(false);
  useEffect(() => { const fn = () => setMobile(window.innerWidth < 768); window.addEventListener('resize', fn); return () => window.removeEventListener('resize', fn); }, []);
  const cur = NAV.find(n => n.path === location.pathname)?.key || 'dashboard';
  const isSolo = company?.plan === 'solo';
  // Solo plan has no time tracking or scheduling/employee features
  const visibleNav = isSolo ? NAV.filter(n => n.key !== 'time' && n.key !== 'schedule') : NAV;

  return (
    <TourCtx.Provider value={{ startTour: () => setTourActive(true) }}>
    <div style={{ display: 'flex', height: '100dvh', overflow: 'hidden' }}>

      {/* Sidebar */}
      {!mobile && (
        <aside style={{ width: 220, background: 'var(--navy)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '20px 18px 16px', borderBottom: '1px solid rgba(255,255,255,.07)', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <img src="/jobhub-logo-dark.png" alt="JobHub" style={{ height: 90, width: 'auto', objectFit: 'contain' }} />
           <div style={{ color: 'var(--muted)', fontSize: 11, textAlign: 'center' }}>{company?.name?.split(' ').slice(0, 2).join(' ')}</div>
          </div>
          <nav style={{ flex: 1, padding: '10px 10px 0', overflowY: 'auto' }}>
            {visibleNav.map(n => (
              <div key={n.key} onClick={() => navigate(n.path)}
                data-tour={`nav-${n.key}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 9, marginBottom: 2, cursor: 'pointer', fontSize: 14, fontWeight: cur === n.key ? 700 : 400, color: cur === n.key ? 'var(--accent)' : 'rgba(255,255,255,.55)', background: cur === n.key ? 'rgba(74,158,221,.18)' : 'transparent', transition: 'background .15s,color .15s', userSelect: 'none' }}
                onMouseEnter={e => { if (cur !== n.key) e.currentTarget.style.background = 'rgba(255,255,255,.07)'; }}
                onMouseLeave={e => { if (cur !== n.key) e.currentTarget.style.background = 'transparent'; }}>
                <span style={{ flexShrink: 0 }}><n.icon size={16} /></span>{n.label}
              </div>
            ))}
          </nav>
          <div style={{ padding: '12px 10px 18px', borderTop: '1px solid rgba(255,255,255,.07)' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 0 }}>
              <button onClick={() => navigate('/settings')} title="Settings" style={{ flex: '0 0 38px', padding: '9px 0', borderRadius: 9, fontSize: 16, background: cur === 'settings' ? 'rgba(74,158,221,.18)' : 'rgba(255,255,255,.07)', color: cur === 'settings' ? 'var(--accent)' : 'rgba(255,255,255,.5)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Cog size={16} /></button>
              <button onClick={logout} style={{ flex: 1, padding: '9px 0', borderRadius: 9, fontSize: 13, fontWeight: 600, background: 'rgba(255,255,255,.07)', color: 'rgba(255,255,255,.5)', border: 'none', cursor: 'pointer' }}>Sign out</button>
            </div>
          </div>
        </aside>
      )}

      {/* Main */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        <header style={{ background: '#fff', borderBottom: '1px solid var(--border)', padding: mobile ? '11px 16px' : '13px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: 10 }}>
          <div>
            <div style={{ fontSize: mobile ? 15 : 16, fontWeight: 800, color: 'var(--navy)', letterSpacing: '-0.3px' }}>{TITLES[location.pathname] || 'JobHub'}</div>
            <div style={{ fontSize: 12, color: 'var(--sub)', marginTop: 1 }}>{company?.name}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {company?.isDemo && <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 10, fontWeight: 800, padding: '4px 9px', borderRadius: 20, letterSpacing: '.04em' }}>DEMO</span>}
          </div>
        </header>
        <main id="main-scroll" style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: mobile ? '14px 14px 80px' : '22px 24px 24px' }}>
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      {mobile && (
        <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: 62, background: '#fff', borderTop: '1px solid var(--border)', zIndex: 900, display: 'flex', boxShadow: '0 -2px 12px rgba(0,0,0,.07)' }}>
          {visibleNav.map(n => (
            <button key={n.key} onClick={() => navigate(n.path)} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, background: 'none', border: 'none', borderTop: `2px solid ${cur === n.key ? 'var(--accent)' : 'transparent'}`, cursor: 'pointer', color: cur === n.key ? 'var(--accent)' : 'var(--sub)', fontSize: 10, fontWeight: cur === n.key ? 700 : 500, padding: 0 }}>
              <n.icon size={19} />{n.short}
            </button>
          ))}
          <button onClick={() => navigate('/settings')} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, background: 'none', border: 'none', borderTop: `2px solid ${cur === 'settings' ? 'var(--accent)' : 'transparent'}`, cursor: 'pointer', color: cur === 'settings' ? 'var(--accent)' : 'var(--sub)', fontSize: 10, fontWeight: cur === 'settings' ? 700 : 500, padding: 0 }}>
            <Cog size={19} />Settings
          </button>
        </nav>
      )}

      {/* App Tour — lives in Shell so survives page navigation */}
      {tourActive && <AppTour onClose={() => setTourActive(false)} />}
    </div>
    </TourCtx.Provider>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KPI({ label, icon, val, sub, up, onClick }) {
  return (
    <div className="card" onClick={onClick} style={{ flex: '1 1 130px', padding: '16px 18px', cursor: onClick ? 'pointer' : 'default', transition: 'box-shadow .15s', ...(onClick ? { ':hover': { boxShadow: '0 4px 12px rgba(0,0,0,.1)' } } : {}) }}>
      <div style={{ fontSize: 11, color: 'var(--sub)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)', marginBottom: 4, letterSpacing: '-0.5px' }}>{val}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: up === true ? 'var(--green)' : up === false ? 'var(--red)' : 'var(--sub)' }}>
        {sub}{onClick && <span style={{ marginLeft: 4, color: 'var(--accent)', fontSize: 10 }}>→</span>}
      </div>
    </div>
  );
}

// ─── APP WALKTHROUGH ──────────────────────────────────────────────────────────
// ─── APP TOUR (tooltip bubble overlay) ───────────────────────────────────────
function AppTour({ onClose }) {
  const [step, setStep] = useState(0);
  const [bubblePos, setBubblePos] = useState({ top: 0, left: 0, placement: 'bottom' });
  const mobile = window.innerWidth < 768;
  const navigate = useNavigate();

  const STEPS = [
    { selector: null,              page: '/',            title: 'Welcome to JobHub 👋', desc: 'This quick tour highlights every key feature. We\'ll walk through each section so you know exactly where everything lives. Tap Next to begin.', placement: 'center' },
    { selector: '[data-tour="kpis"]',      page: '/',   title: 'Dashboard KPIs', desc: 'These 4 cards show your total revenue, profit margin, active job count, and outstanding AR. Tap any card to jump to that section.', placement: 'bottom' },
    { selector: '[data-tour="pie"]',       page: '/',   title: 'Cost Breakdown', desc: 'This donut chart shows how your total costs split between labor, materials, and overhead across all jobs.', placement: 'top' },
    { selector: '[data-tour="jobs-chart"]',page: '/jobs', title: 'Budget vs Spent Chart', desc: 'Each bar shows a job\'s full budget (outline), amount spent (solid fill), and remaining budget (tinted gap). Scroll right to see all jobs.', placement: 'bottom' },
    { selector: '[data-tour="jobs-header"]',page: '/jobs', title: 'Jobs List', desc: 'See every job\'s revenue, actual cost, profit, and budget left. Toggle profit between $ and % using the button in the header. Tap a job name to drill into the full cost breakdown.', placement: 'bottom' },
    { selector: '[data-tour="costs-tabs"]',page: '/costs', title: 'Cost Matching', desc: 'Bills synced from QBO and labor from payroll land here when they can\'t be auto-matched. Tap "Assign →" to link any cost to the correct job in one tap.', placement: 'bottom' },
    { selector: '[data-tour="time-tabs"]', page: '/time', title: 'Time Tracking', desc: 'Review pending employee time entries here. Tap "Review →" to approve or edit hours. On approval, JobHub calculates gross pay and loaded cost and adds it to the job automatically.', placement: 'bottom' },
    { selector: '[data-tour="time-employees"]', page: '/time', title: 'Employee Roster', desc: 'Add employees with their hourly rate, overtime rate, and burden rate. They log in at app.jobhubco.com/employee with just their email and password.', placement: 'bottom' },
    { selector: '[data-tour="clients-list"]', page: '/clients', title: 'Client List', desc: 'All clients synced from QBO. Search by name, sort by balance or newest. Tap any row to see their full invoice history, jobs, and activity log.', placement: 'bottom' },
    { selector: '[data-tour="clients-cal"]', page: '/clients', title: 'Outreach Calendar', desc: 'Every call, email, meeting, and note logged against a client shows as a colored dot on this calendar. Tap any day to see what happened — and tap an activity to go straight to that client.', placement: 'bottom' },
    { selector: '[data-tour="reports-cards"]', page: '/reports', title: 'Reports', desc: 'Generate 5 report types as branded PDFs or Excel files. Click Preview to see the data before downloading. Set up auto-send to email reports to your bookkeeper on a schedule.', placement: 'bottom' },
    { selector: '[data-tour="nav-integrations"]', page: '/integrations', title: 'Integrations', desc: 'Connect QuickBooks Online and your payroll provider here. Once connected, JobHub syncs bills, invoices, clients, and labor costs automatically.', placement: 'bottom' },
    { selector: '[data-tour="settings-tabs"]', page: '/settings', title: 'Settings', desc: 'Update your company name, change your password, and manage your subscription. The Billing tab shows your next bill date and lets you update your payment method via Stripe.', placement: 'bottom' },
    { selector: null, page: null, title: 'You\'re all set! 🎉', desc: 'That\'s the full tour. Start by connecting QuickBooks in Integrations, run your first sync, and your jobs and clients will appear automatically. Need help? Email support@jobhubco.com', placement: 'center' },
  ];

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast  = step === STEPS.length - 1;

  // Navigate to the right page when step changes
  useEffect(() => {
    if (current.page) navigate(current.page);
  }, [step]);

  // Position bubble next to the target element
  useEffect(() => {
    if (!current.selector || current.placement === 'center') {
      setBubblePos({ placement: 'center' });
      return;
    }
    const tryFind = (attempts = 0) => {
      const el = document.querySelector(current.selector);
      if (!el) {
        if (attempts < 10) setTimeout(() => tryFind(attempts + 1), 150);
        else setBubblePos({ placement: 'center' });
        return;
      }
      // Scroll element into view first, then position bubble
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setTimeout(() => {
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const BUBBLE_W = mobile ? Math.min(300, vw - 32) : 320;
      const BUBBLE_H = 180;
      const GAP = 12;

      let placement = current.placement || 'bottom';
      let top, left;

      if (placement === 'bottom') {
        top = rect.bottom + GAP;
        left = rect.left + rect.width / 2 - BUBBLE_W / 2;
        if (top + BUBBLE_H > vh - 20) { placement = 'top'; top = rect.top - BUBBLE_H - GAP; }
      } else {
        top = rect.top - BUBBLE_H - GAP;
        left = rect.left + rect.width / 2 - BUBBLE_W / 2;
        if (top < 10) { placement = 'bottom'; top = rect.bottom + GAP; }
      }
      left = Math.max(12, Math.min(left, vw - BUBBLE_W - 12));
      top  = Math.max(10, top);

      setBubblePos({ top, left, placement, targetRect: rect });
      }, 350); // wait for scroll to complete
    };
    setTimeout(tryFind, 100);
  }, [step]);

  const next = () => { if (isLast) onClose(); else setStep(s => s + 1); };
  const prev = () => { if (!isFirst) setStep(s => s - 1); };

  const BUBBLE_W = mobile ? Math.min(300, window.innerWidth - 32) : 320;
  const isCentered = bubblePos.placement === 'center';

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 5000, pointerEvents: 'none' }}>
      {/* Dim overlay with cutout for target element */}
      {!isCentered && bubblePos.targetRect && (
        <>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,37,53,.55)', pointerEvents: 'all' }} onClick={onClose} />
          {/* Highlight cutout */}
          <div style={{
            position: 'absolute',
            top: bubblePos.targetRect.top - 4,
            left: bubblePos.targetRect.left - 4,
            width: bubblePos.targetRect.width + 8,
            height: bubblePos.targetRect.height + 8,
            borderRadius: 10,
            boxShadow: '0 0 0 9999px rgba(26,37,53,.55)',
            border: '2px solid var(--accent)',
            pointerEvents: 'none',
            zIndex: 1,
          }} />
        </>
      )}
      {isCentered && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,37,53,.7)', backdropFilter: 'blur(3px)', pointerEvents: 'all' }} onClick={onClose} />
      )}

      {/* Bubble */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute',
          pointerEvents: 'all',
          zIndex: 2,
          width: BUBBLE_W,
          background: '#fff',
          borderRadius: 14,
          padding: '18px 20px',
          boxShadow: '0 8px 32px rgba(26,37,53,.3)',
          animation: 'popIn .2s ease',
          ...(isCentered ? {
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
          } : {
            top: bubblePos.top,
            left: bubblePos.left,
          }),
        }}
      >
        {/* Arrow */}
        {!isCentered && (
          <div style={{
            position: 'absolute',
            width: 12, height: 12,
            background: '#fff',
            transform: 'rotate(45deg)',
            ...(bubblePos.placement === 'bottom'
              ? { top: -6, left: BUBBLE_W/2 - 6, borderTop: '1px solid var(--border)', borderLeft: '1px solid var(--border)' }
              : { bottom: -6, left: BUBBLE_W/2 - 6, borderBottom: '1px solid var(--border)', borderRight: '1px solid var(--border)' }
            ),
          }} />
        )}

        {/* Progress */}
        <div style={{ display: 'flex', gap: 3, marginBottom: 12 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{ flex: i === step ? 2 : 1, height: 3, borderRadius: 2, background: i <= step ? 'var(--accent)' : 'var(--border)', transition: 'all .2s' }} />
          ))}
        </div>

        {/* Content */}
        <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 14, marginBottom: 6 }}>{current.title}</div>
        <div style={{ fontSize: 13, color: 'var(--sub)', lineHeight: 1.6, marginBottom: 14 }}>{current.desc}</div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          {!isFirst && <button onClick={prev} style={{ padding: '8px 14px', background: 'var(--surface)', color: 'var(--sub)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>← Back</button>}
          <button onClick={next} style={{ flex: 1, padding: '8px 0', background: isLast ? 'var(--green)' : 'var(--navy)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            {isLast ? '✓ Done!' : 'Next →'}
          </button>
          {isFirst && <button onClick={onClose} style={{ padding: '8px 12px', background: 'none', border: 'none', color: 'var(--sub)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Skip</button>}
        </div>
        <div style={{ textAlign: 'center', fontSize: 10, color: 'var(--sub)', marginTop: 8 }}>{step + 1} of {STEPS.length}</div>
      </div>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────

// ─── REQUEST FEATURE BOX ─────────────────────────────────────────────────────
function RequestFeatureBox() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Nice to have');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit() {
    if (!title.trim() || !description.trim()) { setError('Please fill in both fields.'); return; }
    setLoading(true); setError('');
    try {
      const res = await api.post('/feature-requests', { title, description, priority });
      setSubmitted(true);
      setTitle(''); setDescription(''); setPriority('Nice to have');
    } catch(err) { setError(err.message || 'Failed to submit'); }
    finally { setLoading(false); }
  }

  function handleClose() { setOpen(false); setSubmitted(false); setError(''); }

  return (
    <div style={{marginTop:32,borderTop:'1px solid var(--border)',paddingTop:24}}>
      <div style={{background:'linear-gradient(135deg,#eff6ff 0%,#f0fdf4 100%)',border:'1px solid #bfdbfe',borderRadius:12,padding:'20px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
        <div>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
            <span style={{fontSize:20}}>💡</span>
            <span style={{fontWeight:700,fontSize:15,color:'#1e40af'}}>Have a feature idea?</span>
          </div>
          <p style={{margin:0,fontSize:13,color:'var(--sub)'}}>Help shape JobHub — your requests go directly to our team.</p>
        </div>
        <button onClick={()=>setOpen(true)} style={{background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,padding:'10px 20px',fontWeight:600,fontSize:14,cursor:'pointer',whiteSpace:'nowrap'}}>
          Request a Feature
        </button>
      </div>
      {open && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:16}} onClick={e=>{if(e.target===e.currentTarget)handleClose();}}>
          <div style={{background:'#fff',borderRadius:16,padding:32,width:'100%',maxWidth:480,boxShadow:'0 20px 60px rgba(0,0,0,0.2)'}}>
            {submitted ? (
              <div style={{textAlign:'center',padding:'16px 0'}}>
                <div style={{fontSize:48,marginBottom:12}}>🎉</div>
                <h3 style={{margin:'0 0 8px',color:'#111827'}}>Request Submitted!</h3>
                <p style={{color:'var(--sub)',margin:'0 0 24px'}}>Thanks! We review every request and will be in touch if we need more details.</p>
                <button onClick={handleClose} style={{background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,padding:'10px 24px',fontWeight:600,cursor:'pointer'}}>Done</button>
              </div>
            ) : (
              <>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
                  <h3 style={{margin:0,fontSize:18,color:'#111827'}}>💡 Request a Feature</h3>
                  <button onClick={handleClose} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'var(--sub)'}}>×</button>
                </div>
                <div style={{marginBottom:16}}>
                  <label style={{display:'block',fontWeight:600,fontSize:13,color:'var(--navy)',marginBottom:6}}>Feature Title *</label>
                  <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="e.g. Export contacts to CSV" className="field"/>
                </div>
                <div style={{marginBottom:16}}>
                  <label style={{display:'block',fontWeight:600,fontSize:13,color:'var(--navy)',marginBottom:6}}>Description *</label>
                  <textarea value={description} onChange={e=>setDescription(e.target.value)} placeholder="Describe what you'd like and why it would help..." rows={4} className="field" style={{resize:'vertical'}}/>
                </div>
                <div style={{marginBottom:20}}>
                  <label style={{display:'block',fontWeight:600,fontSize:13,color:'var(--navy)',marginBottom:6}}>Priority</label>
                  <div style={{display:'flex',gap:8}}>
                    {['Nice to have','High','Critical'].map(p=>(
                      <button key={p} onClick={()=>setPriority(p)} style={{flex:1,padding:'8px 4px',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',border:priority===p?'2px solid var(--accent)':'2px solid var(--border)',background:priority===p?'#eff6ff':'#fff',color:priority===p?'var(--accent)':'var(--sub)'}}>
                        {p==='Nice to have'?'👍 Nice to have':p==='High'?'🔥 High':'🚨 Critical'}
                      </button>
                    ))}
                  </div>
                </div>
                {error && <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:8,padding:'10px 12px',color:'var(--red)',fontSize:13,marginBottom:16}}>{error}</div>}
                <button onClick={handleSubmit} disabled={loading} style={{width:'100%',background:loading?'#93c5fd':'var(--accent)',color:'#fff',border:'none',borderRadius:8,padding:12,fontWeight:700,fontSize:15,cursor:loading?'not-allowed':'pointer'}}>
                  {loading?'Submitting...':'Submit Request'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── WIP REPORT ───────────────────────────────────────────────────────────────
function WIPReport() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');

  async function loadWIP() {
    setLoading(true); setError('');
    try {
      const r = await api.get('/jobs');
      const all = r.data?.jobs || r.data || [];
      setJobs(all.filter(j=>['active','in_progress','scheduled','In Progress','Active','Scheduled'].includes(j.status)));
      setOpen(true);
    } catch(err) { setError(err.message||'Failed to load'); }
    finally { setLoading(false); }
  }

  const fmtM = n=>`${(Number(n)||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const pct = (a,b)=>b>0?Math.round((a/b)*100):0;
  const budget = j=>(Number(j.budget_labor||j.budgetLabor||0)+Number(j.budget_materials||j.budgetMaterials||0)+Number(j.budget_other||j.budgetOther||0));
  const actual = j=>(Number(j.actual_labor||j.actualLabor||0)+Number(j.actual_materials||j.actualMaterials||0));
  const totBudget=jobs.reduce((s,j)=>s+budget(j),0);
  const totActual=jobs.reduce((s,j)=>s+actual(j),0);
  const totInvoiced=jobs.reduce((s,j)=>s+Number(j.invoiced_amount||j.invoicedAmount||0),0);

  function dlCSV(){
    const rows=[['Job','Status','Budget','Actual','Used %','Invoiced','Variance'],
      ...jobs.map(j=>[j.name,j.status,budget(j).toFixed(2),actual(j).toFixed(2),pct(actual(j),budget(j))+'%',(Number(j.invoiced_amount||j.invoicedAmount||0)).toFixed(2),(budget(j)-actual(j)).toFixed(2)])];
    const csv=rows.map(r=>r.map(v=>`"${v}"`).join(',')).join('\n');
    const a=document.createElement('a'); a.href='data:text/csv,'+encodeURIComponent(csv); a.download=`WIP_${new Date().toISOString().slice(0,10)}.csv`; a.click();
  }

  const barColor=u=>u>=100?'#ef4444':u>=80?'#f59e0b':'#22c55e';
  const badgeColor=u=>u>=100?{bg:'#fef2f2',txt:'#dc2626'}:u>=80?{bg:'#fffbeb',txt:'#d97706'}:{bg:'#f0fdf4',txt:'#16a34a'};

  return (
    <>
      <div onClick={loadWIP} style={{background:'#fff',border:'1px solid var(--border)',borderRadius:12,padding:'20px 24px',cursor:'pointer',display:'flex',alignItems:'center',gap:16,marginTop:12,transition:'all .15s'}}
        onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--accent)';e.currentTarget.style.boxShadow='0 4px 12px rgba(74,158,221,.12)';}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.boxShadow='none';}}>
        <div style={{background:'#f0fdf4',borderRadius:10,padding:10,fontSize:24}}>🚧</div>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:15,color:'var(--navy)',marginBottom:2}}>Work In Progress Report</div>
          <div style={{fontSize:13,color:'var(--sub)'}}>Active jobs — budget vs actual, variance & invoiced status</div>
        </div>
        {loading?<div style={{color:'var(--sub)',fontSize:13}}>Loading...</div>:<div style={{color:'var(--accent)',fontSize:20}}>›</div>}
      </div>
      {error&&<div style={{color:'var(--red)',fontSize:13,padding:'6px 0'}}>{error}</div>}
      {open&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'flex-start',justifyContent:'center',zIndex:9999,padding:'24px 16px',overflowY:'auto'}} onClick={e=>{if(e.target===e.currentTarget)setOpen(false);}}>
          <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:900,boxShadow:'0 20px 60px rgba(0,0,0,0.2)'}}>
            <div style={{padding:'24px 28px 0',display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <div>
                <h2 style={{margin:'0 0 4px',fontSize:20,color:'var(--navy)'}}>🚧 Work In Progress Report</h2>
                <p style={{margin:0,color:'var(--sub)',fontSize:13}}>{jobs.length} active job{jobs.length!==1?'s':''} · {new Date().toLocaleDateString()}</p>
              </div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={dlCSV} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 16px',fontSize:13,fontWeight:600,cursor:'pointer',color:'var(--navy)'}}>⬇ CSV</button>
                <button onClick={()=>setOpen(false)} style={{background:'none',border:'none',fontSize:22,cursor:'pointer',color:'var(--sub)',padding:'0 4px'}}>×</button>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,padding:'20px 28px'}}>
              {[['Total Budget',fmtM(totBudget),'#2563eb','#eff6ff'],['Total Actual',fmtM(totActual),'#7c3aed','#f5f3ff'],['Total Invoiced',fmtM(totInvoiced),'#059669','#ecfdf5']].map(([l,v,col,bg])=>(
                <div key={l} style={{background:bg,borderRadius:10,padding:'14px 16px'}}>
                  <div style={{fontSize:12,color:'var(--sub)',marginBottom:4}}>{l}</div>
                  <div style={{fontSize:18,fontWeight:700,color:col}}>{v}</div>
                </div>
              ))}
            </div>
            {jobs.length===0?(
              <div style={{padding:'32px 28px',textAlign:'center',color:'var(--sub)'}}>No active jobs found.</div>
            ):(
              <div style={{padding:'0 28px 28px',overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                  <thead>
                    <tr style={{borderBottom:'2px solid var(--border)'}}>
                      {['Job Name','Status','Budget','Actual','Used','Invoiced','Variance'].map(h=>(
                        <th key={h} style={{padding:'10px 12px',textAlign:h==='Job Name'?'left':'right',color:'var(--navy)',fontWeight:700,whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((j,i)=>{
                      const b=budget(j),a=actual(j),inv=Number(j.invoiced_amount||j.invoicedAmount||0),v=b-a,u=pct(a,b);
                      const bc=badgeColor(u);
                      return (
                        <tr key={j.id} style={{borderBottom:'1px solid var(--divider)',background:i%2===0?'#fff':'#fafafa'}}>
                          <td style={{padding:'12px',fontWeight:600,color:'var(--navy)',maxWidth:200}}>{j.name}</td>
                          <td style={{padding:'12px',textAlign:'right'}}><span style={{background:'var(--surface)',borderRadius:20,padding:'3px 10px',fontSize:11,fontWeight:600,color:'var(--sub)'}}>{j.status}</span></td>
                          <td style={{padding:'12px',textAlign:'right'}}>{fmtM(b)}</td>
                          <td style={{padding:'12px',textAlign:'right'}}>{fmtM(a)}</td>
                          <td style={{padding:'12px',textAlign:'right'}}>
                            <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4}}>
                              <span style={{background:bc.bg,color:bc.txt,borderRadius:20,padding:'2px 8px',fontSize:11,fontWeight:700}}>{u}%</span>
                              <div style={{width:60,height:4,background:'var(--border)',borderRadius:2}}><div style={{width:`${Math.min(u,100)}%`,height:'100%',background:barColor(u),borderRadius:2}}/></div>
                            </div>
                          </td>
                          <td style={{padding:'12px',textAlign:'right'}}>{fmtM(inv)}</td>
                          <td style={{padding:'12px',textAlign:'right',fontWeight:700,color:v>=0?'var(--green)':'var(--red)'}}>{v>=0?'+':''}{fmtM(v)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{borderTop:'2px solid var(--border)',background:'var(--surface)'}}>
                      <td colSpan={2} style={{padding:'12px',fontWeight:700,color:'var(--navy)'}}>TOTALS</td>
                      <td style={{padding:'12px',textAlign:'right',fontWeight:700}}>{fmtM(totBudget)}</td>
                      <td style={{padding:'12px',textAlign:'right',fontWeight:700}}>{fmtM(totActual)}</td>
                      <td style={{padding:'12px',textAlign:'right',fontWeight:700,color:badgeColor(pct(totActual,totBudget)).txt}}>{pct(totActual,totBudget)}%</td>
                      <td style={{padding:'12px',textAlign:'right',fontWeight:700}}>{fmtM(totInvoiced)}</td>
                      <td style={{padding:'12px',textAlign:'right',fontWeight:700,color:(totBudget-totActual)>=0?'var(--green)':'var(--red)'}}>{(totBudget-totActual)>=0?'+':''}{fmtM(totBudget-totActual)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── CRM TOGGLE (employer side — Team Lead employees only) ────────────────────
function CRMToggle({ employee, onUpdate }) {
  const [enabled, setEnabled] = useState(employee.crm_access||false);
  const [loading, setLoading] = useState(false);
  async function toggle() {
    setLoading(true);
    const val=!enabled;
    try {
      await api.patch(`/crm/access/${employee.id}`,{enabled:val});
      setEnabled(val);
      if(onUpdate) onUpdate(employee.id,val);
    } catch(err) { alert('Failed to update CRM access: '+err.message); }
    finally { setLoading(false); }
  }
  return (
    <div style={{display:'flex',alignItems:'center',gap:6}}>
      <span style={{fontSize:11,color:'var(--sub)',fontWeight:500}}>CRM</span>
      <button onClick={toggle} disabled={loading} title={enabled?'Disable CRM access':'Enable CRM access'}
        style={{position:'relative',width:36,height:20,borderRadius:10,border:'none',background:enabled?'var(--accent)':'#d1d5db',cursor:loading?'not-allowed':'pointer',transition:'background .2s',padding:0,flexShrink:0}}>
        <div style={{position:'absolute',top:2,left:enabled?18:2,width:16,height:16,borderRadius:'50%',background:'#fff',boxShadow:'0 1px 3px rgba(0,0,0,.2)',transition:'left .2s'}}/>
      </button>
      <span style={{fontSize:11,color:enabled?'var(--accent)':'var(--sub)',fontWeight:600}}>{loading?'…':enabled?'On':'Off'}</span>
    </div>
  );
}

// ─── TEAM LEAD CRM (employee portal) ─────────────────────────────────────────
function TeamLeadCRM({ empToken }) {
  const [view, setView] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddContact, setShowAddContact] = useState(false);
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [newContact, setNewContact] = useState({name:'',phone:'',email:'',address:'',notes:'',status:'Lead'});
  const [newActivity, setNewActivity] = useState({contact_id:'',type:'Call',notes:'',scheduled_at:'',completed_at:''});
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const eApi = {
    get: url=>fetch(`${process.env.REACT_APP_API_URL}/api${url}`,{headers:{Authorization:`Bearer ${empToken}`}}).then(r=>r.json()),
    post:(url,body)=>fetch(`${process.env.REACT_APP_API_URL}/api${url}`,{method:'POST',headers:{'Content-Type':'application/json',Authorization:`Bearer ${empToken}`},body:JSON.stringify(body)}).then(async r=>{const d=await r.json();if(!r.ok)throw new Error(d.error);return d;}),
  };

  async function loadAll(){
    setLoading(true); setError('');
    try {
      const [cd,ad]=await Promise.all([eApi.get('/crm/contacts'),eApi.get('/crm/activities')]);
      setContacts(cd.contacts||[]); setActivities(ad.activities||[]);
    } catch(err){setError(err.message);} finally{setLoading(false);}
  }
  useEffect(()=>{loadAll();},[]);

  async function saveContact(){
    if(!newContact.name.trim()){toast.error('Name required');return;}
    setSaving(true);
    try{const d=await eApi.post('/crm/contacts',newContact);setContacts(p=>[d.contact,...p]);setNewContact({name:'',phone:'',email:'',address:'',notes:'',status:'Lead'});setShowAddContact(false);}
    catch(err){toast.error(err.message);}finally{setSaving(false);}
  }
  async function saveActivity(){
    if(!newActivity.contact_id||!newActivity.type){toast.error('Contact and type required');return;}
    setSaving(true);
    try{await eApi.post('/crm/activities',newActivity);await loadAll();setNewActivity({contact_id:'',type:'Call',notes:'',scheduled_at:'',completed_at:''});setShowLogActivity(false);}
    catch(err){toast.error(err.message);}finally{setSaving(false);}
  }

  const SC=s=>({Lead:'#6b7280',Contacted:'#2563eb',Quoted:'#7c3aed',Won:'#16a34a',Lost:'#dc2626','Follow-up':'#d97706'}[s]||'#6b7280');
  const TI=t=>({Call:'📞',Email:'✉️',Visit:'🏠',Quote:'','Follow-up':'🔔',Other:'📝'}[t]||'📝');
  const upcoming=activities.filter(a=>a.scheduled_at&&!a.completed_at&&new Date(a.scheduled_at)>=new Date()).sort((a,b)=>new Date(a.scheduled_at)-new Date(b.scheduled_at));
  const grouped=upcoming.reduce((acc,a)=>{const d=new Date(a.scheduled_at).toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'});if(!acc[d])acc[d]=[];acc[d].push(a);return acc;},{});
  const iS={width:'100%',border:'1px solid var(--border)',borderRadius:8,padding:'9px 12px',fontSize:14,boxSizing:'border-box'};
  const lS={display:'block',fontWeight:600,fontSize:12,color:'var(--navy)',marginBottom:5};

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div><div style={{fontWeight:800,color:'var(--navy)',fontSize:15}}>CRM</div><div style={{fontSize:12,color:'var(--sub)'}}>{contacts.length} contacts · {activities.length} activities</div></div>
        <div style={{display:'flex',gap:6}}>
          <button onClick={()=>setShowLogActivity(true)} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,padding:'7px 12px',fontSize:12,fontWeight:600,cursor:'pointer',color:'var(--navy)'}}>+ Log</button>
          <button onClick={()=>setShowAddContact(true)} style={{background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,padding:'7px 12px',fontSize:12,fontWeight:600,cursor:'pointer'}}>+ Contact</button>
        </div>
      </div>
      <div style={{display:'flex',gap:0,background:'var(--surface)',borderRadius:10,padding:4,marginBottom:16}}>
        {[['contacts','👥 Contacts'],['log',' Activity Log'],['calendar','📅 Calendar']].map(([k,l])=>(
          <button key={k} onClick={()=>setView(k)} style={{flex:1,padding:'7px 8px',borderRadius:8,border:'none',cursor:'pointer',fontWeight:600,fontSize:11,background:view===k?'#fff':'transparent',color:view===k?'var(--accent)':'var(--sub)',boxShadow:view===k?'0 1px 3px rgba(0,0,0,.1)':'none'}}>{l}</button>
        ))}
      </div>
      {error&&<div style={{color:'var(--red)',background:'#fef2f2',padding:12,borderRadius:8,marginBottom:12,fontSize:13}}>{error}</div>}
      {loading&&<div style={{color:'var(--sub)',padding:20,textAlign:'center'}}>Loading...</div>}

      {!loading&&view==='contacts'&&(
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {contacts.length===0?(
            <div style={{textAlign:'center',padding:32,color:'var(--sub)'}}>
              <div style={{fontSize:32,marginBottom:8}}>👥</div>
              <div style={{marginBottom:12}}>No contacts yet.</div>
              <button onClick={()=>setShowAddContact(true)} style={{background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,padding:'9px 18px',fontWeight:600,cursor:'pointer'}}>+ Add Contact</button>
            </div>
          ):contacts.map(c=>(
            <div key={c.id} style={{background:'#fff',border:'1px solid var(--border)',borderRadius:10,padding:14}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                <div style={{fontWeight:700,color:'var(--navy)'}}>{c.name}</div>
                <span style={{borderRadius:20,padding:'2px 8px',fontSize:11,fontWeight:600,color:SC(c.status),background:'var(--surface)',border:`1px solid ${SC(c.status)}44`}}>{c.status}</span>
              </div>
              {c.phone&&<div style={{fontSize:12,color:'var(--sub)',marginBottom:2}}>📞 {c.phone}</div>}
              {c.email&&<div style={{fontSize:12,color:'var(--sub)',marginBottom:2}}>✉️ {c.email}</div>}
              {c.address&&<div style={{fontSize:12,color:'var(--sub)',marginBottom:2}}>📍 {c.address}</div>}
              <div style={{borderTop:'1px solid var(--divider)',marginTop:8,paddingTop:6,fontSize:11,color:'var(--sub)'}}>{c.activity_count||0} activities · Last: {c.last_activity_at?new Date(c.last_activity_at).toLocaleDateString():'Never'}</div>
            </div>
          ))}
        </div>
      )}

      {!loading&&view==='log'&&(
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {activities.length===0?(
            <div style={{textAlign:'center',padding:32,color:'var(--sub)'}}><div style={{fontSize:32,marginBottom:8}}></div><div>No activities logged yet.</div></div>
          ):activities.map(a=>(
            <div key={a.id} style={{background:'#fff',border:'1px solid var(--border)',borderRadius:10,padding:'12px 14px',display:'flex',gap:10}}>
              <div style={{fontSize:18,flexShrink:0}}>{TI(a.type)}</div>
              <div style={{flex:1}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                  <span style={{fontWeight:700,color:'var(--navy)',fontSize:13}}>{a.contact_name}</span>
                  <span style={{fontSize:11,color:'var(--sub)'}}>{a.scheduled_at?new Date(a.scheduled_at).toLocaleDateString():new Date(a.created_at).toLocaleDateString()}</span>
                </div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  <span style={{background:'var(--surface)',borderRadius:20,padding:'2px 8px',fontSize:11,fontWeight:600,color:'var(--sub)'}}>{a.type}</span>
                  {a.completed_at&&<span style={{background:'#ecfdf5',color:'#16a34a',borderRadius:20,padding:'2px 8px',fontSize:11,fontWeight:600}}>✓ Done</span>}
                </div>
                {a.notes&&<div style={{color:'var(--sub)',fontSize:12,marginTop:4}}>{a.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading&&view==='calendar'&&(
        <div>
          {Object.keys(grouped).length===0?(
            <div style={{textAlign:'center',padding:32,color:'var(--sub)'}}><div style={{fontSize:32,marginBottom:8}}>📅</div><div>No upcoming scheduled activities.</div></div>
          ):Object.entries(grouped).map(([date,acts])=>(
            <div key={date} style={{marginBottom:16}}>
              <div style={{fontWeight:700,color:'var(--navy)',fontSize:13,marginBottom:6,paddingBottom:4,borderBottom:'2px solid var(--border)'}}>{date}</div>
              {acts.map(a=>(
                <div key={a.id} style={{background:'#fff',border:'1px solid var(--border)',borderRadius:8,padding:'10px 12px',display:'flex',gap:8,marginBottom:4}}>
                  <div style={{fontSize:16}}>{TI(a.type)}</div>
                  <div>
                    <div style={{fontWeight:600,color:'var(--navy)',fontSize:13}}>{a.contact_name}</div>
                    <div style={{fontSize:11,color:'var(--sub)'}}>{new Date(a.scheduled_at).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})} · {a.type}</div>
                    {a.notes&&<div style={{fontSize:11,color:'var(--sub)',marginTop:2}}>{a.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {showAddContact&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:16}} onClick={e=>{if(e.target===e.currentTarget)setShowAddContact(false);}}>
          <div style={{background:'#fff',borderRadius:16,padding:24,width:'100%',maxWidth:420}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}><h3 style={{margin:0}}>Add Contact</h3><button onClick={()=>setShowAddContact(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'var(--sub)'}}>×</button></div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <div><label style={lS}>Name *</label><input value={newContact.name} onChange={e=>setNewContact(p=>({...p,name:e.target.value}))} style={iS} placeholder="Contact name"/></div>
              <div><label style={lS}>Phone</label><input value={newContact.phone} onChange={e=>setNewContact(p=>({...p,phone:e.target.value}))} style={iS} placeholder="Phone"/></div>
              <div><label style={lS}>Email</label><input value={newContact.email} onChange={e=>setNewContact(p=>({...p,email:e.target.value}))} style={iS} placeholder="Email"/></div>
              <div><label style={lS}>Address</label><input value={newContact.address} onChange={e=>setNewContact(p=>({...p,address:e.target.value}))} style={iS} placeholder="Address"/></div>
              <div><label style={lS}>Status</label><select value={newContact.status} onChange={e=>setNewContact(p=>({...p,status:e.target.value}))} style={iS}>{['Lead','Contacted','Quoted','Won','Lost','Follow-up'].map(s=><option key={s}>{s}</option>)}</select></div>
              <div><label style={lS}>Notes</label><textarea value={newContact.notes} onChange={e=>setNewContact(p=>({...p,notes:e.target.value}))} rows={2} style={{...iS,resize:'vertical'}} placeholder="Notes"/></div>
            </div>
            <button onClick={saveContact} disabled={saving} style={{width:'100%',marginTop:16,background:saving?'#93c5fd':'var(--accent)',color:'#fff',border:'none',borderRadius:8,padding:12,fontWeight:700,fontSize:14,cursor:saving?'not-allowed':'pointer'}}>{saving?'Saving...':'Add Contact'}</button>
          </div>
        </div>
      )}

      {showLogActivity&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:16}} onClick={e=>{if(e.target===e.currentTarget)setShowLogActivity(false);}}>
          <div style={{background:'#fff',borderRadius:16,padding:24,width:'100%',maxWidth:420}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}><h3 style={{margin:0}}>Log Activity</h3><button onClick={()=>setShowLogActivity(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer',color:'var(--sub)'}}>×</button></div>
            <div style={{display:'flex',flexDirection:'column',gap:10}}>
              <div><label style={lS}>Contact *</label><select value={newActivity.contact_id} onChange={e=>setNewActivity(p=>({...p,contact_id:e.target.value}))} style={iS}><option value="">Select contact</option>{contacts.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
              <div><label style={lS}>Type *</label><select value={newActivity.type} onChange={e=>setNewActivity(p=>({...p,type:e.target.value}))} style={iS}>{['Call','Email','Visit','Quote','Follow-up','Other'].map(t=><option key={t}>{t}</option>)}</select></div>
              <div><label style={lS}>Scheduled Date/Time</label><input type="datetime-local" value={newActivity.scheduled_at} onChange={e=>setNewActivity(p=>({...p,scheduled_at:e.target.value}))} style={iS}/></div>
              <div><label style={lS}>Completed (if done)</label><input type="datetime-local" value={newActivity.completed_at} onChange={e=>setNewActivity(p=>({...p,completed_at:e.target.value}))} style={iS}/></div>
              <div><label style={lS}>Notes</label><textarea value={newActivity.notes} onChange={e=>setNewActivity(p=>({...p,notes:e.target.value}))} rows={2} style={{...iS,resize:'vertical'}} placeholder="Notes"/></div>
            </div>
            <button onClick={saveActivity} disabled={saving} style={{width:'100%',marginTop:16,background:saving?'#93c5fd':'var(--accent)',color:'#fff',border:'none',borderRadius:8,padding:12,fontWeight:700,fontSize:14,cursor:saving?'not-allowed':'pointer'}}>{saving?'Saving...':'Save Activity'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null);
  const { startTour } = useTour();
  const navigate = useNavigate();
  const toast = useToast();
  const mobile = window.innerWidth < 768;

  const load = useCallback(() => {
    setLoading(true);
    api.get('/dashboard').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Opens the job's detail view on the Jobs tab (Jobs reads openJobId from router state)
  function openJob(j) {
    navigate('/jobs', { state: { openJobId: j.id, from: 'dashboard' } });
  }

  function applyStatus(jobId, newStatus) {
    setPicker(null);
    setData(prev => ({
      ...prev,
      jobs: prev.jobs.map(j => j.id === jobId ? { ...j, status: newStatus } : j),
    }));
    toast.success(`Status updated → ${newStatus === 'on_hold' ? 'On Hold' : newStatus.charAt(0).toUpperCase() + newStatus.slice(1)}`);
    api.patch(`/jobs/${jobId}`, { status: newStatus }).catch(() => {});
  }

  if (loading) return (
  <div>
    {/* KPI skeleton row — 4 cards matching real layout */}
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
      {[1,2,3,4].map(i => (
        <div key={i} className="card" style={{ flex: '1 1 180px', minWidth: 160, padding: '16px 20px' }}>
          <div style={{ width: 80, height: 11, borderRadius: 4, background: 'var(--divider)', marginBottom: 10, animation: 'shimmer 1.4s infinite' }} />
          <div style={{ width: '60%', height: 28, borderRadius: 6, background: 'var(--divider)', marginBottom: 8, animation: 'shimmer 1.4s infinite' }} />
          <div style={{ width: '85%', height: 10, borderRadius: 4, background: 'var(--divider)', animation: 'shimmer 1.4s infinite' }} />
        </div>
      ))}
    </div>
    {/* Pie chart skeleton */}
    <div className="card" style={{ padding: 20, marginBottom: 16, height: 180, animation: 'shimmer 1.4s infinite', background: 'var(--divider)', borderRadius: 14 }} />
    {/* Recent Jobs skeleton */}
    <div className="card" style={{ padding: 20 }}>
      <div style={{ width: 120, height: 14, borderRadius: 4, background: 'var(--divider)', marginBottom: 16, animation: 'shimmer 1.4s infinite' }} />
      {[1,2,3,4,5].map(i => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ width: '40%', height: 12, borderRadius: 4, background: 'var(--divider)', animation: 'shimmer 1.4s infinite' }} />
          <div style={{ width: '20%', height: 12, borderRadius: 4, background: 'var(--divider)', animation: 'shimmer 1.4s infinite' }} />
        </div>
      ))}
    </div>
  </div>
);

  if (!data) return null;
  const { kpis, jobs } = data;

  const activeCount = jobs.filter(j => j.status === 'active').length;
  const totalJobs = jobs.length;

  return (
    <div>
      <div style={{ minHeight: 0 }}><OnboardingChecklist /></div>
      {/* KPIs */}
      <div data-tour="kpis" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <KPI label="Revenue" icon="" val={fmt(kpis.totalRevenue)} sub={`${fmt(kpis.outstandingAR)} outstanding AR`} up={null} onClick={() => navigate('/clients')} />
        <KPI label="Gross Profit" icon="" val={`${kpis.profitMargin}%`} sub="margin this period" up={kpis.profitMargin >= 20} onClick={() => navigate('/reports')} />
        <KPI label="Active Jobs" icon="" val={activeCount} sub="tap to view all jobs" up={null} onClick={() => navigate('/jobs')} />
        <KPI label="Outstanding AR" icon="" val={fmt(kpis.outstandingAR)} sub={`${totalJobs} total jobs tracked`} up={kpis.outstandingAR === 0} onClick={() => navigate('/clients')} />
      </div>

      {/* Cost breakdown pie chart */}
      <div data-tour="pie">
        {!loading && data && <CostPieChart kpis={data.kpis} />}
      </div>

      {/* Recent Jobs */}
      <div className="card" style={{ padding: mobile ? 16 : 20 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2 }}>
          <div className="sh">Recent Jobs</div>
          <button onClick={() => navigate('/jobs')} style={{ fontSize:12, color:'var(--accent)', background:'none', border:'none', cursor:'pointer', fontWeight:700 }}>View all →</button>
        </div>
        <div className="ss">Tap any status badge to update it · tap a job to open it</div>
        {jobs.length === 0 ? (
          <div style={{ textAlign:'center', padding:'32px 0' }}>
            <div style={{ fontSize:36, marginBottom:10 }}></div>
            <div style={{ fontWeight:700, color:'var(--navy)', marginBottom:6 }}>No jobs yet</div>
            <div style={{ fontSize:13, color:'var(--sub)', marginBottom:16 }}>Connect QuickBooks to sync your jobs, or add one manually</div>
            <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
              <button onClick={() => navigate('/jobs')} style={{ padding:'9px 18px', background:'var(--navy)', color:'#fff', border:'none', borderRadius:9, fontWeight:700, cursor:'pointer', fontSize:12 }}>+ Add a Job</button>
              <button onClick={() => navigate('/integrations')} style={{ padding:'9px 18px', background:'var(--surface)', color:'var(--navy)', border:'1px solid var(--border)', borderRadius:9, fontWeight:700, cursor:'pointer', fontSize:12 }}>Connect QBO →</button>
            </div>
          </div>
        ) : mobile ? (
          jobs.slice(0, 6).map(j => (
            <div key={j.id} className="mc tap" role="button" tabIndex={0} onClick={() => openJob(j)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openJob(j); } }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div>
                  <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 13 }}>{j.name?.slice(0, 30)}</div>
                  <div style={{ color: 'var(--sub)', fontSize: 12, marginTop: 2 }}>{j.customerName}</div>
                </div>
                <span className="badge" onClick={e => { e.stopPropagation(); setPicker({ jobId: j.id, current: j.status }); }} onKeyDown={e => e.stopPropagation()} style={{ ...(SC[j.status] || {}), cursor: 'pointer', border: `1px solid ${(SC[j.status] || {}).color || '#ccc'}22` }}>
                  {j.status === 'on_hold' ? 'On Hold' : j.status.charAt(0).toUpperCase() + j.status.slice(1)} <span style={{ fontSize: 9, opacity: .7 }}>▼</span>
                </span>
              </div>
              <div style={{ display: 'flex', gap: 16, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                <div><div style={{ fontSize: 10, color: 'var(--sub)', fontWeight: 600, textTransform: 'uppercase' }}>Budget</div><div style={{ fontSize: 14, fontWeight: 800 }}>{fmt(j.budgetTotal)}</div></div>
                <div><div style={{ fontSize: 10, color: 'var(--sub)', fontWeight: 600, textTransform: 'uppercase' }}>Actual</div><div style={{ fontSize: 14, fontWeight: 800, color: j.variance < 0 ? 'var(--red)' : 'var(--green)' }}>{fmt(j.actualTotal)}</div></div>
                <div><div style={{ fontSize: 10, color: 'var(--sub)', fontWeight: 600, textTransform: 'uppercase' }}>Used</div><div style={{ fontSize: 14, fontWeight: 800 }}>{j.pctUsed}%</div></div>
              </div>
            </div>
          ))
        ) : (
          <div className="tbl-wrap">
            <table>
              <thead><tr><th>Job</th><th>Customer</th><th>Budget</th><th>Actual</th><th>Margin</th><th>Status</th></tr></thead>
              <tbody>
                {jobs.slice(0, 8).map(j => (
                  <tr key={j.id} className="ch" tabIndex={0} onClick={() => openJob(j)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openJob(j); } }}>
                    <td style={{ color: 'var(--accent)', fontWeight: 700, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.name}</td>
                    <td style={{ color: 'var(--sub)' }}>{j.customerName}</td>
                    <td style={{ fontWeight: 700 }}>{fmt(j.budgetTotal)}</td>
                    <td style={{ fontWeight: 700, color: j.variance < 0 ? 'var(--red)' : 'inherit' }}>{fmt(j.actualTotal)}</td>
                    <td style={{ fontWeight: 800, color: j.pctUsed >= 90 ? 'var(--red)' : j.pctUsed >= 75 ? 'var(--amber)' : 'var(--green)' }}>{j.pctUsed}%</td>
                    <td>
                      <span className="badge" onClick={e => { e.stopPropagation(); setPicker({ jobId: j.id, current: j.status }); }} style={{ ...(SC[j.status] || {}), cursor: 'pointer', border: `1px solid ${(SC[j.status] || {}).color || '#ccc'}22` }}>
                        {j.status === 'on_hold' ? 'On Hold' : j.status.charAt(0).toUpperCase() + j.status.slice(1)} <span style={{ fontSize: 9, opacity: .7 }}>▼</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {picker && <Picker type="job" id={picker.jobId} current={picker.current} onClose={() => setPicker(null)} onApply={s => applyStatus(picker.jobId, s)} />}

      {/* App Walkthrough Button */}
      <div style={{ textAlign: 'center', padding: '24px 0 8px' }}>
        <button onClick={startTour} style={{ padding: '12px 28px', background: 'none', border: '2px solid var(--border)', borderRadius: 24, fontSize: 13, fontWeight: 700, color: 'var(--sub)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>🗺️</span> Take the App Tour
        </button>
        <div style={{ fontSize: 11, color: 'var(--sub)', marginTop: 6 }}>Learn what every screen does in 2 minutes</div>
      </div>

      <RequestFeatureBox />
    </div>
  );
}

// ─── Cost Breakdown Pie Chart ─────────────────────────────────────────────────
function CostPieChart({ kpis }) {
  const canvasRef = React.useRef(null);
  const chartRef = React.useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !kpis) return;
    const labor     = kpis.totalLabor || 0;
    const materials = kpis.totalMaterials || 0;
    const overhead  = Math.round((labor + materials) * 0.08);
    const total     = labor + materials + overhead;
    if (total === 0) return;

    const init = () => {
      if (chartRef.current) chartRef.current.destroy();
      chartRef.current = new window.Chart(canvasRef.current, {
        type: 'doughnut',
        data: {
          labels: ['Labor', 'Materials', 'Overhead'],
          datasets: [{
            data: [labor, materials, overhead],
            backgroundColor: ['#4a9edd', '#7aa3c0', '#6366f1'],
            borderWidth: 0,
            hoverOffset: 4,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          cutout: '68%',
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => ` ${ctx.label}: $${Math.round(ctx.raw).toLocaleString()} (${Math.round(ctx.raw / total * 100)}%)` } },
          },
        },
      });
    };

    if (window.Chart) { init(); }
    else {
      const existing = document.getElementById('chartjs-script');
      if (!existing) {
        const s = document.createElement('script');
        s.id = 'chartjs-script';
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
        s.onload = init;
        document.head.appendChild(s);
      } else { existing.addEventListener('load', init); }
    }

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [kpis]);

  if (!kpis) return null;
  const labor     = kpis.totalLabor || 0;
  const materials = kpis.totalMaterials || 0;
  const overhead  = Math.round((labor + materials) * 0.08);
  const total     = labor + materials + overhead;

  return (
    <div className="card" style={{ padding: 18, marginBottom: 16 }}>
      <div className="sh" style={{ marginBottom: 4 }}>Cost Breakdown</div>
      <div className="ss">Total job costs by category</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', height: 130, width: 130, flexShrink: 0 }}>
          <canvas ref={canvasRef} role="img" aria-label="Doughnut chart showing cost breakdown by labor, materials and overhead" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
          {[['Labor', labor, '#4a9edd'], ['Materials', materials, '#7aa3c0'], ['Overhead (~8%)', overhead, '#6366f1']].map(([l, v, c]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: c, display: 'inline-block', flexShrink: 0 }} />
              <span style={{ color: 'var(--sub)', flex: 1 }}>{l}</span>
              <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{fmt(v)}</span>
              <span style={{ color: 'var(--sub)', fontSize: 11, minWidth: 34, textAlign: 'right' }}>{total > 0 ? Math.round(v / total * 100) : 0}%</span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid var(--divider)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ fontWeight: 700, color: 'var(--navy)' }}>Total</span>
            <span style={{ fontWeight: 800, color: 'var(--navy)' }}>{fmt(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}


function JobsBarChart({ jobs }) {
  const canvasRef = React.useRef(null);
  const chartRef  = React.useRef(null);

  useEffect(() => {
    if (!jobs.length || !canvasRef.current) return;

    const sorted = [...jobs].sort((a, b) => {
      const o = { on_hold: 0, active: 1, completed: 2 };
      return (o[a.status] ?? 3) - (o[b.status] ?? 3);
    });

    const labels  = sorted.map(j => j.name.length > 16 ? j.name.slice(0, 16) + '…' : j.name);
    const budgets = sorted.map(j => j.budgetTotal || 0);
    const actuals = sorted.map(j => Math.min(j.actualTotal || 0, j.budgetTotal || 0));
    const colors  = sorted.map(j =>
      j.status === 'completed' ? '#10b981' :
      j.status === 'active'    ? '#f59e0b' : '#ef4444'
    );
    const maxBudget = Math.max(...budgets, 1);

    const init = () => {
      if (chartRef.current) chartRef.current.destroy();

      const budgetOutlinePlugin = {
        id: 'budgetOutline',
        beforeDatasetsDraw(chart) {
          const { ctx, scales: { y } } = chart;
          const meta = chart.getDatasetMeta(0);
          if (!meta.data.length) return;
          meta.data.forEach((bar, i) => {
            const budget = budgets[i];
            const actual = actuals[i];
            if (!budget) return;
            const barW      = bar.width;
            const left      = bar.x - barW / 2;
            const budgetTop = y.getPixelForValue(budget);
            const actualTop = y.getPixelForValue(actual);
            const bottom    = y.getPixelForValue(0);

            ctx.save();

            // Semi-transparent tint filling gap between actual and budget
            if (actual < budget) {
              ctx.fillStyle = colors[i] + '28'; // ~16% opacity tint
              ctx.fillRect(left, budgetTop, barW, actualTop - budgetTop);
            }

            // Budget outline border
            ctx.strokeStyle = colors[i] + 'cc';
            ctx.lineWidth = 2;
            ctx.strokeRect(left, budgetTop, barW, bottom - budgetTop);

            ctx.restore();
          });
        },
      };

      chartRef.current = new window.Chart(canvasRef.current, {
        type: 'bar',
        plugins: [budgetOutlinePlugin],
        data: {
          labels,
          datasets: [{
            label: 'Spent',
            data: actuals,
            backgroundColor: colors,
            borderWidth: 0,
            borderRadius: 4,
            barPercentage: 0.55,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                title: items => sorted[items[0].dataIndex]?.name || '',
                label: ctx => {
                  const j = sorted[ctx.dataIndex];
                  const pct = j.budgetTotal > 0 ? Math.round((j.actualTotal / j.budgetTotal) * 100) : 0;
                  return [
                    ` Spent: $${Math.round(j.actualTotal || 0).toLocaleString()} (${pct}%)`,
                    ` Budget: $${Math.round(j.budgetTotal || 0).toLocaleString()}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { font: { size: 11 }, color: '#374151', maxRotation: 30 },
            },
            y: {
              min: 0,
              max: Math.ceil(maxBudget * 1.12 / 1000) * 1000,
              grid: { color: '#f3f4f6' },
              ticks: {
                font: { size: 11 },
                color: '#6b7280',
                maxTicksLimit: 6,
                callback: v => '$' + (v >= 1000 ? (v / 1000).toFixed(0) + 'K' : v),
              },
            },
          },
        },
      });
    };

    if (window.Chart) { init(); }
    else {
      const existing = document.getElementById('chartjs-script');
      if (!existing) {
        const s = document.createElement('script');
        s.id = 'chartjs-script';
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
        s.onload = init;
        document.head.appendChild(s);
      } else { existing.addEventListener('load', init); }
    }

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [jobs]);

  const completed = jobs.filter(j => j.status === 'completed').length;
  const active    = jobs.filter(j => j.status === 'active').length;
  const open      = jobs.filter(j => j.status !== 'completed' && j.status !== 'active').length;
  // Minimum 60px per bar so they never get squished
  const minWidth  = Math.max(jobs.length * 60 + 60, 400);

  return (
    <div className="card" style={{ padding: '18px 20px', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div className="sh">Budget vs Spent</div>
          <div style={{ fontSize: 12, color: 'var(--sub)', marginTop: 2 }}>Outline = full budget · solid fill = amount spent so far</div>
        </div>
        <div style={{ display: 'flex', gap: 14 }}>
          {[['#ef4444', `${open} On Hold`], ['#f59e0b', `${active} Active`], ['#10b981', `${completed} Complete`]].map(([color, label]) => (
            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#6b7280' }}>
              <span style={{ width: 10, height: 10, borderRadius: 2, background: color, display: 'inline-block' }} />{label}
            </span>
          ))}
        </div>
      </div>
      {/* Scrollable wrapper — canvas grows to fit all bars */}
      <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <div style={{ position: 'relative', height: 220, minWidth }}>
          <canvas ref={canvasRef} role="img" aria-label="Vertical bar chart showing budget vs actual spend per job" />
        </div>
      </div>
    </div>
  );
}


// ─── JOB COST SETTINGS ───────────────────────────────────────────────────────
function JobCostSettings() {
  const [accounts, setAccounts]     = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [hasQBO, setHasQBO]         = useState(false);
  const [overrides, setOverrides]   = useState({});
  const [saving, setSaving]         = useState(false);
  const [dirty, setDirty]           = useState(false);
  const toast = useToast();
  const mobile = window.innerWidth < 768;

  useEffect(() => {
    Promise.all([
      api.get('/jobcost/accounts').catch(() => ({ data: { accounts:[], hasQBOConnection:false } })),
      api.get('/jobcost/summary').catch(() => ({ data: null })),
    ]).then(([a, s]) => {
      setAccounts(a.data.accounts || []);
      setHasQBO(a.data.hasQBOConnection);
      setSummary(s.data);
      const init = {};
      (a.data.accounts || []).forEach(acc => { init[acc.id] = acc.included; });
      setOverrides(init);
    }).finally(() => setLoading(false));
  }, []);

  function toggle(id) {
    setOverrides(prev => ({ ...prev, [id]: !prev[id] }));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      await api.post('/jobcost/accounts', { overrides });
      setDirty(false);
      toast.success('Account filter saved');
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  const included = accounts.filter(a => overrides[a.id] !== false);
  const excluded = accounts.filter(a => overrides[a.id] === false);
  const cogsAccounts = accounts.filter(a => a.isCOGS);
  const otherAccounts = accounts.filter(a => !a.isCOGS);

  if (loading) return <div className="card" style={{padding:20}}>{[1,2,3].map(i=><Skel key={i} h={44} mb={8}/>)}</div>;

  return (
    <div>
      {/* Summary KPIs */}
      {summary && (
        <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:16}}>
          <KPI label="Bills Matched" icon="✅" val={summary.matchedBills.count} sub={`$${summary.matchedBills.total.toFixed(0)} total`} up={null} />
          <KPI label="Bills Unmatched" icon="⚠️" val={summary.unmatchedBills.count} sub={`$${summary.unmatchedBills.total.toFixed(0)} needs assignment`} up={summary.unmatchedBills.count === 0} />
          <KPI label="Labor Entries" icon="👷" val={summary.laborEntries.count} sub={`$${summary.laborEntries.total.toFixed(0)} total`} up={null} />
        </div>
      )}

      {/* How it works */}
      <div className="card" style={{padding:18,marginBottom:16}}>
        <div className="sh" style={{marginBottom:8}}>How Cost Filtering Works</div>
        <div style={{fontSize:13,color:'var(--sub)',lineHeight:1.8}}>
          When JobHub syncs bills from QuickBooks, it checks each line item's account type.
          Only <strong style={{color:'var(--navy)'}}>Cost of Goods Sold</strong> and supply/material accounts are pulled into job costing by default.
          Overhead expenses like rent, utilities, and insurance are automatically excluded.
        </div>
        <div style={{display:'flex',gap:24,marginTop:14,flexWrap:'wrap'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
            <span style={{width:10,height:10,borderRadius:2,background:'var(--green)',display:'inline-block'}}/>
            <span style={{color:'var(--sub)'}}><strong style={{color:'var(--navy)'}}>Included by default:</strong> COGS accounts, supplies, materials, labor, subcontractors</span>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
            <span style={{width:10,height:10,borderRadius:2,background:'var(--red)',display:'inline-block'}}/>
            <span style={{color:'var(--sub)'}}><strong style={{color:'var(--navy)'}}>Excluded by default:</strong> Rent, utilities, insurance, office expenses</span>
          </div>
        </div>
      </div>

      {/* QBO Account list */}
      <div className="card" style={{overflow:'hidden',marginBottom:16}}>
        <div style={{padding:'14px 18px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:10}}>
          <div>
            <div className="sh">QBO Expense Accounts</div>
            <div style={{fontSize:12,color:'var(--sub)',marginTop:2}}>
              {hasQBO ? `${included.length} included · ${excluded.length} excluded` : 'Connect QuickBooks to see your accounts'}
            </div>
          </div>
          {dirty && (
            <button onClick={save} disabled={saving} style={{padding:'8px 18px',background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13}}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>

        {!hasQBO ? (
          <div style={{textAlign:'center',padding:'40px 20px',color:'var(--sub)'}}>
            <div style={{fontSize:32,marginBottom:12}}>🔗</div>
            <div style={{fontWeight:700,color:'var(--navy)',marginBottom:6}}>QuickBooks not connected</div>
            <div style={{fontSize:13,marginBottom:16}}>Connect QuickBooks in the Integrations tab to see and manage your expense accounts</div>
          </div>
        ) : accounts.length === 0 ? (
          <div style={{textAlign:'center',padding:'40px 20px',color:'var(--sub)'}}>
            <div style={{fontSize:13}}>Run a QBO sync to load your expense accounts</div>
          </div>
        ) : (
          <div>
            {/* COGS accounts */}
            {cogsAccounts.length > 0 && (
              <div>
                <div style={{padding:'8px 18px',background:'#f0fdf4',borderBottom:'1px solid var(--border)',fontSize:11,fontWeight:700,color:'#166534',textTransform:'uppercase',letterSpacing:'0.05em'}}>
                  Cost of Goods Sold — Always included by default
                </div>
                {cogsAccounts.map((a,i) => (
                  <div key={a.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 18px',borderBottom:i<cogsAccounts.length-1?'1px solid var(--divider)':'none'}}>
                    <div>
                      <div style={{fontWeight:600,color:'var(--navy)',fontSize:13}}>{a.name}</div>
                      <div style={{fontSize:11,color:'var(--sub)',marginTop:2}}>{a.accountSubType || a.accountType}</div>
                    </div>
                    <div onClick={() => toggle(a.id)} style={{width:38,height:22,borderRadius:11,background:overrides[a.id]!==false?'var(--green)':'var(--border)',position:'relative',cursor:'pointer',transition:'background .2s',flexShrink:0}}>
                      <div style={{width:18,height:18,borderRadius:9,background:'#fff',position:'absolute',top:2,left:overrides[a.id]!==false?18:2,transition:'left .2s',boxShadow:'0 1px 3px rgba(0,0,0,.2)'}}/>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Other expense accounts */}
            {otherAccounts.length > 0 && (
              <div>
                <div style={{padding:'8px 18px',background:'var(--surface)',borderTop:'1px solid var(--border)',borderBottom:'1px solid var(--border)',fontSize:11,fontWeight:700,color:'var(--sub)',textTransform:'uppercase',letterSpacing:'0.05em'}}>
                  Other Expense Accounts — Toggle to include in job costing
                </div>
                {otherAccounts.map((a,i) => (
                  <div key={a.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 18px',borderBottom:i<otherAccounts.length-1?'1px solid var(--divider)':'none',background:overrides[a.id]?'#fafffe':'inherit'}}>
                    <div>
                      <div style={{fontWeight:600,color:'var(--navy)',fontSize:13}}>{a.name}</div>
                      <div style={{fontSize:11,color:'var(--sub)',marginTop:2}}>{a.accountSubType || a.accountType}
                        {a.isSupply && <span style={{marginLeft:6,background:'#dbeafe',color:'#1e40af',padding:'1px 6px',borderRadius:10,fontSize:10,fontWeight:700}}>Supply</span>}
                      </div>
                    </div>
                    <div onClick={() => toggle(a.id)} style={{width:38,height:22,borderRadius:11,background:overrides[a.id]?'var(--accent)':'var(--border)',position:'relative',cursor:'pointer',transition:'background .2s',flexShrink:0}}>
                      <div style={{width:18,height:18,borderRadius:9,background:'#fff',position:'absolute',top:2,left:overrides[a.id]?18:2,transition:'left .2s',boxShadow:'0 1px 3px rgba(0,0,0,.2)'}}/>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {dirty && (
        <div style={{position:'sticky',bottom:mobile?70:20,display:'flex',justifyContent:'center'}}>
          <button onClick={save} disabled={saving} style={{padding:'12px 32px',background:'var(--accent)',color:'#fff',border:'none',borderRadius:24,fontWeight:800,cursor:'pointer',fontSize:14,boxShadow:'0 4px 20px rgba(74,158,221,.4)'}}>
            {saving ? 'Saving...' : '💾 Save Filter Settings'}
          </button>
        </div>
      )}
    </div>
  );
}
// ─── MATCHED COSTS ────────────────────────────────────────────────────────────
function MatchedCosts({ jobs, onReassign }) {
  const [bills, setBills] = useState([]);
  const [labor, setLabor] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('bills');
  const mobile = window.innerWidth < 768;

  useEffect(() => {
    Promise.all([
      api.get('/costs/matched-bills').catch(() => ({ data: [] })),
      api.get('/costs/matched-labor').catch(() => ({ data: [] })),
    ]).then(([b, l]) => {
      setBills(b.data || []);
      setLabor(l.data || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{padding:20}}>{[1,2,3].map(i=><Skel key={i} h={44} mb={8}/>)}</div>;

  return (
    <div>
      <div style={{display:'flex',borderBottom:'1px solid var(--border)'}}>
        {[['bills',`Bills (${bills.length})`],['labor',`Labor (${labor.length})`]].map(([key,label])=>(
          <button key={key} onClick={()=>setTab(key)} style={{flex:1,padding:'10px 16px',background:'none',border:'none',borderBottom:`2px solid ${tab===key?'var(--accent)':'transparent'}`,color:tab===key?'var(--accent)':'var(--sub)',fontWeight:tab===key?700:400,cursor:'pointer',fontSize:13}}>
            {label}
          </button>
        ))}
      </div>
      {tab==='bills' && (bills.length===0 ? (
        <div style={{textAlign:'center',padding:'30px 0',color:'var(--sub)',fontSize:13}}>No matched bills yet</div>
      ) : mobile ? (
        <div style={{padding:'12px 16px'}}>
          {bills.map(b=>(
            <div key={b.id} className="mc" style={{marginBottom:8}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                <div>
                  <div style={{fontWeight:700,color:'var(--navy)',fontSize:13}}>{b.vendor||'Unknown'}</div>
                  <div style={{fontSize:12,color:'var(--accent)',marginTop:2}}>📍 {jobs.find(j=>j.id===b.job_id)?.name||'Unknown job'}</div>
                </div>
                <div style={{fontWeight:800,color:'var(--navy)'}}>${parseFloat(b.amount).toFixed(2)}</div>
              </div>
              <button onClick={()=>onReassign('bills',b.id,b.vendor||b.description,b.amount)} style={{width:'100%',padding:'7px 0',background:'var(--surface)',color:'var(--navy)',border:'1px solid var(--border)',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:12}}>
                Reassign →
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Vendor</th><th>Description</th><th>Amount</th><th>Assigned To</th><th>Date</th><th></th></tr></thead>
            <tbody>
              {bills.map(b=>(
                <tr key={b.id}>
                  <td style={{fontWeight:700,color:'var(--navy)'}}>{b.vendor||'—'}</td>
                  <td style={{color:'var(--sub)',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.description||'—'}</td>
                  <td style={{fontWeight:700}}>${parseFloat(b.amount).toFixed(2)}</td>
                  <td style={{color:'var(--accent)',fontWeight:600}}>{jobs.find(j=>j.id===b.job_id)?.name||'—'}</td>
                  <td style={{color:'var(--sub)',fontSize:12}}>{b.date}</td>
                  <td><button onClick={()=>onReassign('bills',b.id,b.vendor||b.description,b.amount)} style={{padding:'5px 10px',background:'var(--surface)',color:'var(--navy)',border:'1px solid var(--border)',borderRadius:6,fontWeight:700,cursor:'pointer',fontSize:11}}>Reassign</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {tab==='labor' && (labor.length===0 ? (
        <div style={{textAlign:'center',padding:'30px 0',color:'var(--sub)',fontSize:13}}>No matched labor yet</div>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Employee</th><th>Hours</th><th>Total</th><th>Assigned To</th><th>Date</th><th></th></tr></thead>
            <tbody>
              {labor.map(l=>(
                <tr key={l.id}>
                  <td style={{fontWeight:700,color:'var(--navy)'}}>{l.employee_name}</td>
                  <td>{l.hours}h</td>
                  <td style={{fontWeight:700}}>${parseFloat(l.total_cost).toFixed(2)}</td>
                  <td style={{color:'var(--accent)',fontWeight:600}}>{jobs.find(j=>j.id===l.job_id)?.name||'—'}</td>
                  <td style={{color:'var(--sub)',fontSize:12}}>{l.pay_period_end}</td>
                  <td><button onClick={()=>onReassign('labor',l.id,`${l.employee_name} — ${l.hours}h`,l.total_cost)} style={{padding:'5px 10px',background:'var(--surface)',color:'var(--navy)',border:'1px solid var(--border)',borderRadius:6,fontWeight:700,cursor:'pointer',fontSize:11}}>Reassign</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ─── COST MATCHING ────────────────────────────────────────────────────────────
function CostMatching() {
  const [unmatched, setUnmatched] = useState({ bills: [], labor: [] });
  const [jobs, setJobs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('bills');
  const [assigning, setAssigning] = useState(null);
  const [picker, setPicker]       = useState(null);
  const [addForm, setAddForm]     = useState(null);
  const [form, setForm]           = useState({});
  const [saving, setSaving]       = useState(false);
  const [splitMode, setSplitMode] = useState('dollar');
  const [splitRows, setSplitRows] = useState([{job_id:'',amount:''},{job_id:'',amount:''}]);
  const toast = useToast();
  const mobile = window.innerWidth < 768;
  const { costCategories } = useIndustry();

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/costs/unmatched'),
      api.get('/dashboard'),
    ]).then(([u, d]) => {
      setUnmatched(u.data);
      setJobs(d.data.jobs || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  async function assignSplit(id, totalAmount) {
    const filled = splitRows.filter(r => r.job_id && r.amount);
    if (filled.length < 2) { toast.error('Add at least 2 splits'); return; }
   const total = parseFloat(totalAmount);
    const splits = splitMode === 'pct'
      ? filled.map(r => ({ job_id: r.job_id, amount: parseFloat((parseFloat(r.amount) / 100 * total).toFixed(2)) }))
        : filled.map(r => ({ job_id: r.job_id, amount: parseFloat(r.amount) }));
    if (splitMode === 'pct') {
      const pctSum = filled.reduce((s,r) => s + parseFloat(r.amount||0), 0);
      if (Math.abs(pctSum - 100) > 0.1) { toast.error('Percentages must add up to 100%'); return; }
    } else {
      const sum = splits.reduce((s,r) => s + parseFloat(r.amount||0), 0);
      if (Math.abs(sum - total) > 0.02) { toast.error(`Splits must equal bill total ($${total.toFixed(2)})`); return; }
    }
    setAssigning(id);
    try {
      const finalSplits = splitMode === 'pct'
        ? filled.map(r => ({ job_id: parseInt(r.job_id), amount: parseFloat((parseFloat(r.amount) / 100 * total).toFixed(2)) }))
        : filled.map(r => ({ job_id: parseInt(r.job_id), amount: parseFloat(r.amount) }));
      await api.post(`/costs/bills/${id}/split`, { splits: finalSplits });
      toast.success('Expense split across jobs');
      setSplitMode('dollar');
      setSplitRows([{job_id:'',amount:''},{job_id:'',amount:''}]);
      setPicker(null);
      load();
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setAssigning(null); }
  }

  async function assign(type, id, jobId) {
    setAssigning(id);
    try {
      await api.patch(`/costs/${type}/${id}/assign`, { job_id: jobId });
      const jobName = jobs.find(j => j.id === jobId)?.name || '';
      toast.success(`Assigned to ${jobName}`);
      setPicker(null);
      load();
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setAssigning(null); }
  }

  async function addCost() {
    setSaving(true);
    try {
      if (addForm === 'bill') {
        if (!form.amount) { toast.error('Amount required'); return; }
        await api.post('/costs/bills', form);
        toast.success('Bill added');
      } else {
        if (!form.employee_name || !form.hours || !form.rate) { toast.error('Employee, hours, and rate required'); return; }
        await api.post('/costs/labor', form);
        toast.success('Labor entry added');
      }
      setAddForm(null); setForm({});
      load();
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  const totalUnmatched = (unmatched.bills?.length || 0) + (unmatched.labor?.length || 0);
  const activeJobs = jobs.filter(j => j.status === 'active');

// Job picker overlay
  const JobPicker = ({ type, id, desc, amount, detail, date, category }) => {
    const total = parseFloat(amount || 0);
    const allocated = splitRows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
    const remaining = total - allocated;
    const isSplit = splitMode !== false;

    return (
      <div onClick={() => { setPicker(null); setSplitMode('dollar'); setSplitRows([{job_id:'',amount:''},{job_id:'',amount:''}]); }}
        style={{ position:'fixed',inset:0,background:'rgba(26,37,53,.5)',backdropFilter:'blur(3px)',zIndex:3000,display:'flex',alignItems:mobile?'flex-end':'center',justifyContent:'center',padding:mobile?0:24 }}>
        <div onClick={e=>e.stopPropagation()}
          style={{ background:'#fff',borderRadius:mobile?'20px 20px 0 0':16,width:'100%',maxWidth:480,
            height:mobile?(isSplit?'92vh':'auto'):undefined,
            display:'flex',flexDirection:'column',
            boxShadow:'0 16px 48px rgba(26,37,53,.25)',animation:mobile?'slideUp .3s ease':'popIn .25s ease' }}>

          {/* Header */}
          <div style={{ padding:'20px 20px 16px',borderBottom:'1px solid var(--border)',flexShrink:0 }}>
            {mobile && <div style={{ width:36,height:4,background:'var(--border)',borderRadius:2,margin:'0 auto 16px' }} />}
            <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
              <div>
                <div style={{ fontWeight:800,color:'var(--navy)',fontSize:16 }}>{isSplit ? 'Split Expense' : 'Assign to Job'}</div>
                <div style={{ fontSize:13,color:'var(--sub)',marginTop:3 }}>{desc}</div>
                {detail && detail !== desc && (
                  <div style={{ fontSize:13,color:'var(--navy)',marginTop:4 }}>{detail}</div>
                )}
                <div style={{ display:'flex',gap:8,marginTop:6,flexWrap:'wrap' }}>
                  {date && <span style={{ fontSize:11,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:20,padding:'2px 8px',color:'var(--sub)' }}>📅 {date}</span>}
                  {category && <span style={{ fontSize:11,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:20,padding:'2px 8px',color:'var(--sub)' }}>🏷 {category}</span>}
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontWeight:800,color:'var(--navy)',fontSize:20 }}>${total.toFixed(2)}</div>
                <div style={{ fontSize:11,color:'var(--sub)' }}>total</div>
              </div>
            </div>
            {type==='bills' && (
              <button onClick={()=>{ setSplitMode(isSplit?false:'dollar'); setSplitRows([{job_id:'',amount:''},{job_id:'',amount:''}]); }}
                style={{ marginTop:12,width:'100%',padding:'9px 0',borderRadius:9,border:`1px solid ${isSplit?'var(--border)':'var(--accent)'}`,
                  background:isSplit?'var(--surface)':'#eff6ff',color:isSplit?'var(--sub)':'var(--accent)',
                  fontWeight:700,fontSize:13,cursor:'pointer' }}>
                {isSplit ? '← Assign to single job instead' : '✂️ Split across multiple jobs'}
              </button>
            )}
          </div>

          {/* Body — scrollable */}
          <div style={{ flex:1,overflowY:'auto',padding:'16px 20px' }}>
            {!isSplit ? (
              <>
                {activeJobs.length === 0 && <div style={{ color:'var(--sub)',fontSize:13,textAlign:'center',padding:'20px 0' }}>No active jobs found</div>}
                {activeJobs.map(j => (
                  <button key={j.id} onClick={() => assign(type, id, j.id)} disabled={assigning === id}
                    style={{ display:'flex',justifyContent:'space-between',alignItems:'center',width:'100%',
                      padding:'14px 16px',borderRadius:10,border:'1px solid var(--border)',
                      background:assigning===id?'#f9fafb':'var(--surface)',marginBottom:10,cursor:'pointer',textAlign:'left' }}>
                    <div>
                      <div style={{ fontWeight:700,color:'var(--navy)',fontSize:14 }}>{j.name}</div>
                      <div style={{ fontSize:12,color:'var(--sub)',marginTop:2 }}>{j.customerName}</div>
                    </div>
                    <span style={{ fontSize:13,color:'var(--accent)',fontWeight:700 }}>{assigning===id?'...':'Assign →'}</span>
                  </button>
                ))}
              </>
            ) : (
              <>
                {/* $ / % toggle */}
                <div style={{ display:'flex',gap:8,marginBottom:16 }}>
                  <button onClick={()=>{ setSplitMode('dollar'); setSplitRows([{job_id:'',amount:''},{job_id:'',amount:''}]); }}
                    style={{ flex:1,padding:'10px 0',borderRadius:9,border:`2px solid ${splitMode==='dollar'?'var(--navy)':'var(--border)'}`,
                      background:splitMode==='dollar'?'var(--navy)':'#fff',color:splitMode==='dollar'?'#fff':'var(--sub)',
                      fontWeight:700,fontSize:14,cursor:'pointer' }}>$ Dollar</button>
                  <button onClick={()=>{ setSplitMode('pct'); setSplitRows([{job_id:'',amount:''},{job_id:'',amount:''}]); }}
                    style={{ flex:1,padding:'10px 0',borderRadius:9,border:`2px solid ${splitMode==='pct'?'var(--navy)':'var(--border)'}`,
                      background:splitMode==='pct'?'var(--navy)':'#fff',color:splitMode==='pct'?'#fff':'var(--sub)',
                      fontWeight:700,fontSize:14,cursor:'pointer' }}>% Percent</button>
                </div>

                {/* Running total */}
                <div style={{ background: Math.abs(splitMode==='pct'?allocated-100:remaining) < 0.05 ?'#d1fae5':'#fef3c7',
                  borderRadius:9,padding:'10px 14px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                  <span style={{ fontSize:13,fontWeight:600,color:'var(--navy)' }}>
                    {splitMode==='pct' ? `Allocated: ${allocated.toFixed(0)}%` : `Remaining: $${remaining.toFixed(2)}`}
                  </span>
                  <span style={{ fontSize:12,color:'var(--sub)' }}>
                    {splitMode==='pct' ? `${(100-allocated).toFixed(0)}% left` : `of $${total.toFixed(2)}`}
                  </span>
                </div>

                {/* Split rows */}
                {splitRows.map((row, i) => (
                  <div key={i} style={{ background:'var(--surface)',borderRadius:10,padding:14,marginBottom:10,border:'1px solid var(--border)' }}>
                    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10 }}>
                      <span style={{ fontSize:12,fontWeight:700,color:'var(--sub)',textTransform:'uppercase',letterSpacing:'.04em' }}>Job {i+1}</span>
                      {splitRows.length > 2 && (
                        <button onClick={()=>setSplitRows(p=>p.filter((_,j)=>j!==i))}
                          style={{ background:'none',border:'none',color:'var(--sub)',cursor:'pointer',fontSize:18,lineHeight:1 }}>✕</button>
                      )}
                    </div>
                    <select value={row.job_id} onChange={e=>setSplitRows(p=>p.map((r,j)=>j===i?{...r,job_id:e.target.value}:r))}
                      style={{ width:'100%',padding:'12px',borderRadius:8,border:'1px solid var(--border)',fontSize:14,marginBottom:10,background:'#fff' }}>
                      <option value=''>Select job...</option>
                      {activeJobs.map(j=><option key={j.id} value={j.id}>{j.name}</option>)}
                    </select>
                    <div style={{ position:'relative' }}>
                      <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',fontSize:15,color:'var(--sub)',fontWeight:600 }}>
                        {splitMode==='pct'?'%':'$'}
                      </span>
                      <input type='number' inputMode='decimal' placeholder='0'
                        defaultValue={row.amount}
                        onBlur={e=>setSplitRows(p=>p.map((r,j)=>j===i?{...r,amount:e.target.value}:r))}
                        style={{ width:'100%',padding:'12px 12px 12px 28px',borderRadius:8,border:'1px solid var(--border)',fontSize:16,boxSizing:'border-box',background:'#fff' }} />
                    </div>
                  </div>
                ))}

                <button onClick={()=>setSplitRows(p=>[...p,{job_id:'',amount:''}])}
                  style={{ width:'100%',padding:'11px 0',borderRadius:9,border:'2px dashed var(--border)',
                    background:'none',color:'var(--accent)',fontWeight:700,fontSize:14,cursor:'pointer',marginBottom:4 }}>
                  + Add Another Job
                </button>
              </>
            )}
          </div>

          {/* Footer — sticky */}
          <div style={{ padding:'12px 20px',borderTop:'1px solid var(--border)',flexShrink:0,
            paddingBottom:mobile?'80px':'12px' }}>
            {isSplit ? (
              <button onClick={()=>assignSplit(id,amount)} disabled={assigning===id}
                style={{ width:'100%',padding:14,background:'var(--accent)',color:'#fff',border:'none',
                  borderRadius:10,fontWeight:800,fontSize:15,cursor:'pointer',marginBottom:8 }}>
                {assigning===id ? 'Saving...' : '✓ Save Split'}
              </button>
            ) : null}
            <button onClick={()=>{ setPicker(null); setSplitMode('dollar'); setSplitRows([{job_id:'',amount:''},{job_id:'',amount:''}]); }}
              style={{ width:'100%',padding:12,background:'none',border:'1px solid var(--border)',
                borderRadius:10,color:'var(--sub)',fontWeight:700,fontSize:14,cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };
  if (loading) return <div className="card" style={{padding:20}}>{[1,2,3].map(i=><Skel key={i} h={44} mb={8}/>)}</div>;

  return (
    <div>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontSize:18,fontWeight:800,color:'var(--navy)'}}>Cost Matching</div>
          <div style={{fontSize:13,color:'var(--sub)',marginTop:2}}>
            {totalUnmatched > 0 ? <span style={{color:'var(--amber)',fontWeight:700}}>{totalUnmatched} unmatched cost{totalUnmatched!==1?'s':''} need job assignment</span> : <span style={{color:'var(--green)',fontWeight:700}}>All costs matched ✓</span>}
          </div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={() => { setAddForm('bill'); setForm({}); }} style={{padding:'8px 14px',background:'var(--surface)',color:'var(--navy)',border:'1px solid var(--border)',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>+ Add Bill</button>
          <button onClick={() => { setAddForm('labor'); setForm({}); }} style={{padding:'8px 14px',background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>+ Add Labor</button>
        </div>
      </div>

      {/* Add cost form */}
      {addForm && (
        <div className="card" style={{padding:20,marginBottom:16}}>
          <div style={{fontWeight:700,color:'var(--navy)',fontSize:14,marginBottom:14}}>
            {addForm==='bill' ? 'Add Material / Bill' : 'Add Labor Entry'}
          </div>
          <div style={{display:'grid',gridTemplateColumns:mobile?'1fr':'repeat(2,1fr)',gap:12,marginBottom:12}}>
            {addForm==='bill' ? <>
              <div><label className="fl">Vendor</label><input className="field" value={form.vendor||''} onChange={e=>setForm(p=>({...p,vendor:e.target.value}))} placeholder="e.g. Home Depot"/></div>
              <div><label className="fl">Amount *</label><input className="field" type="number" step="0.01" value={form.amount||''} onChange={e=>setForm(p=>({...p,amount:e.target.value}))} placeholder="0.00"/></div>
              <div><label className="fl">Description</label><input className="field" value={form.description||''} onChange={e=>setForm(p=>({...p,description:e.target.value}))} placeholder="Wire, conduit, fixtures..."/></div>
              <div><label className="fl">Category</label>
                <select className="field" value={form.category||''} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                  <option value="">Select...</option>
                  {(costCategories.length ? costCategories : ['Materials','Labor','Equipment','Subcontractors','Permits','Overhead']).map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label className="fl">Date</label><input className="field" type="date" value={form.date||new Date().toISOString().split('T')[0]} onChange={e=>setForm(p=>({...p,date:e.target.value}))}/></div>
              <div><label className="fl">Assign to Job (optional)</label>
                <select className="field" value={form.job_id||''} onChange={e=>setForm(p=>({...p,job_id:e.target.value}))}>
                  <option value="">Unassigned</option>
                  {jobs.map(j=><option key={j.id} value={j.id}>{j.name}</option>)}
                </select>
              </div>
            </> : <>
              <div><label className="fl">Employee Name *</label><input className="field" value={form.employee_name||''} onChange={e=>setForm(p=>({...p,employee_name:e.target.value}))} placeholder="e.g. M. Torres"/></div>
              <div><label className="fl">Role</label>
                <select className="field" value={form.role||''} onChange={e=>setForm(p=>({...p,role:e.target.value}))}>
                  <option value="">Select...</option>
                  {['Master Electrician','Lead Electrician','Journeyman','Apprentice','Helper'].map(r=><option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div><label className="fl">Hours *</label><input className="field" type="number" step="0.25" value={form.hours||''} onChange={e=>setForm(p=>({...p,hours:e.target.value}))} placeholder="8"/></div>
              <div><label className="fl">Hourly Rate *</label><input className="field" type="number" step="0.01" value={form.rate||''} onChange={e=>setForm(p=>({...p,rate:e.target.value}))} placeholder="32.00"/></div>
              <div><label className="fl">Pay Period End</label><input className="field" type="date" value={form.pay_period_end||new Date().toISOString().split('T')[0]} onChange={e=>setForm(p=>({...p,pay_period_end:e.target.value}))}/></div>
              <div><label className="fl">Assign to Job (optional)</label>
                <select className="field" value={form.job_id||''} onChange={e=>setForm(p=>({...p,job_id:e.target.value}))}>
                  <option value="">Unassigned</option>
                  {jobs.map(j=><option key={j.id} value={j.id}>{j.name}</option>)}
                </select>
              </div>
            </>}
          </div>
          {addForm==='labor' && form.hours && form.rate && (
            <div style={{background:'#eff6ff',borderRadius:8,padding:'8px 14px',fontSize:13,color:'#1e40af',marginBottom:12}}>
              Total cost: <strong>${(parseFloat(form.hours||0)*parseFloat(form.rate||0)).toFixed(2)}</strong>
            </div>
          )}
          <div style={{display:'flex',gap:10}}>
            <button onClick={addCost} disabled={saving} style={{padding:'10px 20px',background:'var(--navy)',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13}}>{saving?'Saving...':'Save'}</button>
            <button onClick={()=>{setAddForm(null);setForm({});}} style={{padding:'10px 16px',background:'var(--surface)',color:'var(--sub)',border:'1px solid var(--border)',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div data-tour="costs-tabs" className="card" style={{overflow:'hidden'}}>
        <div style={{display:'flex',borderBottom:'1px solid var(--border)'}}>
          {[['bills',`Unmatched Bills (${unmatched.bills?.length||0})`],['labor',`Unmatched Labor (${unmatched.labor?.length||0})`],['matched','Matched Costs']].map(([key,label])=>(
            <button key={key} onClick={()=>setTab(key)} style={{flex:1,padding:'13px 16px',background:'none',border:'none',borderBottom:`3px solid ${tab===key?'var(--accent)':'transparent'}`,color:tab===key?'var(--accent)':'var(--sub)',fontWeight:tab===key?700:400,cursor:'pointer',fontSize:13,transition:'all .15s'}}>
              {label}
            </button>
          ))}
        </div>

        {tab==='bills' && (
          <div>
            {unmatched.bills?.length===0 ? (
              <div style={{textAlign:'center',padding:'40px 0',color:'var(--sub)'}}>
                <div style={{fontSize:28,marginBottom:10}}>✅</div>
                <div style={{fontWeight:700,color:'var(--navy)',marginBottom:4}}>All bills matched</div>
                <div style={{fontSize:13}}>QBO bills synced from QuickBooks will appear here when they need a job assigned</div>
              </div>
            ) : mobile ? (
              <div style={{padding:'12px 16px'}}>
                {unmatched.bills.map(b=>(
                  <div key={b.id} className="mc" style={{marginBottom:10}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                      <div>
                        <div style={{fontWeight:700,color:'var(--navy)',fontSize:13}}>{b.vendor||'Unknown Vendor'}</div>
                        <div style={{color:'var(--sub)',fontSize:12,marginTop:2}}>{b.description||'No description'}</div>
                        <div style={{fontSize:11,color:'#9ca3af',marginTop:2}}>{b.date} · {b.source}</div>
                      </div>
                      <div style={{fontWeight:800,color:'var(--navy)',fontSize:15}}>${parseFloat(b.amount).toFixed(2)}</div>
                    </div>
                    <div style={{display:'flex',gap:8}}>
                      <button onClick={()=>setPicker({type:'bills',id:b.id,desc:b.vendor||b.description,amount:b.amount,detail:b.description,date:b.date,category:b.category})} style={{flex:1,padding:'9px 0',background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:12}}>
                        Assign →
                      </button>
                      <button onClick={()=>{setSplitMode('dollar');setPicker({type:'bills',id:b.id,desc:b.vendor||b.description,amount:b.amount,detail:b.description,date:b.date,category:b.category});}} style={{padding:'9px 14px',background:'none',border:'1px solid var(--accent)',color:'var(--accent)',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:12}}>
                        ✂️
                     </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Vendor</th><th>Description</th><th>Category</th><th>Amount</th><th>Date</th><th>Source</th><th></th></tr></thead>
                  <tbody>
                    {unmatched.bills.map(b=>(
                      <tr key={b.id}>
                        <td style={{fontWeight:700,color:'var(--navy)'}}>{b.vendor||'—'}</td>
                        <td style={{color:'var(--sub)',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.description||'—'}</td>
                        <td><span style={{fontSize:11,color:'var(--sub)'}}>{b.category||'—'}</span></td>
                        <td style={{fontWeight:700}}>${parseFloat(b.amount).toFixed(2)}</td>
                        <td style={{color:'var(--sub)',fontSize:12}}>{b.date}</td>
                        <td><span className="badge" style={{background:b.source==='quickbooks'?'#ede9fe':'#f3f4f6',color:b.source==='quickbooks'?'#5b21b6':'#374151',fontSize:10}}>{b.source}</span></td>
                        <td>
                          <button onClick={()=>setPicker({type:'bills',id:b.id,desc:b.vendor||b.description,amount:b.amount})} style={{padding:'5px 12px',background:'var(--accent)',color:'#fff',border:'none',borderRadius:6,fontWeight:700,cursor:'pointer',fontSize:11,whiteSpace:'nowrap'}}>
                            Assign →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab==='labor' && (
          <div>
            {unmatched.labor?.length===0 ? (
              <div style={{textAlign:'center',padding:'40px 0',color:'var(--sub)'}}>
                <div style={{fontSize:28,marginBottom:10}}>✅</div>
                <div style={{fontWeight:700,color:'var(--navy)',marginBottom:4}}>All labor matched</div>
                <div style={{fontSize:13}}>Payroll entries from ADP, Gusto, or Paychex appear here when they need a job assigned</div>
              </div>
            ) : mobile ? (
              <div style={{padding:'12px 16px'}}>
                {unmatched.labor.map(l=>(
                  <div key={l.id} className="mc" style={{marginBottom:10}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                      <div>
                        <div style={{fontWeight:700,color:'var(--navy)',fontSize:13}}>{l.employee_name}</div>
                        <div style={{color:'var(--sub)',fontSize:12,marginTop:2}}>{l.role||'Employee'} · {l.hours}h @ ${parseFloat(l.rate).toFixed(2)}/hr</div>
                        <div style={{fontSize:11,color:'#9ca3af',marginTop:2}}>{l.pay_period_end} · {l.source}</div>
                      </div>
                      <div style={{fontWeight:800,color:'var(--navy)',fontSize:15}}>${parseFloat(l.total_cost).toFixed(2)}</div>
                    </div>
                    <button onClick={()=>setPicker({type:'labor',id:l.id,desc:`${l.employee_name} — ${l.hours}h`,amount:l.total_cost})} style={{width:'100%',padding:'9px 0',background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:12}}>
                      Assign to Job →
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Employee</th><th>Role</th><th>Hours</th><th>Rate</th><th>Total Cost</th><th>Pay Period</th><th>Source</th><th></th></tr></thead>
                  <tbody>
                    {unmatched.labor.map(l=>(
                      <tr key={l.id}>
                        <td style={{fontWeight:700,color:'var(--navy)'}}>{l.employee_name}</td>
                        <td style={{color:'var(--sub)'}}>{l.role||'—'}</td>
                        <td>{l.hours}h</td>
                        <td>${parseFloat(l.rate).toFixed(2)}/hr</td>
                        <td style={{fontWeight:700}}>${parseFloat(l.total_cost).toFixed(2)}</td>
                        <td style={{color:'var(--sub)',fontSize:12}}>{l.pay_period_end}</td>
                        <td><span className="badge" style={{background:l.source!=='manual'?'#dbeafe':'#f3f4f6',color:l.source!=='manual'?'#1e40af':'#374151',fontSize:10}}>{l.source}</span></td>
                        <td>
                          <button onClick={()=>setPicker({type:'labor',id:l.id,desc:`${l.employee_name} — ${l.hours}h`,amount:l.total_cost})} style={{padding:'5px 12px',background:'var(--accent)',color:'#fff',border:'none',borderRadius:6,fontWeight:700,cursor:'pointer',fontSize:11,whiteSpace:'nowrap'}}>
                            Assign →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {tab==='matched' && <MatchedCosts jobs={jobs} onReassign={(type,id,desc,amount)=>setPicker({type,id,desc,amount})} />}
      </div>  {/* closes the card */}
      {picker && <JobPicker type={picker.type} id={picker.id} desc={picker.desc} amount={picker.amount} />}
    </div>
  );
}

// ─── JOBS ─────────────────────────────────────────────────────────────────────
function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [picker, setPicker] = useState(null);
  const [profitMode, setProfitMode] = useState('dollar');
  const [sortBy, setSortBy] = useState('status');
  const [showJobForm, setShowJobForm] = useState(false);
  const [editJob, setEditJob] = useState(null); // null = add, obj = edit
  const [jobForm, setJobForm] = useState({});
  const [savingJob, setSavingJob] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const mobile = window.innerWidth < 768;
  // Set when a job was opened from the dashboard's Recent Jobs list
  const openJobId = location.state?.openJobId;
  const fromDashboard = location.state?.from === 'dashboard';

  const loadJobs = () => {
    setLoading(true);
    api.get('/dashboard').then(r => {
      const list = r.data.jobs || [];
      setJobs(list);
      if (openJobId) { const j = list.find(x => x.id === openJobId); if (j) setDetail(j); }
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { loadJobs(); }, []);

  function openAdd() {
    setJobForm({ status: 'active', budget_labor: '', budget_materials: '', budget_overhead: '', budget_subcontractor: '' });
    setEditJob(null);
    setShowJobForm(true);
  }

  function openEdit(j) {
    setJobForm({
      name: j.name || '',
      customer_name: j.customerName || '',
      description: j.description || '',
      status: j.status || 'active',
      start_date: j.startDate?.slice(0,10) || '',
      end_date: j.endDate?.slice(0,10) || '',
      budget_labor: j.budgetLabor || '',
      budget_materials: j.budgetMaterials || '',
      budget_overhead: j.budgetOverhead || '',
      budget_subcontractor: j.budgetSubcontractor || '',
    });
    setEditJob(j);
    setShowJobForm(true);
  }

  async function saveJob() {
    if (!jobForm.name?.trim()) { toast.error('Job name is required'); return; }
    setSavingJob(true);
    try {
      const payload = {
        ...jobForm,
        budget_labor:          parseFloat(jobForm.budget_labor)          || 0,
        budget_materials:      parseFloat(jobForm.budget_materials)      || 0,
        budget_overhead:       parseFloat(jobForm.budget_overhead)       || 0,
        budget_subcontractor:  parseFloat(jobForm.budget_subcontractor)  || 0,
        start_date: jobForm.start_date || null,
        end_date:   jobForm.end_date   || null,
      };
      if (editJob) {
        await api.patch(`/jobs/${editJob.id}`, payload);
        toast.success('Job updated');
      } else {
        await api.post('/jobs', payload);
        toast.success(`${jobForm.name} created`);
      }
      setShowJobForm(false); setEditJob(null); setJobForm({});
      // Reload jobs AND update detail if we just edited the currently viewed job
      api.get('/dashboard').then(r => {
        const fresh = r.data.jobs || [];
        setJobs(fresh);
        if (editJob && detail?.id === editJob.id) {
          const updated = fresh.find(j => j.id === editJob.id);
          if (updated) setDetail(updated);
        }
      });
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setSavingJob(false); }
  }

  async function deleteJob(j) {
    if (!window.confirm(`Delete "${j.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/jobs/${j.id}`);
      toast.success(`${j.name} deleted`);
      setDetail(null);
      loadJobs();
    } catch(e) { toast.error(getErrorMessage(e)); }
  }

  function applyStatus(jobId, s) {
    setPicker(null);
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: s } : j));
    if (detail?.id === jobId) setDetail(prev => ({ ...prev, status: s }));
    toast.success(`${jobId.slice(0, 8)} → ${s === 'on_hold' ? 'On Hold' : s.charAt(0).toUpperCase() + s.slice(1)}`);
    api.patch(`/jobs/${jobId}`, { status: s }).catch(() => {});
  }

  if (loading) return <div className="card" style={{ padding: 20 }}>{[1,2,3,4,5].map(i => <Skel key={i} h={44} mb={8} />)}</div>;

  // ── Add / Edit Job form ────────────────────────────────────────────────────
  if (showJobForm) {
    const F = jobForm;
    const set = (k, v) => setJobForm(p => ({...p, [k]: v}));
    const totalBudget = (parseFloat(F.budget_labor)||0) + (parseFloat(F.budget_materials)||0) + (parseFloat(F.budget_overhead)||0) + (parseFloat(F.budget_subcontractor)||0);
    return (
      <div>
        <button onClick={() => { setShowJobForm(false); setEditJob(null); }} style={{ background:'none', border:'1px solid var(--border)', borderRadius:8, padding:'8px 14px', color:'var(--navy)', cursor:'pointer', fontSize:13, fontWeight:600, marginBottom:14 }}>← Back</button>
        <div className="card" style={{ padding: mobile ? 18 : 24 }}>
          <div className="sh" style={{ marginBottom: 4 }}>{editJob ? `Edit ${editJob.name}` : 'Add New Job'}</div>
          <div className="ss" style={{ marginBottom: 20 }}>Set budgets to track costs against your estimate in real time</div>

          {/* Job details */}
          <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap:14, marginBottom:14 }}>
            <div style={{ gridColumn: mobile ? 1 : '1 / -1' }}>
              <label className="fl">Job Name *</label>
              <input className="field" value={F.name||''} onChange={e=>set('name',e.target.value)} placeholder="e.g. 123 Main St — Panel Upgrade" />
            </div>
            <div>
              <label className="fl">Customer / Client</label>
              <input className="field" value={F.customer_name||''} onChange={e=>set('customer_name',e.target.value)} placeholder="e.g. ABC Construction" />
            </div>
            <div>
              <label className="fl">Status</label>
              <select className="field" value={F.status||'active'} onChange={e=>set('status',e.target.value)}>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="fl">Start Date</label>
              <input className="field" type="date" value={F.start_date||''} onChange={e=>set('start_date',e.target.value)} />
            </div>
            <div>
              <label className="fl">End Date</label>
              <input className="field" type="date" value={F.end_date||''} onChange={e=>set('end_date',e.target.value)} />
            </div>
            <div style={{ gridColumn: mobile ? 1 : '1 / -1' }}>
              <label className="fl">Description / Scope</label>
              <textarea className="field" rows={2} value={F.description||''} onChange={e=>set('description',e.target.value)} placeholder="Brief description of the job scope..." />
            </div>
          </div>

          {/* Budget section */}
          <div style={{ background:'var(--surface)', borderRadius:12, padding:'16px 18px', marginBottom:18 }}>
            <div style={{ fontWeight:700, color:'var(--navy)', fontSize:13, marginBottom:14 }}>Budget Breakdown</div>
            <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(4,1fr)', gap:12, marginBottom:12 }}>
              {[
                ['budget_labor',         'Labor',         '👷'],
                ['budget_materials',     'Materials',     '🔧'],
                ['budget_overhead',      'Overhead',      '🏢'],
                ['budget_subcontractor', 'Subcontractors','🤝'],
              ].map(([key, label, icon]) => (
                <div key={key}>
                  <label className="fl">{icon} {label}</label>
                  <div style={{ position:'relative' }}>
                    <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--sub)', fontSize:13 }}>$</span>
                    <input className="field" type="number" min="0" step="100" value={F[key]||''} onChange={e=>set(key,e.target.value)} placeholder="0" style={{ paddingLeft:22 }} />
                  </div>
                </div>
              ))}
            </div>
            {totalBudget > 0 && (
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:10, borderTop:'1px solid var(--border)' }}>
                <span style={{ fontSize:13, fontWeight:600, color:'var(--sub)' }}>Total Budget</span>
                <span style={{ fontSize:16, fontWeight:800, color:'var(--navy)' }}>${totalBudget.toLocaleString('en-US', { minimumFractionDigits:2, maximumFractionDigits:2 })}</span>
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={saveJob} disabled={savingJob||!jobForm.name?.trim()} style={{ flex:2, padding:12, background:'var(--navy)', color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:14 }}>
              {savingJob ? 'Saving...' : editJob ? '💾 Save Changes' : '+ Create Job'}
            </button>
            <button onClick={() => { setShowJobForm(false); setEditJob(null); }} style={{ flex:1, padding:12, background:'var(--surface)', color:'var(--sub)', border:'1px solid var(--border)', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:14 }}>Cancel</button>
          </div>
          {editJob && (
            <button onClick={() => deleteJob(editJob)} style={{ width:'100%', marginTop:10, padding:10, background:'none', color:'var(--red)', border:'1px solid #fca5a5', borderRadius:10, fontWeight:600, cursor:'pointer', fontSize:13 }}>
              🗑 Delete This Job
            </button>
          )}
        </div>
      </div>
    );
  }

  // Job detail
  if (detail) {
    const j = detail;
    const labor          = parseFloat(j.actualLabor) || 0;
    const materials      = parseFloat(j.actualMaterials) || 0;
    const taxes          = Math.round((labor + materials) * 0.0765);
    const overhead       = Math.round((labor + materials) * 0.05);
    const totalActual    = labor + materials + taxes + overhead;
    const invoiced       = parseFloat(j.invoicedAmount) || 0;
    const profit         = invoiced - totalActual;
    const profitMargin   = invoiced > 0 ? Math.round((profit / invoiced) * 100) : null;

    const pieData = [
      { label: 'Labor',    value: labor,     color: '#4a9edd' },
      { label: 'Materials',value: materials,  color: '#10b981' },
      { label: 'Taxes',    value: taxes,      color: '#f59e0b' },
      { label: 'Overhead', value: overhead,   color: '#6366f1' },
    ].filter(d => d.value > 0);

    function JobPieChart() {
      const ref = React.useRef(null);
      const cr  = React.useRef(null);
      useEffect(() => {
        if (!ref.current || !pieData.length) return;
        const init = () => {
          if (cr.current) cr.current.destroy();
          cr.current = new window.Chart(ref.current, {
            type: 'doughnut',
            data: {
              labels: pieData.map(d => d.label),
              datasets: [{ data: pieData.map(d => d.value), backgroundColor: pieData.map(d => d.color), borderWidth: 0, hoverOffset: 6 }],
            },
            options: {
              responsive: true, maintainAspectRatio: false, cutout: '65%',
              plugins: {
                legend: { display: false },
                tooltip: { callbacks: { label: ctx => ` ${ctx.label}: $${Math.round(ctx.raw).toLocaleString()} (${totalActual > 0 ? Math.round(ctx.raw / totalActual * 100) : 0}%)` } },
              },
            },
          });
        };
        if (window.Chart) init();
        else {
          const existing = document.getElementById('chartjs-script');
          if (!existing) { const s = document.createElement('script'); s.id = 'chartjs-script'; s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js'; s.onload = init; document.head.appendChild(s); }
          else existing.addEventListener('load', init);
        }
        return () => { if (cr.current) { cr.current.destroy(); cr.current = null; } };
      }, []);
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', height: 140, width: 140, flexShrink: 0 }}>
            <canvas ref={ref} role="img" aria-label="Doughnut chart showing job cost breakdown" />
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pieData.map(d => (
              <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                <span style={{ color: 'var(--sub)', flex: 1 }}>{d.label}</span>
                <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{fmtFull(d.value)}</span>
                <span style={{ color: 'var(--sub)', fontSize: 11, minWidth: 32, textAlign: 'right' }}>{totalActual > 0 ? Math.round(d.value / totalActual * 100) : 0}%</span>
              </div>
            ))}
            <div style={{ borderTop: '1px solid var(--divider)', paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ fontWeight: 700, color: 'var(--navy)' }}>Total Costs</span>
              <span style={{ fontWeight: 800, color: 'var(--navy)' }}>{fmtFull(totalActual)}</span>
            </div>
            {invoiced > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: 'var(--navy)' }}>Profit</span>
                <span style={{ fontWeight: 800, color: profit >= 0 ? 'var(--green)' : 'var(--red)' }}>
                  {fmtFull(profit)} {profitMargin !== null ? `(${profitMargin}%)` : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div>
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          <button onClick={() => fromDashboard ? navigate('/') : setDetail(null)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', color: 'var(--navy)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>← Back</button>
          <button onClick={() => openEdit(detail)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', color: 'var(--navy)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>✏️ Edit Job</button>
        </div>
        <div className="card" style={{ padding: mobile ? 18 : 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{j.name}</div>
              <div style={{ fontSize: 13, color: 'var(--sub)', marginTop: 3 }}>{j.customerName}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <span className="badge" onClick={() => setPicker({ jobId: j.id, current: j.status })} style={{ ...(SC[j.status] || {}), cursor: 'pointer' }}>
                {j.status === 'on_hold' ? 'On Hold' : j.status.charAt(0).toUpperCase() + j.status.slice(1)} ▼
              </span>
              <span style={{ fontSize: 11, color: 'var(--sub)' }}>tap to update</span>
            </div>
          </div>

          {/* KPI row — now includes Revenue and Profit */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            {invoiced > 0 && <KPI label="Revenue" icon="" val={fmtFull(invoiced)} sub={fmtFull(j.paidAmount) + ' collected'} up={null} />}
            <KPI label="Total Costs" icon="" val={fmtFull(totalActual)} sub={`${j.pctUsed}% of budget used`} up={j.variance >= 0} />
            <KPI label="Remaining Budget" icon="" val={fmtFull(Math.abs(j.variance))} sub={j.variance >= 0 ? 'under budget' : 'over budget'} up={j.variance >= 0} />
            {invoiced > 0 && <KPI label="Profit" icon="" val={fmtFull(profit)} sub={profitMargin !== null ? `${profitMargin}% margin` : ''} up={profit >= 0} />}
          </div>

          {/* Pie chart */}
          {totalActual > 0 ? (
            <div style={{ background: 'var(--surface)', borderRadius: 10, padding: 16, marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13, marginBottom: 14 }}>Cost Breakdown</div>
              <JobPieChart />
            </div>
          ) : (
            <div style={{ background: 'var(--surface)', borderRadius: 10, padding: 16, marginBottom: 14, textAlign: 'center', color: 'var(--sub)', fontSize: 13 }}>
              No costs recorded yet for this job
            </div>
          )}

          {/* Dates */}
          {(j.startDate || j.endDate) && (
            <div style={{ padding: '12px 16px', background: '#eff6ff', borderRadius: 10, fontSize: 13, color: '#1e40af' }}>
              {j.startDate && <><strong>Start:</strong> {j.startDate?.slice(0, 10)} &nbsp;·&nbsp;</>}
              {j.endDate && <><strong>End:</strong> {j.endDate?.slice(0, 10)}</>}
            </div>
          )}
        </div>
        {picker && <Picker type="job" id={picker.jobId} current={picker.current} onClose={() => setPicker(null)} onApply={s => applyStatus(picker.jobId, s)} />}
      </div>
    );
  }

  const fmtProfit = (n) => {
    if (!n && n !== 0) return '$0.00';
    return (n < 0 ? '-$' : '$') + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const sortedJobs = [...jobs].sort((a, b) => {
    switch (sortBy) {
      case 'start_date': return new Date(b.startDate || 0) - new Date(a.startDate || 0);
      case 'budget_desc': return (b.budgetTotal || 0) - (a.budgetTotal || 0);
      case 'budget_asc': return (a.budgetTotal || 0) - (b.budgetTotal || 0);
      case 'profit_desc': return (b.profit || -Infinity) - (a.profit || -Infinity);
      case 'profit_asc': return (a.profit || Infinity) - (b.profit || Infinity);
      default: { const o = { on_hold: 0, active: 1, completed: 2 }; return (o[a.status] ?? 3) - (o[b.status] ?? 3); }
    }
  });

  const SORTS = [
    { key: 'status',       label: 'Status' },
    { key: 'start_date',   label: 'Start Date' },
    { key: 'budget_desc',  label: 'Size ↓' },
    { key: 'budget_asc',   label: 'Size ↑' },
    { key: 'profit_desc',  label: 'Profit ↓' },
    { key: 'profit_asc',   label: 'Profit ↑' },
  ];

  return (
    <div>
      <div data-tour="jobs-chart">
        {jobs.length > 0 && <JobsBarChart jobs={jobs} />}
      </div>
      <div data-tour="jobs-table" className="card" style={{ padding: mobile ? 16 : 20, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
        <div className="sh" data-tour="jobs-header">All Jobs</div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={openAdd} style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ Add Job</button>
          <button onClick={() => navigate('/jobcost')} title="Job Cost Setup" style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 10px', cursor: 'pointer', fontSize: 16, color: 'var(--sub)', lineHeight: 1 }}></button>
        </div>
      </div>
      {/* Sort pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontSize: 11, color: 'var(--sub)', fontWeight: 600, alignSelf: 'center', marginRight: 2 }}>Sort:</span>
        {SORTS.map(s => (
          <button key={s.key} onClick={() => setSortBy(s.key)} style={{ padding: '4px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: sortBy === s.key ? 'var(--navy)' : 'var(--surface)', color: sortBy === s.key ? '#fff' : 'var(--sub)', border: `1px solid ${sortBy === s.key ? 'var(--navy)' : 'var(--border)'}`, whiteSpace: 'nowrap' }}>{s.label}</button>
        ))}
      </div>
      <div className="ss" style={{ marginBottom: 10 }}>Tap any status badge to update · tap job name to drill in</div>
      {jobs.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px 0' }}>
          <div style={{ fontSize:40, marginBottom:12 }}></div>
          <div style={{ fontWeight:700, color:'var(--navy)', fontSize:15, marginBottom:6 }}>No jobs yet</div>
          <div style={{ fontSize:13, color:'var(--sub)', marginBottom:18 }}>Add a job manually or connect QuickBooks to sync your jobs automatically</div>
          <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
            <button onClick={openAdd} style={{ padding:'10px 20px', background:'var(--navy)', color:'#fff', border:'none', borderRadius:9, fontWeight:700, cursor:'pointer', fontSize:13 }}>+ Add Your First Job</button>
            <button onClick={() => navigate('/integrations')} style={{ padding:'10px 20px', background:'var(--surface)', color:'var(--navy)', border:'1px solid var(--border)', borderRadius:9, fontWeight:700, cursor:'pointer', fontSize:13 }}>Connect QuickBooks →</button>
          </div>
        </div>
      ) : mobile ? (
        sortedJobs.map(j => (
          <div key={j.id} className="mc" onClick={() => setDetail(j)} style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 13 }}>{j.name?.slice(0, 28)}</div>
                <div style={{ color: 'var(--navy)', fontWeight: 600, fontSize: 13, marginTop: 2 }}>{j.customerName}</div>
              </div>
              <span className="badge" onClick={e => { e.stopPropagation(); setPicker({ jobId: j.id, current: j.status }); }} style={{ ...(SC[j.status] || {}), cursor: 'pointer' }}>
                {j.status === 'on_hold' ? 'On Hold' : j.status.charAt(0).toUpperCase() + j.status.slice(1)} ▼
              </span>
            </div>
            <div style={{ display: 'flex', gap: 16, paddingTop: 8, borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
              {j.invoicedAmount > 0 && <div><div style={{ fontSize: 10, color: 'var(--sub)', fontWeight: 600, textTransform: 'uppercase' }}>Revenue</div><div style={{ fontSize: 14, fontWeight: 800 }}>{fmt(j.invoicedAmount)}</div></div>}
              <div><div style={{ fontSize: 10, color: 'var(--sub)', fontWeight: 600, textTransform: 'uppercase' }}>Actual Cost</div><div style={{ fontSize: 14, fontWeight: 800 }}>{fmt(j.actualTotal)}</div></div>
              {j.invoicedAmount > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--sub)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>
                    Profit
                    <button onClick={e => { e.stopPropagation(); setProfitMode(profitMode === 'dollar' ? 'percent' : 'dollar'); }} style={{ padding: '0px 5px', borderRadius: 8, fontSize: 9, fontWeight: 700, cursor: 'pointer', background: 'var(--navy)', color: '#fff', border: 'none', lineHeight: 1.6 }}>
                      {profitMode === 'dollar' ? '$→%' : '%→$'}
                    </button>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: j.profit >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {profitMode === 'dollar' ? fmtProfit(j.profit) : `${j.profitMargin ?? 0}%`}
                  </div>
                </div>
              )}
              <div><div style={{ fontSize: 10, color: 'var(--sub)', fontWeight: 600, textTransform: 'uppercase' }}>Budget Left</div><div style={{ fontSize: 14, fontWeight: 800, color: j.variance >= 0 ? 'var(--green)' : 'var(--red)' }}>{j.variance >= 0 ? fmt(j.variance) : `▲ ${fmt(Math.abs(j.variance))}`}</div></div>
            </div>
          </div>
        ))
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Job Name</th><th>Customer</th><th>Revenue</th><th>Actual Cost</th>
                <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    Profit
                    <span style={{ display: 'inline-flex', gap: 3, marginLeft: 4 }}>
                      <button onClick={e => { e.stopPropagation(); setProfitMode('dollar'); }} style={{ padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700, cursor: 'pointer', background: profitMode === 'dollar' ? 'var(--navy)' : 'transparent', color: profitMode === 'dollar' ? '#fff' : 'var(--sub)', border: `1px solid ${profitMode === 'dollar' ? 'var(--navy)' : 'var(--border)'}`, lineHeight: 1.4 }}>$</button>
                      <button onClick={e => { e.stopPropagation(); setProfitMode('percent'); }} style={{ padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 700, cursor: 'pointer', background: profitMode === 'percent' ? 'var(--navy)' : 'transparent', color: profitMode === 'percent' ? '#fff' : 'var(--sub)', border: `1px solid ${profitMode === 'percent' ? 'var(--navy)' : 'var(--border)'}`, lineHeight: 1.4 }}>%</button>
                    </span>
                  </div>
                </th>
                <th>Budget Left</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedJobs.map(j => (
                <tr key={j.id} className="ch">
                  <td style={{ color: 'var(--accent)', fontWeight: 700, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} onClick={() => setDetail(j)}>{j.name}</td>
                  <td style={{ color: 'var(--sub)' }}>{j.customerName}</td>
                  <td style={{ fontWeight: 700 }}>{j.invoicedAmount > 0 ? fmt(j.invoicedAmount) : <span style={{ color: 'var(--sub)', fontSize: 11 }}>—</span>}</td>
                  <td style={{ fontWeight: 700 }}>{fmt(j.actualTotal)}</td>
                  <td style={{ fontWeight: 800, color: j.invoicedAmount > 0 ? (j.profit >= 0 ? 'var(--green)' : 'var(--red)') : 'var(--sub)' }}>
                    {j.invoicedAmount > 0
                      ? profitMode === 'dollar'
                        ? fmtProfit(j.profit)
                        : `${j.profitMargin ?? 0}%`
                      : <span style={{ fontSize: 11 }}>No invoice</span>}
                  </td>
                  <td style={{ fontWeight: 700, color: j.variance >= 0 ? 'var(--green)' : 'var(--red)' }}>{j.variance >= 0 ? fmt(j.variance) : `▲ ${fmt(Math.abs(j.variance))}`}</td>
                  <td>
                    <span className="badge" onClick={() => setPicker({ jobId: j.id, current: j.status })} style={{ ...(SC[j.status] || {}), cursor: 'pointer', border: `1px solid ${(SC[j.status] || {}).color || '#ccc'}22` }}>
                      {j.status === 'on_hold' ? 'On Hold' : j.status.charAt(0).toUpperCase() + j.status.slice(1)} <span style={{ fontSize: 9, opacity: .7 }}>▼</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {picker && <Picker type="job" id={picker.jobId} current={picker.current} onClose={() => setPicker(null)} onApply={s => applyStatus(picker.jobId, s)} />}
    </div>
    </div>
  );
}

// ─── CLIENTS ──────────────────────────────────────────────────────────────────
function Clients() {
  const mobile = window.innerWidth < 768;
  const navigate = useNavigate();
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [detail, setDetail]     = useState(null);
  const [detailLoading, setDL]  = useState(false);
  const [sortBy, setSortBy]     = useState('balance');
  const [showAdd, setShowAdd]   = useState(false);
  const [showActivity, setAct]  = useState(false);
  const [editMode, setEdit]     = useState(false);
  const [form, setForm]         = useState({});
  const [actForm, setActForm]   = useState({ type: 'call', subject: '', body: '' });
  const [saving, setSaving]     = useState(false);
  const [calMonth, setCalMonth] = useState(new Date());
  const [calOpen, setCalOpen]   = useState(!mobile);
  const [selectedDay, setSelectedDay] = useState(null);
  const [allActivities, setAllActivities] = useState([]);
  const [swipeStart, setSwipeStart] = useState(null);
  const [phoneAction, setPhoneAction] = useState(null);
  const [search, setSearch]     = useState('');
  const toast = useToast();

  const load = () => {
    setLoading(true);
    api.get('/crm/clients').then(r => setClients(r.data || [])).catch(() => setClients([])).finally(() => setLoading(false));
    api.get('/crm/activities').then(r => setAllActivities(r.data || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const openDetail = (c) => {
    setDL(true); setDetail(null);
    api.get(`/crm/clients/${c.id}`).then(r => setDetail(r.data)).catch(() => {}).finally(() => setDL(false));
  };

  const SORTS = [
    { key: 'balance',   label: 'Balance ↓' },
    { key: 'name',      label: 'Name A–Z' },
    { key: 'invoiced',  label: 'Invoiced ↓' },
    { key: 'recent',    label: 'Newest' },
  ];

  const sorted = [...clients].sort((a, b) => {
    // Always push inactive to bottom
    if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
    switch (sortBy) {
      case 'name':     return a.name.localeCompare(b.name);
      case 'invoiced': return (parseFloat(b.total_invoiced) || 0) - (parseFloat(a.total_invoiced) || 0);
      case 'recent':   return new Date(b.created_at) - new Date(a.created_at);
      default:         return (parseFloat(b.qb_balance) || 0) - (parseFloat(a.qb_balance) || 0);
    }
  }).filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.contact_name?.toLowerCase().includes(q);
  });

  const SC_INV = { paid: { bg: '#d1fae5', color: '#065f46' }, sent: { bg: '#ede9fe', color: '#5b21b6' }, overdue: { bg: '#fee2e2', color: '#991b1b' }, partial: { bg: '#fef3c7', color: '#92400e' }, draft: { bg: '#f3f4f6', color: '#374151' }, void: { bg: '#f3f4f6', color: '#9ca3af' } };
  const ACT_ICONS = { call: '📞', email: '📧', meeting: '🤝', note: '📝' };

  async function saveClient() {
    setSaving(true);
    try {
      await api.post('/crm/clients', form);
      toast.success(`${form.name} added`);
      setShowAdd(false); setForm({});
      load();
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  async function saveEdit() {
    if (!detail) return;
    setSaving(true);
    try {
      await api.patch(`/crm/clients/${detail.id}`, form);
      toast.success('Client updated');
      setEdit(false);
      openDetail(detail);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  async function logActivity() {
    if (!detail) return;
    setSaving(true);
    try {
      await api.post(`/crm/clients/${detail.id}/activities`, actForm);
      toast.success('Activity logged');
      setAct(false); setActForm({ type: 'call', subject: '', body: '' });
      openDetail(detail);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  // ── Add client modal ──
  if (showAdd) return (
    <div>
      <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', color: 'var(--navy)', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 14 }}>← Back</button>
      <div className="card" style={{ padding: 24 }}>
        <div className="sh" style={{ marginBottom: 16 }}>Add New Client</div>
        {[['name','Client Name *'], ['contact_name','Contact Person'], ['email','Email'], ['phone','Phone'], ['city','City'], ['state','State']].map(([k, l]) => (
          <div key={k} style={{ marginBottom: 14 }}>
            <label className="fl">{l}</label>
            <input className="field" value={form[k] || ''} onChange={e => setForm(p => ({...p, [k]: e.target.value}))} />
          </div>
        ))}
        <div style={{ marginBottom: 14 }}>
          <label className="fl">Notes</label>
          <textarea className="field" rows={3} value={form.notes || ''} onChange={e => setForm(p => ({...p, notes: e.target.value}))} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={saveClient} disabled={saving || !form.name} style={{ flex: 1, padding: 12, background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>{saving ? 'Saving...' : 'Add Client'}</button>
          <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: 12, background: 'var(--surface)', color: 'var(--sub)', border: '1px solid var(--border)', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
        </div>
      </div>
    </div>
  );

  // ── Client detail ──
  if (detail || detailLoading) {
    if (detailLoading) return <div className="card" style={{ padding: 20 }}>{[1,2,3].map(i => <Skel key={i} h={44} mb={8} />)}</div>;
    const c = detail;

    // Edit mode
    if (editMode) return (
      <div>
        <button onClick={() => setEdit(false)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', color: 'var(--navy)', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 14 }}>← Cancel</button>
        <div className="card" style={{ padding: 24 }}>
          <div className="sh" style={{ marginBottom: 16 }}>Edit {c.name}</div>
          {[['name','Client Name'], ['contact_name','Contact Person'], ['email','Email'], ['phone','Phone'], ['city','City'], ['state','State']].map(([k, l]) => (
            <div key={k} style={{ marginBottom: 14 }}>
              <label className="fl">{l}</label>
              <input className="field" value={form[k] ?? c[k] ?? ''} onChange={e => setForm(p => ({...p, [k]: e.target.value}))} />
            </div>
          ))}
          <div style={{ marginBottom: 14 }}>
            <label className="fl">Notes</label>
            <textarea className="field" rows={3} value={form.notes ?? c.notes ?? ''} onChange={e => setForm(p => ({...p, notes: e.target.value}))} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={saveEdit} disabled={saving} style={{ flex: 1, padding: 12, background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save Changes'}</button>
            <button onClick={() => setEdit(false)} style={{ flex: 1, padding: 12, background: 'var(--surface)', color: 'var(--sub)', border: '1px solid var(--border)', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      </div>
    );

    return (
      <div>
        <button onClick={() => { setDetail(null); setAct(false); setEdit(false); }} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', color: 'var(--navy)', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 14 }}>← Back to Clients</button>
        <div className="card" style={{ padding: mobile ? 18 : 24, marginBottom: 14 }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{c.name}</div>
              {c.contact_name && <div style={{ fontSize: 13, color: 'var(--sub)', marginTop: 3 }}>{c.contact_name}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => { setForm({}); setEdit(true); }} style={{ padding: '7px 14px', background: 'var(--surface)', color: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>✏️ Edit</button>
              <button onClick={async () => {
                try {
                  await api.patch(`/crm/clients/${c.id}`, { is_active: !c.is_active });
                  toast.success(c.is_active ? `${c.name} marked inactive` : `${c.name} reactivated`);
                  openDetail(c);
                  load();
                } catch (e) { toast.error(getErrorMessage(e)); }
              }} style={{ padding: '7px 14px', background: c.is_active ? '#fef2f2' : '#f0fdf4', color: c.is_active ? 'var(--red)' : 'var(--green)', border: `1px solid ${c.is_active ? '#fca5a5' : '#86efac'}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                {c.is_active ? '⏸ Mark Inactive' : '▶ Reactivate'}
              </button>
              <button onClick={() => setAct(!showActivity)} style={{ padding: '7px 14px', background: showActivity ? 'var(--navy)' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ Log Activity</button>
            </div>
          </div>

          {/* KPIs */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <KPI label="Outstanding" icon="🧾" val={fmt(c.qb_balance)} sub="current balance" up={c.qb_balance === 0} />
            <KPI label="Total Invoiced" icon="" val={fmt(c.total_invoiced)} sub={`${c.invoice_count || 0} invoices`} up={null} />
            {c.email && <div className="card" style={{ flex: '1 1 130px', padding: '16px 18px' }}><div style={{ fontSize: 11, color: 'var(--sub)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 8 }}>Contact</div><a href={`mailto:${c.email}`} style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', textDecoration: 'none', display: 'block' }}>{c.email}</a>{c.phone && <span onClick={e=>{e.stopPropagation();setPhoneAction({phone:c.phone,name:c.name,id:c.id});}} style={{fontSize:12,color:'var(--accent)',cursor:'pointer',marginTop:4,display:'block'}}>{c.phone}</span>}</div>}
          </div>

          {/* Log activity form */}
          {showActivity && (
            <div style={{ background: 'var(--surface)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13, marginBottom: 12 }}>Log Activity</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                {['call','email','meeting','note'].map(t => (
                  <button key={t} onClick={() => setActForm(p => ({...p, type: t}))} style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: 'pointer', background: actForm.type === t ? 'var(--navy)' : 'var(--white)', color: actForm.type === t ? '#fff' : 'var(--sub)', border: `1px solid ${actForm.type === t ? 'var(--navy)' : 'var(--border)'}` }}>
                    {ACT_ICONS[t]} {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
              <div style={{ marginBottom: 10 }}>
                <label className="fl">Subject</label>
                <input className="field" value={actForm.subject} onChange={e => setActForm(p => ({...p, subject: e.target.value}))} placeholder={`${actForm.type} with ${c.name}`} />
              </div>
              <div style={{ marginBottom: 10 }}>
                <label className="fl">Notes</label>
                <textarea className="field" rows={2} value={actForm.body} onChange={e => setActForm(p => ({...p, body: e.target.value}))} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={logActivity} disabled={saving} style={{ flex: 1, padding: 10, background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>{saving ? 'Saving...' : 'Save Activity'}</button>
                <button onClick={() => setAct(false)} style={{ padding: '10px 16px', background: 'var(--surface)', color: 'var(--sub)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Jobs */}
          {c.jobs?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13, marginBottom: 10 }}>Jobs ({c.jobs.length})</div>
              {c.jobs.map(j => (
                <div key={j.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--divider)', fontSize: 13 }}>
                  <span style={{ color: 'var(--navy)', fontWeight: 600 }}>{j.name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ color: 'var(--sub)', fontSize: 12 }}>{fmt(j.budget_total)}</span>
                    <Badge status={j.status} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Invoices */}
          {c.invoices?.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13, marginBottom: 10 }}>Invoices</div>
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>#</th><th>Amount</th><th>Paid</th><th>Balance</th><th>Status</th><th>Due</th></tr></thead>
                  <tbody>
                    {c.invoices.map(inv => {
                      const sc = SC_INV[inv.status] || SC_INV.draft;
                      return (
                        <tr key={inv.id}>
                          <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{inv.invoice_number || '—'}</td>
                          <td style={{ fontWeight: 700 }}>{fmt(inv.amount)}</td>
                          <td style={{ color: 'var(--green)', fontWeight: 700 }}>{fmt(inv.amount_paid)}</td>
                          <td style={{ fontWeight: 700, color: inv.balance > 0 ? 'var(--amber)' : 'var(--green)' }}>{fmt(inv.balance)}</td>
                          <td><span className="badge" style={{ background: sc.bg, color: sc.color }}>{inv.status}</span></td>
                          <td style={{ color: 'var(--sub)', fontSize: 12 }}>{inv.due_date?.slice(0, 10) || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Activity log */}
          {c.activities?.length > 0 && (
            <div>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13, marginBottom: 10 }}>Activity Log</div>
              {c.activities.map(a => (
                <div key={a.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--divider)' }}>
                  <div style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{ACT_ICONS[a.type] || '📝'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13 }}>{a.subject || a.type}</div>
                    {a.body && <div style={{ fontSize: 12, color: 'var(--sub)', marginTop: 2, lineHeight: 1.5 }}>{a.body}</div>}
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{new Date(a.activity_date).toLocaleDateString()} · {a.created_by}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Activity Calendar ──────────────────────────────────────────────────────
  const ACT_COLORS_CAL = { call:'#4a9edd', email:'#10b981', meeting:'#f59e0b', note:'#6366f1' };

  function ActivityCalendar() {
    const year     = calMonth.getFullYear();
    const month    = calMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month+1, 0).getDate();
    const today    = new Date().toISOString().split('T')[0];
    const monthName = calMonth.toLocaleDateString('en-US', { month:'long', year:'numeric' });

    // Index activities by date
    const actByDate = allActivities.reduce((acc, a) => {
      const d = a.activity_date?.slice(0,10);
      if (!d) return acc;
      if (!acc[d]) acc[d] = [];
      acc[d].push(a);
      return acc;
    }, {});

    // Selected day activities
    const selectedActs = selectedDay ? (actByDate[selectedDay] || []) : [];

    const cells = [];
    for (let i=0; i<firstDay; i++) cells.push(null);
    for (let d=1; d<=daysInMonth; d++) cells.push(d);

    return (
      <div style={{ marginBottom: mobile ? 0 : 16 }}>
        <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:14, padding:'16px 18px', boxShadow:'0 1px 4px rgba(0,0,0,.06)' }}>
          {/* Month nav */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <button onClick={()=>setCalMonth(new Date(year,month-1,1))} style={{ background:'none', border:'1px solid var(--border)', borderRadius:8, padding:'5px 12px', cursor:'pointer', fontWeight:700, color:'var(--navy)', fontSize:14, touchAction:'manipulation' }}>‹</button>
            <div style={{ fontWeight:700, color:'var(--navy)', fontSize:14 }}>{monthName}</div>
            <button onClick={()=>setCalMonth(new Date(year,month+1,1))} style={{ background:'none', border:'1px solid var(--border)', borderRadius:8, padding:'5px 12px', cursor:'pointer', fontWeight:700, color:'var(--navy)', fontSize:14, touchAction:'manipulation' }}>›</button>
          </div>
          {/* Day headers */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:4 }}>
            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
              <div key={d} style={{ textAlign:'center', fontSize:10, fontWeight:700, color:'var(--sub)', padding:'3px 0' }}>{d}</div>
            ))}
          </div>
          {/* Day cells */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2 }}>
            {cells.map((d,i) => {
              if (!d) return <div key={`e${i}`}/>;
              const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
              const acts    = actByDate[dateStr] || [];
              const isToday = dateStr === today;
              const isSel   = dateStr === selectedDay;
              const types   = [...new Set(acts.map(a=>a.type))].slice(0,3);
              return (
                <div key={d}
                  onClick={() => setSelectedDay(isSel ? null : dateStr)}
                  onTouchEnd={e => { e.preventDefault(); setSelectedDay(isSel ? null : dateStr); }}
                  style={{ borderRadius:8, padding:'5px 3px', minHeight:40, textAlign:'center', cursor:'pointer', touchAction:'manipulation',
                    background: isSel ? 'var(--navy)' : isToday ? '#eff6ff' : acts.length ? '#fafafa' : 'transparent',
                    border: isSel ? '1px solid var(--navy)' : isToday ? '1px solid var(--accent)' : '1px solid transparent' }}>
                  <div style={{ fontSize:11, fontWeight:isToday?800:500, color: isSel?'#fff' : isToday?'var(--accent)':'var(--navy)' }}>{d}</div>
                  <div style={{ display:'flex', justifyContent:'center', gap:2, marginTop:2, flexWrap:'wrap' }}>
                    {types.map(t => <div key={t} style={{ width:5, height:5, borderRadius:3, background: isSel?'rgba(255,255,255,.7)' : ACT_COLORS_CAL[t]||'#ccc' }}/>)}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div style={{ display:'flex', gap:10, marginTop:10, justifyContent:'center', flexWrap:'wrap' }}>
            {Object.entries(ACT_COLORS_CAL).map(([type, color]) => (
              <div key={type} style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'var(--sub)' }}>
                <div style={{ width:7, height:7, borderRadius:4, background:color }}/>
                {type.charAt(0).toUpperCase()+type.slice(1)}
              </div>
            ))}
          </div>
        </div>

        {/* Selected day detail */}
        {selectedDay && (
          <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:14, padding:'14px 18px', marginTop:8, boxShadow:'0 1px 4px rgba(0,0,0,.06)', animation:'slideUp .2s ease' }}>
            <div style={{ fontWeight:700, color:'var(--navy)', fontSize:13, marginBottom:10 }}>
              {new Date(selectedDay+'T12:00:00').toLocaleDateString('en-US',{weekday:'long',month:'long',day:'numeric'})}
              {selectedActs.length > 0 && <span style={{ marginLeft:8, fontSize:11, color:'var(--sub)', fontWeight:400 }}>{selectedActs.length} activit{selectedActs.length===1?'y':'ies'}</span>}
            </div>
            {selectedActs.length === 0 ? (
              <div style={{ fontSize:13, color:'var(--sub)', textAlign:'center', padding:'10px 0' }}>No outreach on this day</div>
            ) : selectedActs.map((a,i) => (
              <div key={a.id}
                onClick={() => { setSelectedDay(null); setCalOpen(false); openDetail({ id: a.client_id }); }}
                style={{ display:'flex', gap:10, padding:'8px 0', borderBottom:i<selectedActs.length-1?'1px solid var(--divider)':'none', cursor:'pointer', borderRadius:6 }}>
                <span style={{ fontSize:16, flexShrink:0 }}>{ACT_ICONS[a.type]||'📝'}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, color:'var(--navy)', fontSize:12 }}>{a.subject || a.type}</div>
                  {a.client_name && <div style={{ fontSize:11, color:'var(--accent)', marginTop:1, fontWeight:600 }}>{a.client_name} →</div>}
                  {a.body && <div style={{ fontSize:11, color:'var(--sub)', marginTop:2, lineHeight:1.5 }}>{a.body}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Client list ──
  if (loading) return <div className="card" style={{ padding: 20 }}>{[1,2,3,4].map(i => <Skel key={i} h={44} mb={8} />)}</div>;

  return (
    <div>
      {/* Calendar — pinned top on desktop, toggle button on mobile */}
      <div data-tour="clients-cal">
        {(!mobile || calOpen) && <ActivityCalendar />}
      </div>

      {/* Mobile calendar toggle hint removed — use 📅 button */}

      <div data-tour="clients-list" className="card" style={{ padding: mobile ? 16 : 20, overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
        <div className="sh">Clients</div>
        <div style={{ display:'flex', gap:8 }}>
          {mobile && <button onClick={() => setCalOpen(!calOpen)} style={{ padding:'7px 12px', background: calOpen?'var(--navy)':'var(--surface)', color: calOpen?'#fff':'var(--sub)', border:`1px solid ${calOpen?'var(--navy)':'var(--border)'}`, borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer' }}>📅</button>}
          <button onClick={async () => {
            try {
              const r = await api.post('/crm/sync-from-qbo', {});
              toast.success(`Synced ${r.data.synced} clients from QuickBooks`);
              load();
            } catch(e) { toast.error(getErrorMessage(e)); }
          }} style={{ padding:'7px 12px', background:'var(--surface)', color:'var(--navy)', border:'1px solid var(--border)', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>🔄 Sync QBO</button>
          <button onClick={() => { setForm({}); setShowAdd(true); }} style={{ padding: '7px 14px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>+ Add Client</button>
        </div>
      </div>
      {/* Sort pills */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontSize: 11, color: 'var(--sub)', fontWeight: 600, alignSelf: 'center', marginRight: 2 }}>Sort:</span>
        {SORTS.map(s => (
          <button key={s.key} onClick={() => setSortBy(s.key)} style={{ padding: '4px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: sortBy === s.key ? 'var(--navy)' : 'var(--surface)', color: sortBy === s.key ? '#fff' : 'var(--sub)', border: `1px solid ${sortBy === s.key ? 'var(--navy)' : 'var(--border)'}` }}>{s.label}</button>
        ))}
      </div>
      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: 'var(--sub)', pointerEvents: 'none' }}>🔍</span>
        <input
          className="field"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by company or contact name..."
          style={{ paddingLeft: 32, fontSize: 13 }}
        />
        {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--sub)', lineHeight: 1 }}>×</button>}
      </div>
      {clients.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
          <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15, marginBottom: 6 }}>No clients yet</div>
          <div style={{ fontSize: 13, color: 'var(--sub)', marginBottom: 18 }}>Tap "🔄 Sync QBO" above to pull all your QuickBooks customers in automatically, or add clients manually</div>
          <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
            <button onClick={async () => {
              try {
                const r = await api.post('/crm/sync-from-qbo', {});
                toast.success(`Synced ${r.data.synced} clients from QuickBooks`);
                load();
              } catch(e) { toast.error(getErrorMessage(e)); }
            }} style={{ padding:'9px 18px', background:'var(--navy)', color:'#fff', border:'none', borderRadius:9, fontWeight:700, cursor:'pointer', fontSize:12 }}>🔄 Sync from QuickBooks</button>
            <button onClick={() => { setForm({}); setShowAdd(true); }} style={{ padding:'9px 18px', background:'var(--surface)', color:'var(--navy)', border:'1px solid var(--border)', borderRadius:9, fontWeight:700, cursor:'pointer', fontSize:12 }}>+ Add Manually</button>
          </div>
        </div>
      ) : sorted.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--sub)' }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>🔍</div>
          <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>No results for "{search}"</div>
          <button onClick={() => setSearch('')} style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear search</button>
        </div>
      ) : mobile ? (
        sorted.map(c => (
          <div key={c.id} className="mc" onClick={() => openDetail(c)} style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: 13 }}>{c.name}</div>
                {c.contact_name && <div style={{ color: 'var(--sub)', fontSize: 12, marginTop: 2 }}>{c.contact_name}</div>}
              </div>
              <span className="badge" style={{ background: c.is_active ? '#dbeafe' : '#f3f4f6', color: c.is_active ? '#1e40af' : '#374151' }}>{c.is_active ? 'Active' : 'Inactive'}</span>
            </div>
            <div style={{ display: 'flex', gap: 16, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
              <div><div style={{ fontSize: 10, color: 'var(--sub)', fontWeight: 600, textTransform: 'uppercase' }}>Balance</div><div style={{ fontSize: 14, fontWeight: 800, color: c.qb_balance > 0 ? 'var(--amber)' : 'var(--green)' }}>{fmt(c.qb_balance)}</div></div>
              {c.phone && <div><div style={{ fontSize: 10, color: 'var(--sub)', fontWeight: 600, textTransform: 'uppercase' }}>Phone</div><span onClick={e=>{e.stopPropagation();setPhoneAction({phone:c.phone,name:c.name,id:c.id});}} style={{fontSize:13,fontWeight:600,color:'var(--accent)',cursor:'pointer'}}>{c.phone}</span></div>}
              {c.email && <div style={{ minWidth: 0, overflow: 'hidden' }}><div style={{ fontSize: 10, color: 'var(--sub)', fontWeight: 600, textTransform: 'uppercase' }}>Email</div><a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{c.email}</a></div>}
            </div>
          </div>
        ))
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Business</th><th>Contact</th><th>Phone</th><th>Email</th><th>Location</th><th>Balance</th><th>Status</th></tr></thead>
            <tbody>
              {sorted.map(c => (
                <tr key={c.id} className="ch" onClick={() => openDetail(c)}>
                  <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{c.name}</td>
                  <td style={{ color: 'var(--sub)' }}>{c.contact_name || '—'}</td>
                  <td>{c.phone ? <a href={`tel:${c.phone}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>{c.phone}</a> : <span style={{ color: 'var(--sub)' }}>—</span>}</td>
                  <td>{c.email ? <a href={`mailto:${c.email}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>{c.email}</a> : <span style={{ color: 'var(--sub)' }}>—</span>}</td>
                  <td style={{ color: 'var(--sub)' }}>{c.city && c.state ? `${c.city}, ${c.state}` : c.city || c.state || '—'}</td>
                  <td style={{ fontWeight: 700, color: c.qb_balance > 0 ? 'var(--amber)' : 'var(--green)' }}>{fmt(c.qb_balance)}</td>
                  <td><span className="badge" style={{ background: c.is_active ? '#dbeafe' : '#f3f4f6', color: c.is_active ? '#1e40af' : '#374151' }}>{c.is_active ? 'Active' : 'Inactive'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    {phoneAction&&(<div onClick={()=>setPhoneAction(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:20}}><div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:16,padding:24,width:'100%',maxWidth:320,textAlign:'center'}}><div style={{fontSize:15,fontWeight:700,color:'var(--navy)',marginBottom:4}}>{phoneAction.name}</div><div style={{fontSize:14,color:'var(--sub)',marginBottom:20}}>{phoneAction.phone}</div><a href={`tel:${phoneAction.phone}`} onClick={async()=>{try{await api.post(`/crm/clients/${phoneAction.id}/activities`,{type:'call',subject:`Called ${phoneAction.name}`,body:`Outbound call to ${phoneAction.phone}`});toast.success('Call logged');}catch(e){}setPhoneAction(null);}} style={{display:'block',background:'var(--accent)',color:'#fff',borderRadius:10,padding:14,fontWeight:700,fontSize:15,textDecoration:'none',marginBottom:10}}>📞 Call</a><a href={`sms:${phoneAction.phone}`} onClick={async()=>{try{await api.post(`/crm/clients/${phoneAction.id}/activities`,{type:'text',subject:`Texted ${phoneAction.name}`,body:`SMS to ${phoneAction.phone}`});toast.success('Text logged');}catch(e){}setPhoneAction(null);}} style={{display:'block',background:'#16a34a',color:'#fff',borderRadius:10,padding:14,fontWeight:700,fontSize:15,textDecoration:'none',marginBottom:10}}>💬 Text</a><button onClick={()=>setPhoneAction(null)} style={{background:'none',border:'none',color:'#9ca3af',fontSize:14,cursor:'pointer',padding:8}}>Cancel</button></div></div>)}
    </div>
    </div>
  );
}

// ─── REPORTS ──────────────────────────────────────────────────────────────────
function Reports() {
  const [downloadModal, setDownloadModal] = useState(null);
  const [emailReport, setEmailReport] = useState({ sending: false, sent: false, addr: '' });
  const toast = useToast();
  const { company } = useAuth();
  const mobile = window.innerWidth < 768;
  const [from, setFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [to, setTo]     = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [schedForm, setSchedForm] = useState({ report_type: 'budget', recipient_email: '', frequency: 'weekly', day_of_week: '1' });
  const [savingSched, setSavingSched] = useState(false);
  const [payrollData, setPayrollData] = useState(null);

  const presets = [
    { label: 'This Month', from: `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-01`, to: new Date().toISOString().split('T')[0] },
    { label: 'Last 30 Days', from: new Date(Date.now()-30*864e5).toISOString().split('T')[0], to: new Date().toISOString().split('T')[0] },
    { label: 'This Year', from: `${new Date().getFullYear()}-01-01`, to: new Date().toISOString().split('T')[0] },
  ];

  const REPORT_NAMES = { budget:'Budget vs Actual', labor:'Labor Cost', materials:'Materials Cost', pl:'P&L Summary', invoices:'Invoice & AR Report', payroll:'Payroll Report' };
  const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).catch(()=>{}).finally(()=>setLoading(false));
    api.get('/reports/schedules').then(r => setSchedules(r.data||[])).catch(()=>{});
  }, []);

  function buildReportData(type) {
    if (!data) return { cols:[], rows:[], totals:null, currencyCols:[] };
    const { jobs, kpis } = data;
    switch(type) {
      case 'budget': {
        const rows = jobs.map(j=>[j.name, j.customerName, j.status.replace('_',' '), j.budgetTotal||0, j.actualTotal||0, Math.abs(j.variance||0), `${j.pctUsed||0}%`]);
        const totals = ['TOTALS','','', jobs.reduce((s,j)=>s+(j.budgetTotal||0),0), jobs.reduce((s,j)=>s+(j.actualTotal||0),0), jobs.reduce((s,j)=>s+Math.abs(j.variance||0),0), ''];
        return { cols:['Job Name','Customer','Status','Budget','Actual Cost','Remaining','% Used'], rows, totals, currencyCols:[3,4,5] };
      }
      case 'labor': {
        const rows = jobs.map(j=>[j.name, j.customerName, j.status.replace('_',' '), j.actualLabor||0, j.budgetLabor||0, (j.budgetLabor||0)-(j.actualLabor||0)]);
        const totals = ['TOTALS','','', jobs.reduce((s,j)=>s+(j.actualLabor||0),0), jobs.reduce((s,j)=>s+(j.budgetLabor||0),0), jobs.reduce((s,j)=>s+(j.budgetLabor||0)-(j.actualLabor||0),0)];
        return { cols:['Job Name','Customer','Status','Actual Labor','Budget Labor','Variance'], rows, totals, currencyCols:[3,4,5] };
      }
      case 'materials': {
        const rows = jobs.map(j=>[j.name, j.customerName, j.status.replace('_',' '), j.actualMaterials||0, j.budgetMaterials||0, (j.budgetMaterials||0)-(j.actualMaterials||0)]);
        const totals = ['TOTALS','','', jobs.reduce((s,j)=>s+(j.actualMaterials||0),0), jobs.reduce((s,j)=>s+(j.budgetMaterials||0),0), jobs.reduce((s,j)=>s+(j.budgetMaterials||0)-(j.actualMaterials||0),0)];
        return { cols:['Job Name','Customer','Status','Actual Materials','Budget Materials','Variance'], rows, totals, currencyCols:[3,4,5] };
      }
      case 'pl': {
        const profit = (kpis.totalRevenue||0)-(kpis.totalLabor||0)-(kpis.totalMaterials||0);
        return {
          cols:['Metric','Amount'],
          rows:[['Total Revenue (Invoiced)', kpis.totalRevenue||0],['Total Collected',(kpis.totalRevenue||0)-(kpis.outstandingAR||0)],['Outstanding AR',kpis.outstandingAR||0],['',''],['Total Labor Cost',kpis.totalLabor||0],['Total Materials Cost',kpis.totalMaterials||0],['Total Direct Costs',(kpis.totalLabor||0)+(kpis.totalMaterials||0)],['',''],['Gross Profit',profit],['Profit Margin',`${kpis.profitMargin||0}%`],['Active Jobs',String(kpis.activeJobs||0)]],
          totals:null, currencyCols:[1],
        };
      }
      case 'invoices': {
        const j2 = jobs.filter(j=>j.invoicedAmount>0);
        const rows = j2.map(j=>[j.name,j.customerName,j.invoicedAmount||0,j.paidAmount||0,(j.invoicedAmount||0)-(j.paidAmount||0),j.profit||0,`${j.profitMargin??0}%`]);
        const totals = ['TOTALS','',j2.reduce((s,j)=>s+(j.invoicedAmount||0),0),j2.reduce((s,j)=>s+(j.paidAmount||0),0),j2.reduce((s,j)=>s+(j.invoicedAmount||0)-(j.paidAmount||0),0),j2.reduce((s,j)=>s+(j.profit||0),0),''];
        return { cols:['Job Name','Customer','Revenue','Collected','Outstanding','Profit','Margin'], rows, totals, currencyCols:[2,3,4,5] };
      }
      default: return { cols:[], rows:[], totals:null, currencyCols:[] };
    }
  }

  // Payroll cross-tab — fetched live from API
  async function fetchAndBuildPayroll() {
    const pd = await api.get(`/reports/payroll?from=${from}&to=${to}`).then(r => r.data);
    setPayrollData(pd);
    return buildPayrollTable(pd);
  }

  function buildPayrollTable(pd) {
    if (!pd || !pd.employees?.length) return { cols:['Employee','Role'], rows:[], totals:null, currencyCols:[], isPayroll:true, pd };
    const jobs = pd.jobs;
    const emps = pd.employees;

    // Columns: Employee | Role | [per job: Hrs | GP] x N | Total Hrs | Total GP | Est. Taxes & Fees | Est. Loaded Cost
    const jobCols = jobs.flatMap(j => [`${j.jobName.slice(0,16)} Hrs`, `${j.jobName.slice(0,16)} GP`]);
    const cols = ['Employee', 'Role', ...jobCols, 'Total Hrs', 'Total GP', 'Est. Taxes & Fees', 'Est. Loaded Cost'];

    // Currency cols: GP cols (odd indices in the job pairs) + Total GP + Est Taxes + Est Loaded
    const gpColIndices = jobs.map((_, i) => 2 + i*2 + 1);  // 3, 5, 7...
    const totalGPCol   = cols.length - 3;
    const taxCol       = cols.length - 2;
    const loadedCol    = cols.length - 1;
    const currencyCols = [...gpColIndices, totalGPCol, taxCol, loadedCol];

    const FICA_RATE = 0.0765; // employer FICA est
    const SUI_RATE  = 0.027;  // avg state unemployment est
    const TAX_RATE  = FICA_RATE + SUI_RATE; // ~10.4% total employer tax estimate

    const rows = emps.map(e => {
      const jobCells = jobs.flatMap(j => {
        const entry = j.employees?.[e.id];
        const hrs = entry?.hours   || 0;
        const gp  = entry?.grossPay || 0;
        return [hrs > 0 ? hrs : '', gp > 0 ? gp : ''];
      });
      const estTaxes  = e.totalGrossPay * TAX_RATE;
      const estLoaded = e.totalGrossPay + estTaxes;
      return [e.name, e.role||'—', ...jobCells, e.totalHours, e.totalGrossPay, estTaxes, estLoaded];
    });

    // Totals row
    const grandHrs      = emps.reduce((s,e) => s + e.totalHours, 0);
    const grandGP       = emps.reduce((s,e) => s + e.totalGrossPay, 0);
    const grandTaxes    = grandGP * TAX_RATE;
    const grandLoaded   = grandGP + grandTaxes;
    const jobTotals     = jobs.flatMap(j => {
      const hrs = emps.reduce((s,e) => s + (j.employees?.[e.id]?.hours||0), 0);
      const gp  = emps.reduce((s,e) => s + (j.employees?.[e.id]?.grossPay||0), 0);
      return [hrs > 0 ? hrs : '', gp > 0 ? gp : ''];
    });
    const totals = ['TOTALS', '', ...jobTotals, grandHrs, grandGP, grandTaxes, grandLoaded];

    return { cols, rows, totals, currencyCols, isPayroll:true, pd, taxRate: TAX_RATE };
  }

  const fmtC = (v, isCurrency) => {
    if (typeof v === 'number' && isCurrency) return '$'+v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,',');
    if (typeof v === 'number') return v.toFixed(2);
    return String(v??'');
  };
  const fmtRows = (rows, cc) => rows.map(row => row.map((cell,i) => fmtC(cell, cc?.includes(i))));

  const loadPDFLibs = () => {
    const loadScript = (id, src) => new Promise(r => {
      if (document.getElementById(id)) return r();
      const s = document.createElement('script'); s.id=id; s.src=src; s.onload=r; document.head.appendChild(s);
    });
    return loadScript('jspdf','https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')
      .then(()=>loadScript('jspdf-at','https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js'));
  };

  async function downloadPDF(type, title) {
    setGenerating(type+'_pdf');
    const reportData = type==='payroll'
      ? (payrollData ? buildPayrollTable(payrollData) : await fetchAndBuildPayroll())
      : buildReportData(type);
    const { cols, rows, totals, currencyCols } = reportData;
    const fr = fmtRows(rows, currencyCols);
    const ft = totals ? fmtRows([totals], currencyCols)[0] : null;
    const companyName = company?.name||'JobHub';
    const today = new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'});
    try {
      await loadPDFLibs();
      const { jsPDF } = window.jspdf;
      const landscape = cols.length > 5;
      const doc = new jsPDF({ orientation: landscape?'landscape':'portrait', unit:'mm' });
      const pageW = landscape ? 297 : 210;
      const mg = 14;
      // Header bar
      doc.setFillColor(26,37,53); doc.rect(0,0,pageW,28,'F');
      doc.setFillColor(74,158,221); doc.roundedRect(mg,6,16,16,3,3,'F');
      doc.setTextColor(255,255,255); doc.setFontSize(11); doc.setFont(undefined,'bold');
      doc.text('JH', mg+8, 16.5, {align:'center'});
      doc.setFontSize(13); doc.text(companyName, mg+20, 12);
      doc.setFontSize(10); doc.setFont(undefined,'normal'); doc.setTextColor(190,210,230);
      doc.text(title, mg+20, 19);
      doc.text(`Period: ${from} → ${to}`, pageW-mg, 12, {align:'right'});
      doc.text(`Generated: ${today}`, pageW-mg, 19, {align:'right'});
      // P&L summary boxes
      let startY = 36;
      if (type==='pl' && data?.kpis) {
        const kpis = data.kpis;
        const profit = (kpis.totalRevenue||0)-(kpis.totalLabor||0)-(kpis.totalMaterials||0);
        const items = [{label:'Total Revenue',val:'$'+(kpis.totalRevenue||0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,','),color:[26,37,53]},{label:'Gross Profit',val:'$'+profit.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,','),color:profit>=0?[16,185,129]:[239,68,68]},{label:'Margin',val:`${kpis.profitMargin||0}%`,color:(kpis.profitMargin||0)>=20?[16,185,129]:[239,68,68]}];
        const bw=(pageW-mg*2-8)/3;
        items.forEach((item,i)=>{ const x=mg+i*(bw+4); doc.setFillColor(249,250,251); doc.rect(x,32,bw,20,'F'); doc.setDrawColor(229,231,235); doc.rect(x,32,bw,20,'S'); doc.setFontSize(8); doc.setTextColor(107,114,128); doc.setFont(undefined,'normal'); doc.text(item.label.toUpperCase(),x+bw/2,38,{align:'center'}); doc.setFontSize(12); doc.setFont(undefined,'bold'); doc.setTextColor(...item.color); doc.text(item.val,x+bw/2,47,{align:'center'}); });
        startY=58;
      }
      // Payroll disclaimer box
      if (type==='payroll') {
        doc.setFillColor(255,247,230);
        doc.rect(mg, 32, pageW-mg*2, 14, 'F');
        doc.setDrawColor(251,191,36);
        doc.rect(mg, 32, pageW-mg*2, 14, 'S');
        doc.setFontSize(8); doc.setTextColor(146,64,14); doc.setFont(undefined,'normal');
        doc.text('⚠  Approved hours only. Taxes & fees are estimated at ~10.4% (7.65% FICA + 2.75% avg SUI). Verify actuals with your payroll provider before remitting.', mg+4, 40);
        startY = 52;
      }
      // Table
      doc.autoTable({ head:[cols], body:fr, foot:ft?[ft]:[], startY, margin:{left:mg,right:mg},
        styles:{fontSize:8.5,cellPadding:3,lineColor:[229,231,235],lineWidth:0.3},
        headStyles:{fillColor:[26,37,53],textColor:255,fontStyle:'bold',fontSize:9},
        footStyles:{fillColor:[243,244,246],textColor:[26,37,53],fontStyle:'bold'},
        alternateRowStyles:{fillColor:[249,250,251]},
        columnStyles:currencyCols?.reduce((acc,i)=>({...acc,[i]:{halign:'right'}}),{}),
      });
      // Page footer
      const pc=doc.internal.getNumberOfPages();
      for(let i=1;i<=pc;i++){ doc.setPage(i); doc.setFontSize(8); doc.setTextColor(156,163,175); doc.setFont(undefined,'normal'); doc.text(`${companyName} — Confidential`,mg,doc.internal.pageSize.height-8); doc.text(`Page ${i} of ${pc}`,pageW-mg,doc.internal.pageSize.height-8,{align:'right'}); }
            const pdfFilename = `${title.replace(/\s+/g,'-')}-${from}-${to}.pdf`;
                  // Use blob + anchor to work around iOS Safari async user gesture restriction
                  const isMobilePdf = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                  if (isMobilePdf) {
                    setDownloadModal({ uri: doc.output('datauristring'), filename: pdfFilename, type: 'pdf', title, period: `${from} → ${to}` });
                  } else {
                    const pdfBlob = doc.output('blob');
                    const pdfUrl = URL.createObjectURL(pdfBlob);
                    const pdfLink = document.createElement('a');
                    pdfLink.href = pdfUrl; pdfLink.download = pdfFilename;
                    document.body.appendChild(pdfLink); pdfLink.click(); document.body.removeChild(pdfLink);
                    setTimeout(() => URL.revokeObjectURL(pdfUrl), 3000);
                  }
            toast.success(`${title} downloaded as PDF`);
    } catch(e){ console.error(e); toast.error('PDF generation failed'); }
    finally{ setGenerating(''); }
  }

  async function downloadExcel(type, title) {
    setGenerating(type+'_xls');
    const reportData = type==='payroll'
      ? (payrollData ? buildPayrollTable(payrollData) : await fetchAndBuildPayroll())
      : buildReportData(type);
    const { cols, rows, totals, currencyCols } = reportData;
    const fr = fmtRows(rows, currencyCols);
    const ft = totals ? fmtRows([totals], currencyCols)[0] : null;
    const companyName = company?.name||'JobHub';
    const loadXLSX = ()=>new Promise(r=>{ if(window.XLSX)return r(window.XLSX); const s=document.createElement('script'); s.src='https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js'; s.onload=()=>r(window.XLSX); document.head.appendChild(s); });
    try {
      const XLSX = await loadXLSX();
      const sheetData=[[companyName],[title],[`Period: ${from} → ${to}`,``,`Generated: ${new Date().toLocaleDateString()}`],[],cols,...fr];
      if(ft) sheetData.push([],ft);
      const ws=XLSX.utils.aoa_to_sheet(sheetData);
      ws['!merges']=[{s:{r:0,c:0},e:{r:0,c:cols.length-1}},{s:{r:1,c:0},e:{r:1,c:cols.length-1}}];
      ws['!cols']=cols.map((_,i)=>({wch:i===0?32:currencyCols?.includes(i)?16:18}));
      const wb=XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb,ws,title.slice(0,31));
            const xlsFilename = `${title.replace(/\s+/g,'-')}-${from}-${to}.xlsx`;
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                        if (isMobile) {
                                  // Mobile: generate CSV — plain text, no libraries, works perfectly on iOS
                                  const csvFilename = xlsFilename.replace('.xlsx', '.csv');
                                  const escCSV = v => { const s = v == null ? '' : String(v); return s.includes(',') || s.includes('"') || s.includes('\n') ? '"' + s.replace(/"/g,'""') + '"' : s; };
                                  const allRows = [cols, ...fr, ...(ft ? [[]] : []), ...(ft ? [ft] : [])];
                                  const csvContent = allRows.map(row => row.map(escCSV).join(',')).join('\n');
                                  const csvUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent);
                                  setDownloadModal({ uri: csvUri, filename: csvFilename, type: 'csv', csvContent, title, period: `${from} → ${to}` });
                        } else {
                                  const wbout2 = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
                                  const blob2 = new Blob([wbout2], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                                  const url = URL.createObjectURL(blob2);
                                  const a = document.createElement('a');
                                  a.href = url; a.download = xlsFilename;
                                  document.body.appendChild(a); a.click(); document.body.removeChild(a);
                                  setTimeout(() => URL.revokeObjectURL(url), 3000);
                        }
            toast.success(`${title} downloaded as Excel`);
    } catch(e){ toast.error('Excel generation failed'); }
    finally{ setGenerating(''); }
  }

  async function saveSchedule() {
    if(!schedForm.recipient_email){ toast.error('Email is required'); return; }
    setSavingSched(true);
    try {
      const r = await api.post('/reports/schedules', {...schedForm, day_of_week:parseInt(schedForm.day_of_week)});
      setSchedules(prev=>[...prev, r.data]);
      setShowScheduleForm(false);
      setSchedForm({report_type:'budget',recipient_email:'',frequency:'weekly',day_of_week:'1'});
      toast.success('Schedule saved!');
    } catch(e){ toast.error(getErrorMessage(e)); }
    finally{ setSavingSched(false); }
  }

  async function toggleSchedule(s) {
    try { const r=await api.patch(`/reports/schedules/${s.id}`,{is_active:!s.is_active}); setSchedules(prev=>prev.map(x=>x.id===s.id?r.data:x)); }
    catch(e){ toast.error(getErrorMessage(e)); }
  }

  async function deleteSchedule(id) {
    try { await api.delete(`/reports/schedules/${id}`); setSchedules(prev=>prev.filter(x=>x.id!==id)); toast.success('Schedule removed'); }
    catch(e){ toast.error(getErrorMessage(e)); }
  }

  const REPORTS = [
    {key:'budget',    name:'Budget vs Actual',   desc:'Estimated vs real costs per job'},
    {key:'labor',     name:'Labor Cost',          desc:'Labor costs vs budget per job'},
    {key:'materials', name:'Materials Cost',      desc:'Material costs vs budget per job'},
    {key:'pl',        name:'P&L Summary',         desc:'Revenue, costs, and profit overview'},
    {key:'invoices',  name:'Invoice & AR Report', desc:'Revenue, collected, and outstanding per job'},
    {key:'payroll',   name:'Payroll Report',       desc:'Approved hours by job and employee — rows=jobs, columns=employees'},
  ];

  return (
    <div>
      <div className="card" style={{padding:18,marginBottom:16}}>
        <div className="sh">Date Range</div>
        <div className="ss">All reports use this date range when exported</div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
          {presets.map(p=>(
            <button key={p.label} onClick={()=>{setFrom(p.from);setTo(p.to);}} style={{padding:'6px 13px',borderRadius:20,fontSize:12,fontWeight:700,cursor:'pointer',background:from===p.from&&to===p.to?'var(--navy)':'var(--surface)',color:from===p.from&&to===p.to?'#fff':'var(--sub)',border:`1px solid ${from===p.from&&to===p.to?'var(--navy)':'var(--border)'}`}}>{p.label}</button>
          ))}
        </div>
        <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
          <div style={{flex:1,minWidth:130}}><label className="fl">From</label><input className="field" type="date" value={from} onChange={e=>setFrom(e.target.value)}/></div>
          <div style={{fontSize:16,color:'var(--sub)',paddingTop:20,flexShrink:0}}>→</div>
          <div style={{flex:1,minWidth:130}}><label className="fl">To</label><input className="field" type="date" value={to} onChange={e=>setTo(e.target.value)}/></div>
        </div>
      </div>

      <div data-tour="reports-cards" style={{display:'grid',gridTemplateColumns:mobile?'1fr':'repeat(auto-fill,minmax(240px,1fr))',gap:12,marginBottom:16}}>
        {REPORTS.map(r=>{
          const isPdf=generating===r.key+'_pdf', isXls=generating===r.key+'_xls';
          return (
            <div key={r.key} className="card" style={{padding:18}}>
             <div style={{marginBottom:10,color:'var(--accent)'}}>
              {r.key==='budget'    && <BarChart3 size={28}/>}
              {r.key==='labor'     && <HardHat size={28}/>}
              {r.key==='materials' && <Layers size={28}/>}
              {r.key==='pl'        && <DollarSign size={28}/>}
              {r.key==='invoices'  && <FileText size={28}/>}
              {r.key==='payroll'   && <Users size={28}/>}
            </div>
              <div style={{fontWeight:700,color:'var(--navy)',fontSize:14,marginBottom:4}}>{r.name}</div>
              <div style={{fontSize:12,color:'var(--sub)',marginBottom:16,lineHeight:1.5}}>{r.desc}</div>
              <div style={{display:'flex',gap:6}}>
                <button onClick={async()=>{
                  if(r.key==='payroll'){const d=await fetchAndBuildPayroll();setPreview({...d,title:r.name,key:r.key});}
                  else{const d=buildReportData(r.key);setPreview({...d,title:r.name,key:r.key});}
                }} style={{flex:1,background:'var(--surface)',color:'var(--navy)',border:'1px solid var(--border)',borderRadius:8,padding:'8px 0',fontSize:11,fontWeight:700,cursor:'pointer'}}>👁 Preview</button>
                {!mobile&&<button onClick={()=>downloadPDF(r.key,r.name)} disabled={!!generating||loading} style={{flex:1,background:'var(--navy)',color:'#fff',border:'none',borderRadius:8,padding:'8px 0',fontSize:11,fontWeight:700,cursor:'pointer',opacity:isPdf?.7:1}}>{isPdf?'...':'⬇ PDF'}</button>}
                {!mobile&&<button onClick={()=>downloadExcel(r.key,r.name)} disabled={!!generating||loading} style={{flex:1,background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,padding:'8px 0',fontSize:11,fontWeight:700,cursor:'pointer',opacity:isXls?.7:1}}>{isXls?'...':'⬇ Excel'}</button>}
                {mobile&&<button onClick={()=>downloadExcel(r.key,r.name)} disabled={!!generating||loading} style={{flex:2,background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,padding:'8px 0',fontSize:11,fontWeight:700,cursor:'pointer'}}>{isXls?'...':'📧 Email Report'}</button>}              </div>
            </div>
          );
        })}
      </div>

      <div className="card" style={{padding:18}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10,marginBottom:14}}>
          <div><div className="sh">Auto Send</div><div className="ss">Schedule reports to be emailed automatically</div></div>
          <button onClick={()=>setShowScheduleForm(!showScheduleForm)} style={{background:showScheduleForm?'var(--surface)':'var(--accent)',color:showScheduleForm?'var(--sub)':'#fff',border:showScheduleForm?'1px solid var(--border)':'none',borderRadius:8,padding:'8px 14px',fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
            {showScheduleForm?'Cancel':'+ Add Schedule'}
          </button>
        </div>

        {showScheduleForm&&(
          <div style={{background:'var(--surface)',borderRadius:10,padding:16,marginBottom:16}}>
            <div style={{display:'grid',gridTemplateColumns:mobile?'1fr':'repeat(2,1fr)',gap:12,marginBottom:12}}>
              <div><label className="fl">Report</label>
                <select className="field" value={schedForm.report_type} onChange={e=>setSchedForm(p=>({...p,report_type:e.target.value}))}>
                  {REPORTS.map(r=><option key={r.key} value={r.key}>{r.name}</option>)}
                </select>
              </div>
              <div><label className="fl">Send to</label>
                <input className="field" type="email" placeholder="bookkeeper@example.com" value={schedForm.recipient_email} onChange={e=>setSchedForm(p=>({...p,recipient_email:e.target.value}))}/>
              </div>
              <div><label className="fl">Frequency</label>
                <select className="field" value={schedForm.frequency} onChange={e=>setSchedForm(p=>({...p,frequency:e.target.value}))}>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div><label className="fl">{schedForm.frequency==='monthly'?'Day of Month':'Day of Week'}</label>
                {schedForm.frequency==='weekly'
                  ? <select className="field" value={schedForm.day_of_week} onChange={e=>setSchedForm(p=>({...p,day_of_week:e.target.value}))}>{DAYS.map((d,i)=><option key={i} value={String(i)}>{d}</option>)}</select>
                  : <select className="field" value={schedForm.day_of_week} onChange={e=>setSchedForm(p=>({...p,day_of_week:e.target.value}))}>{Array.from({length:28},(_,i)=><option key={i+1} value={String(i+1)}>Day {i+1}</option>)}</select>
                }
              </div>
            </div>
            <button onClick={saveSchedule} disabled={savingSched} style={{padding:'10px 24px',background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13}}>
              {savingSched?'Saving...':'Save Schedule'}
            </button>
          </div>
        )}

        {schedules.length===0&&!showScheduleForm
          ? <div style={{textAlign:'center',padding:'20px 0',color:'var(--sub)',fontSize:13}}>No schedules yet — add one above</div>
          : schedules.map((s,i)=>(
            <div key={s.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 0',borderBottom:i<schedules.length-1?'1px solid var(--divider)':'none',gap:10,flexWrap:'wrap'}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{color:'var(--navy)',fontWeight:700,fontSize:13}}>{REPORT_NAMES[s.report_type]||s.report_type}</div>
                <div style={{color:'var(--sub)',fontSize:12,marginTop:2}}>
                  {s.frequency==='weekly'?`Weekly · ${DAYS[s.day_of_week]||'Monday'}`:`Monthly · Day ${s.day_of_week}`}
                  {' → '}<span style={{color:'var(--accent)'}}>{s.recipient_email}</span>
                </div>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
                <div onClick={()=>toggleSchedule(s)} style={{width:38,height:22,borderRadius:11,background:s.is_active?'var(--accent)':'var(--border)',position:'relative',cursor:'pointer',transition:'background .2s'}}>
                  <div style={{width:18,height:18,borderRadius:9,background:'#fff',position:'absolute',top:2,left:s.is_active?18:2,transition:'left .2s',boxShadow:'0 1px 3px rgba(0,0,0,.2)'}}/>
                </div>
                <button onClick={()=>deleteSchedule(s.id)} style={{background:'none',border:'none',color:'#9ca3af',cursor:'pointer',fontSize:18,padding:'2px 4px',lineHeight:1}}>×</button>
              </div>
            </div>
          ))
        }
      </div>

      {preview&&(
        <div onClick={()=>setPreview(null)} style={{position:'fixed',inset:0,background:'rgba(26,37,53,.6)',backdropFilter:'blur(3px)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:900,maxHeight:'95vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(26,37,53,.3)',animation:'popIn .25s ease'}}>
            <div style={{padding:'16px 22px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0,flexWrap:'wrap',gap:10}}>
              <div>
                <div style={{fontWeight:800,color:'var(--navy)',fontSize:15}}>{preview.title}</div>
                <div style={{fontSize:12,color:'var(--sub)',marginTop:2}}>{company?.name} · {from} → {to}</div>
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <button onClick={()=>downloadPDF(preview.key,preview.title)} disabled={!!generating} style={{padding:'7px 14px',background:'var(--navy)',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>⬇ PDF</button>
                <button onClick={()=>downloadExcel(preview.key,preview.title)} disabled={!!generating} style={{padding:'7px 14px',background:'#16a34a',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>⬇ Excel</button>
                <button onClick={()=>setPreview(null)} style={{background:'none',border:'none',color:'var(--sub)',cursor:'pointer',fontSize:22,lineHeight:1,padding:'4px 8px'}}>×</button>
              </div>
            </div>
            <div style={{overflowY:'auto',padding:'16px 22px'}}>
              {preview.isPayroll && (
                <div style={{background:'#fffbeb',border:'1px solid #fcd34d',borderRadius:8,padding:'8px 14px',fontSize:12,color:'#92400e',marginBottom:12}}>
                  ⚠️ Approved hours only · Taxes & fees estimated at ~10.4% (7.65% FICA + 2.75% avg SUI) · Verify actuals with your payroll provider before remitting
                </div>
              )}
              {preview.rows.length===0
                ? <div style={{textAlign:'center',padding:'40px 0'}}>
                    <div style={{fontSize:36,marginBottom:10}}>📊</div>
                    <div style={{fontWeight:700,color:'var(--navy)',marginBottom:6}}>No data for this period</div>
                    <div style={{fontSize:13,color:'var(--sub)'}}>Connect QuickBooks and sync your data to generate reports</div>
                  </div>
                : <div className="tbl-wrap">
                    <table>
                      <thead><tr>{preview.cols.map(c=><th key={c}>{c}</th>)}</tr></thead>
                      <tbody>
                        {fmtRows(preview.rows,preview.currencyCols).map((row,i)=>(
                          <tr key={i}>{row.map((cell,j)=>(
                            <td key={j} style={{fontWeight:j===0?600:400,color:j===0?'var(--navy)':'inherit',textAlign:preview.currencyCols?.includes(j)?'right':'left'}}>{cell}</td>
                          ))}</tr>
                        ))}
                        {preview.totals&&(
                          <tr style={{background:'var(--surface)'}}>
                            {fmtRows([preview.totals],preview.currencyCols)[0].map((cell,j)=>(
                              <td key={j} style={{fontWeight:700,color:'var(--navy)',borderTop:'2px solid var(--border)',textAlign:preview.currencyCols?.includes(j)?'right':'left'}}>{cell}</td>
                            ))}
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
              }
            </div>
          </div>
        </div>
      )}
      {/* WIP Report card */}
      <WIPReport />

      {/* Excel download modal — replaces window.confirm on mobile */}
      {downloadModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999,padding:20}} onClick={e=>{if(e.target===e.currentTarget){setDownloadModal(null);setEmailReport({sending:false,sent:false,addr:''});}}}>
          <div style={{background:'#fff',borderRadius:20,padding:28,width:'100%',maxWidth:360,textAlign:'center'}}>
            <div style={{fontSize:44,marginBottom:8}}>{downloadModal.type==='pdf'?'📄':'📊'}</div>
            <div style={{fontWeight:800,fontSize:17,color:'#111827',marginBottom:4}}>Your report is ready</div>
            <div style={{fontSize:13,color:'#6b7280',marginBottom:20}}>{downloadModal.filename}</div>

            {!mobile&&<a href={downloadModal.uri} download={downloadModal.filename} onClick={()=>setTimeout(()=>{setDownloadModal(null);setEmailReport({sending:false,sent:false,addr:''}); },1000)}
              style={{display:'block',background:'#2563eb',color:'#fff',borderRadius:12,padding:'14px',fontWeight:700,fontSize:15,textDecoration:'none',marginBottom:10}}>
              ⬇ Download {downloadModal.type==='csv'?'CSV':'PDF'}
            </a>}
            {!mobile&&<div style={{display:'flex',alignItems:'center',gap:8,margin:'4px 0 10px'}}>
              <div style={{flex:1,height:1,background:'#e5e7eb'}}/>
              <span style={{fontSize:12,color:'#9ca3af'}}>or</span>
              <div style={{flex:1,height:1,background:'#e5e7eb'}}/>
            </div>}

            {/* Email option */}
            {emailReport.sent ? (
              <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:12,padding:'14px',color:'#16a34a',fontWeight:700,fontSize:14,marginBottom:10}}>
                ✅ Sent! Check your inbox.
              </div>
            ) : (
              <div>
                <div style={{fontSize:13,color:'#374151',fontWeight:600,marginBottom:8,textAlign:'left'}}>📧 Email as CSV to:</div>
                <input
                  value={emailReport.addr || (downloadModal && !emailReport.addr && company?.email ? (() => { if(!emailReport.addr) setTimeout(()=>setEmailReport(p=>p.addr?p:{...p,addr:company?.email||''}),0); return emailReport.addr; })() : emailReport.addr)}
                  onChange={e=>setEmailReport(p=>({...p,addr:e.target.value}))}
                  placeholder={company?.email||'your@email.com'}
                  type="email"
                  style={{width:'100%',border:'1px solid #d1d5db',borderRadius:8,padding:'10px 12px',fontSize:14,boxSizing:'border-box',marginBottom:8}}
                />
                <button
                  disabled={emailReport.sending || !emailReport.addr.trim()}
                  onClick={async()=>{
                    if(!emailReport.addr.trim()) return;
                    setEmailReport(p=>({...p,sending:true}));
                    try {
                      // Build CSV from the stored data
                      const res = await api.post('/reports/email-to-self',{
                        email: emailReport.addr.trim(),
                        filename: downloadModal.filename.replace('.pdf','.csv'),
                        csvContent: downloadModal.csvContent || '',
                        reportTitle: downloadModal.title || 'Report',
                        period: downloadModal.period || '',
                      });
                      setEmailReport(p=>({...p,sending:false,sent:true}));
                    } catch(err) {
                      alert('Failed to send: '+(err.message||'Unknown error'));
                      setEmailReport(p=>({...p,sending:false}));
                    }
                  }}
                  style={{width:'100%',background:emailReport.sending?'#93c5fd':'#0ea5e9',color:'#fff',border:'none',borderRadius:10,padding:'13px',fontWeight:700,fontSize:14,cursor:emailReport.sending||!emailReport.addr.trim()?'not-allowed':'pointer'}}
                >
                  {emailReport.sending?'Sending...':'📧 Send CSV to Email'}
                </button>
              </div>
            )}

            <button onClick={()=>{setDownloadModal(null);setEmailReport({sending:false,sent:false,addr:''}); }} style={{background:'none',border:'none',color:'#9ca3af',fontSize:13,cursor:'pointer',padding:'8px',marginTop:6}}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ENTRY REVIEW MODAL ───────────────────────────────────────────────────────
function EntryReviewModal({ entry, jobs, saving, mobile, onApprove, onReject, onUnapprove, onClose }) {
  const [f, setF] = useState({
    hours_regular:  parseFloat(entry.hours_regular)  || 0,
    hours_overtime: parseFloat(entry.hours_overtime) || 0,
    job_id: entry.job_id || '',
    notes:  entry.notes  || '',
  });
  const isApproved = entry.status === 'approved';
  const gross  = f.hours_regular * parseFloat(entry.hourly_rate||0) + f.hours_overtime * parseFloat(entry.overtime_rate||0);
  const loaded = gross * (1 + parseFloat(entry.burden_rate||0.26));

  return (
    <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(26,37,53,.55)',backdropFilter:'blur(3px)',zIndex:3000,display:'flex',alignItems:mobile?'flex-end':'center',justifyContent:'center',padding:mobile?0:24}}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:mobile?'20px 20px 0 0':16,width:'100%',maxWidth:460,padding:mobile?'24px 20px 36px':24,boxShadow:'0 20px 60px rgba(26,37,53,.25)',animation:mobile?'slideUp .3s ease':'popIn .25s ease',maxHeight:mobile?'85dvh':'90dvh',overflowY:'auto',WebkitOverflowScrolling:'touch'}}>
        {mobile && <div style={{width:36,height:4,background:'var(--border)',borderRadius:2,margin:'0 auto 18px'}}/>}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:2}}>
          <div style={{fontWeight:800,color:'var(--navy)',fontSize:15}}>{isApproved ? 'Edit Approved Entry' : 'Review Entry'}</div>
          {isApproved && <span className="badge" style={{background:'#d1fae5',color:'#065f46'}}>Approved</span>}
        </div>
        <div style={{fontSize:13,color:'var(--sub)',marginBottom:18}}>{entry.employee_name} · {new Date(entry.work_date).toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'})}</div>

        {isApproved && (
          <div style={{background:'#fef3c7',border:'1px solid #fcd34d',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#92400e',marginBottom:14}}>
            ⚠️ This entry is already approved and has been added to job costing. Editing will re-calculate costs.
          </div>
        )}

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
          <div>
            <label className="fl">Regular Hours</label>
            <input className="field" type="number" step="0.25" value={f.hours_regular} onChange={e=>setF(p=>({...p,hours_regular:parseFloat(e.target.value)||0}))}/>
          </div>
          <div>
            <label className="fl">Overtime Hours</label>
            <input className="field" type="number" step="0.25" value={f.hours_overtime} onChange={e=>setF(p=>({...p,hours_overtime:parseFloat(e.target.value)||0}))}/>
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <label className="fl">Job</label>
          <select className="field" value={f.job_id} onChange={e=>setF(p=>({...p,job_id:e.target.value}))}>
            <option value="">No job assigned</option>
            {jobs.map(j=><option key={j.id} value={j.id}>{j.name}</option>)}
          </select>
        </div>
        <div style={{marginBottom:14}}>
          <label className="fl">Notes</label>
          <textarea className="field" rows={2} value={f.notes} onChange={e=>setF(p=>({...p,notes:e.target.value}))}/>
        </div>
        <div style={{background:'var(--surface)',borderRadius:10,padding:'10px 14px',marginBottom:16,display:'flex',gap:20}}>
          <div><div style={{fontSize:10,color:'var(--sub)',fontWeight:700,textTransform:'uppercase'}}>Gross Pay</div><div style={{fontWeight:800,color:'var(--navy)',fontSize:15}}>${gross.toFixed(2)}</div></div>
          <div><div style={{fontSize:10,color:'var(--sub)',fontWeight:700,textTransform:'uppercase'}}>Loaded Cost</div><div style={{fontWeight:800,color:'var(--navy)',fontSize:15}}>${loaded.toFixed(2)}</div></div>
          <div><div style={{fontSize:10,color:'var(--sub)',fontWeight:700,textTransform:'uppercase'}}>Burden</div><div style={{fontWeight:700,color:'var(--sub)',fontSize:13}}>{Math.round(parseFloat(entry.burden_rate||0.26)*100)}%</div></div>
        </div>

        {isApproved ? (
          <div style={{display:'flex',gap:8}}>
            <button onClick={()=>onApprove(entry,f)} disabled={saving} style={{flex:2,padding:11,background:'var(--navy)',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13}}>{saving?'Saving...':'💾 Save Changes'}</button>
            <button onClick={()=>onUnapprove(entry)} disabled={saving} style={{flex:1,padding:11,background:'#fef3c7',color:'#92400e',border:'1px solid #fcd34d',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:12}}>↩ Unapprove</button>
            <button onClick={onClose} style={{flex:1,padding:11,background:'var(--surface)',color:'var(--sub)',border:'1px solid var(--border)',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13}}>Cancel</button>
          </div>
        ) : (
          <div style={{display:'flex',gap:8}}>
            <button onClick={()=>onApprove(entry,f)} disabled={saving} style={{flex:2,padding:11,background:'var(--green)',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13}}>{saving?'Saving...':'✓ Approve'}</button>
            <button onClick={()=>onReject(entry.id,entry.employee_name)} disabled={saving} style={{flex:1,padding:11,background:'#fee2e2',color:'var(--red)',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13}}>✕ Reject</button>
            <button onClick={onClose} style={{flex:1,padding:11,background:'var(--surface)',color:'var(--sub)',border:'1px solid var(--border)',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13}}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── TIME TRACKING ────────────────────────────────────────────────────────────
function TimeTracking() {
  const { company } = useAuth();
  const [tab, setTab] = useState('entries'); // 'entries' | 'employees'
  const [entries, setEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [editEntry, setEditEntry] = useState(null);
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [empForm, setEmpForm] = useState({});
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const mobile = window.innerWidth < 768;

  const loadEntries = () => api.get(`/time/entries?status=${statusFilter}`)
    .then(r => setEntries(r.data || [])).catch(() => {});
  const loadEmployees = () => api.get('/time/employees')
    .then(r => setEmployees(r.data || [])).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get(`/time/entries?status=${statusFilter}`),
      api.get('/time/employees'),
      api.get('/dashboard'),
    ]).then(([e, emp, d]) => {
      setEntries(e.data || []);
      setEmployees(emp.data || []);
      setJobs(d.data?.jobs || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadEntries(); }, [statusFilter]);

  // ── Approve entry ──────────────────────────────────────────────────────────
  async function approve(entry, edits = {}) {
    setSaving(true);
    try {
      await api.patch(`/time/entries/${entry.id}/approve`, {
        hours_regular: edits.hours_regular ?? entry.hours_regular,
        hours_overtime: edits.hours_overtime ?? entry.hours_overtime,
        job_id: edits.job_id ?? entry.job_id,
        notes: edits.notes ?? entry.notes,
      });
      toast.success(`${entry.employee_name} — approved`);
      setEditEntry(null);
      loadEntries();
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  async function reject(id, name) {
    try {
      await api.patch(`/time/entries/${id}/reject`, {});
      toast.success(`${name} — rejected`);
      setEditEntry(null);
      loadEntries();
    } catch(e) { toast.error(getErrorMessage(e)); }
  }

  async function unapprove(entry) {
    setSaving(true);
    try {
      await api.patch(`/time/entries/${entry.id}/unapprove`, {});
      toast.success(`${entry.employee_name} — moved back to pending`);
      setEditEntry(null);
      loadEntries();
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  // ── Save employee ──────────────────────────────────────────────────────────
  async function saveEmployee() {
    setSaving(true);
    try {
      if (editEmp) {
        await api.patch(`/time/employees/${editEmp.id}`, empForm);
        toast.success('Employee updated');
      } else {
       if (!empForm.name || !empForm.email) { toast.error('Name and email required'); setSaving(false); return; }
        await api.post('/time/employees', empForm);
        toast.success(`${empForm.name} added`);
      }
      setShowAddEmp(false); setEditEmp(null); setEmpForm({});
      loadEmployees();
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  const STATUS_COLORS = { pending: { bg:'#fef3c7',color:'#92400e' }, approved: { bg:'#d1fae5',color:'#065f46' }, rejected: { bg:'#fee2e2',color:'#991b1b' } };
  const { roles: ROLES } = useIndustry();

  if (loading) return <div className="card" style={{padding:20}}>{[1,2,3].map(i=><Skel key={i} h={50} mb={8}/>)}</div>;

  return (
    <div>
      {/* Tabs */}
      <div data-tour="time-tabs" style={{display:'flex',gap:8,marginBottom:16}}>
        {[['entries','Time Entries'],['employees','Employees']].map(([key,label])=>(
          <button key={key} onClick={()=>setTab(key)} style={{padding:'8px 18px',borderRadius:20,fontSize:13,fontWeight:700,cursor:'pointer',background:tab===key?'var(--navy)':'var(--surface)',color:tab===key?'#fff':'var(--sub)',border:`1px solid ${tab===key?'var(--navy)':'var(--border)'}`}}>{label}</button>
        ))}
      </div>

      {/* ── Time Entries tab ── */}
      {tab==='entries' && (
        <div>
          {/* Status filter */}
          <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
            <span style={{fontSize:11,color:'var(--sub)',fontWeight:600,alignSelf:'center',marginRight:2}}>Show:</span>
            {['pending','approved','rejected'].map(s=>(
              <button key={s} onClick={()=>setStatusFilter(s)} style={{padding:'4px 12px',borderRadius:20,fontSize:11,fontWeight:700,cursor:'pointer',background:statusFilter===s?STATUS_COLORS[s].bg:'var(--surface)',color:statusFilter===s?STATUS_COLORS[s].color:'var(--sub)',border:`1px solid ${statusFilter===s?STATUS_COLORS[s].bg:'var(--border)'}`}}>
                {s.charAt(0).toUpperCase()+s.slice(1)} {statusFilter===s&&`(${entries.length})`}
              </button>
            ))}
          </div>

          {entries.length===0 ? (
            <div className="card" style={{padding:40,textAlign:'center'}}>
              <div style={{fontSize:40,marginBottom:12}}>{statusFilter==='pending'?'⏳':''}</div>
              <div style={{fontWeight:700,color:'var(--navy)',marginBottom:6,fontSize:15}}>
                {statusFilter==='pending' ? 'No pending entries' : `No ${statusFilter} entries`}
              </div>
              <div style={{fontSize:13,color:'var(--sub)',marginBottom:18}}>
                {statusFilter==='pending'
                  ? 'Employees submit hours from app.jobhubco.com/employee — they\'ll appear here for review'
                  : 'No time entries match this filter for the current period'}
              </div>
              {statusFilter==='pending' && (
                <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
                  <button onClick={()=>setTab('employees')} style={{padding:'9px 18px',background:'var(--navy)',color:'#fff',border:'none',borderRadius:9,fontWeight:700,cursor:'pointer',fontSize:12}}>Add Employees →</button>
                </div>
              )}
            </div>
          ) : mobile ? (
            <div>
              {entries.map(e=>(
                <div key={e.id} className="mc" style={{marginBottom:10}}>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                    <div>
                      <div style={{fontWeight:700,color:'var(--navy)',fontSize:13}}>{e.employee_name}</div>
                      <div style={{fontSize:12,color:'var(--sub)',marginTop:2}}>{e.role||'Employee'} · {new Date(e.work_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>
                      {e.job_name && <div style={{fontSize:12,color:'var(--accent)',marginTop:2}}>📍 {e.job_name}</div>}
                    </div>
                    <span className="badge" style={{...STATUS_COLORS[e.status]}}>{e.status}</span>
                  </div>
                  <div style={{display:'flex',gap:16,paddingTop:8,borderTop:'1px solid var(--divider)',marginBottom:e.status==='pending'?10:0}}>
                    <div><div style={{fontSize:10,color:'var(--sub)',fontWeight:600,textTransform:'uppercase'}}>Reg</div><div style={{fontWeight:700}}>{e.hours_regular}h</div></div>
                    <div><div style={{fontSize:10,color:'var(--sub)',fontWeight:600,textTransform:'uppercase'}}>OT</div><div style={{fontWeight:700}}>{e.hours_overtime}h</div></div>
                    {e.gross_pay && <div><div style={{fontSize:10,color:'var(--sub)',fontWeight:600,textTransform:'uppercase'}}>Gross</div><div style={{fontWeight:700,color:'var(--green)'}}>${parseFloat(e.gross_pay).toFixed(2)}</div></div>}
                    {e.loaded_cost && <div><div style={{fontSize:10,color:'var(--sub)',fontWeight:600,textTransform:'uppercase'}}>Loaded</div><div style={{fontWeight:700,color:'var(--navy)'}}>${parseFloat(e.loaded_cost).toFixed(2)}</div></div>}
                  </div>
                  {e.status==='pending' && (
                    <button onClick={()=>setEditEntry(e)} style={{width:'100%',padding:'9px 0',background:'var(--navy)',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:12,marginTop:8}}>Review & Approve →</button>
                  )}
                  {e.status==='approved' && (
                    <button onClick={()=>setEditEntry(e)} style={{width:'100%',padding:'9px 0',background:'var(--surface)',color:'var(--navy)',border:'1px solid var(--border)',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:12,marginTop:8}}>Edit Approved Entry ✎</button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="card" style={{overflow:'visible'}}>
              <table>
                <thead><tr><th>Employee</th><th>Role</th><th>Date</th><th>Job</th><th>Reg Hrs</th><th>OT Hrs</th><th>Gross Pay</th><th>Loaded Cost</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {entries.map(e=>(
                    <tr key={e.id}>
                      <td style={{fontWeight:700,color:'var(--navy)'}}>{e.employee_name}</td>
                      <td style={{color:'var(--sub)',fontSize:12}}>{e.role||'—'}</td>
                      <td style={{color:'var(--sub)',whiteSpace:'nowrap'}}>{new Date(e.work_date).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</td>
                      <td style={{color:'var(--accent)',fontSize:12}}>{e.job_name||<span style={{color:'var(--sub)'}}>Unassigned</span>}</td>
                      <td style={{fontWeight:700}}>{e.hours_regular}h</td>
                      <td style={{color:parseFloat(e.hours_overtime)>0?'var(--amber)':'var(--sub)'}}>{e.hours_overtime}h</td>
                      <td style={{fontWeight:700,color:'var(--green)'}}>{e.gross_pay?`$${parseFloat(e.gross_pay).toFixed(2)}`:'—'}</td>
                      <td style={{fontWeight:700}}>{e.loaded_cost?`$${parseFloat(e.loaded_cost).toFixed(2)}`:'—'}</td>
                      <td><span className="badge" style={{...STATUS_COLORS[e.status]}}>{e.status}</span></td>
                      <td>
                        {e.status==='pending' && (
                          <button onClick={()=>setEditEntry(e)} style={{padding:'5px 12px',background:'var(--navy)',color:'#fff',border:'none',borderRadius:6,fontWeight:700,cursor:'pointer',fontSize:11,whiteSpace:'nowrap'}}>Review →</button>
                        )}
                        {e.status==='approved' && (
                          <button onClick={()=>setEditEntry(e)} style={{padding:'5px 12px',background:'var(--surface)',color:'var(--navy)',border:'1px solid var(--border)',borderRadius:6,fontWeight:700,cursor:'pointer',fontSize:11,whiteSpace:'nowrap'}}>Edit ✎</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Employees tab ── */}
      {tab==='employees' && (
        <div data-tour="time-employees">
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <div style={{fontSize:13,color:'var(--sub)'}}>{employees.filter(e=>e.is_active).length} active employees</div>
            <button onClick={()=>{setShowAddEmp(true);setEditEmp(null);setEmpForm({});}} style={{padding:'8px 16px',background:'var(--accent)',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:12}}>+ Add Employee</button>
          </div>

          {/* Add/Edit employee form */}
          {(showAddEmp||editEmp) && (
            <div className="card" style={{padding:20,marginBottom:14}}>
              <div style={{fontWeight:700,color:'var(--navy)',fontSize:14,marginBottom:16}}>{editEmp?`Edit ${editEmp.name}`:'Add New Employee'}</div>
              <div style={{display:'grid',gridTemplateColumns:mobile?'1fr':'repeat(2,1fr)',gap:12,marginBottom:12}}>
                <div>
                  <label className="fl" htmlFor="ef-name">Full Name *</label>
                  <input id="ef-name" className="field" value={empForm.name !== undefined ? empForm.name : (editEmp?.name || '')} onChange={ev=>setEmpForm(p=>({...p,name:ev.target.value}))}/>
                </div>
                <div>
                  <label className="fl" htmlFor="ef-email">Email * (used to log in)</label>
                  <input id="ef-email" className="field" type="email" value={empForm.email !== undefined ? empForm.email : (editEmp?.email || '')} onChange={ev=>setEmpForm(p=>({...p,email:ev.target.value}))}/>
                </div>
                {editEmp && (
                <div>
                  <label className="fl" htmlFor="ef-pw">New Password (leave blank to keep)</label>
                  <input id="ef-pw" className="field" type="password" value={empForm.password || ''} onChange={ev=>setEmpForm(p=>({...p,password:ev.target.value}))}/>
                </div>
                )}
                 <div>
                  <label className="fl" htmlFor="ef-phone">Phone</label>
                </div>
                <div>
                  <label className="fl" htmlFor="ef-role">Role</label>
                  <select id="ef-role" className="field" value={empForm.role !== undefined ? empForm.role : (editEmp?.role || '')} onChange={ev=>setEmpForm(p=>({...p,role:ev.target.value}))}>
                    <option value="">Select role...</option>
{(ROLES||[]).map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="fl" htmlFor="ef-paytype">Pay Type</label>
                  <select id="ef-paytype" className="field" value={empForm.pay_type !== undefined ? empForm.pay_type : (editEmp?.pay_type || 'hourly')} onChange={ev=>setEmpForm(p=>({...p,pay_type:ev.target.value,hourly_rate:undefined,annual_salary:undefined}))}>
                    <option value="hourly">Hourly</option>
                    <option value="salary">Salary</option>
                  </select>
                </div>
                {(empForm.pay_type !== undefined ? empForm.pay_type : (editEmp?.pay_type || 'hourly')) === 'salary' ? (
                <div>
                  <label className="fl" htmlFor="ef-salary">Annual Salary ($)</label>
                  <input id="ef-salary" className="field" type="number" step="1000" value={empForm.annual_salary !== undefined ? empForm.annual_salary : (editEmp?.annual_salary || '')} onChange={ev=>setEmpForm(p=>({...p,annual_salary:ev.target.value}))} placeholder="e.g. 55000"/>
                </div>
                ) : (
                <div>
                  <label className="fl" htmlFor="ef-rate">Hourly Rate ($)</label>
                  <input id="ef-rate" className="field" type="number" step="0.25" value={empForm.hourly_rate !== undefined ? empForm.hourly_rate : (editEmp?.hourly_rate || '')} onChange={ev=>setEmpForm(p=>({...p,hourly_rate:ev.target.value}))}/>
                </div>
                )}
                <div>
                  <label className="fl" htmlFor="ef-ot">Overtime Rate ($ — auto 1.5× if blank)</label>
                  <input id="ef-ot" className="field" type="number" step="0.25" value={empForm.overtime_rate !== undefined ? empForm.overtime_rate : (editEmp?.overtime_rate || '')} onChange={ev=>setEmpForm(p=>({...p,overtime_rate:ev.target.value}))}/>
                </div>
                <div>
                  <label className="fl" htmlFor="ef-burden">Burden Rate (e.g. 0.26 = 26%)</label>
                  <input id="ef-burden" className="field" type="number" step="0.01" value={empForm.burden_rate !== undefined ? empForm.burden_rate : (editEmp?.burden_rate || 0.26)} onChange={ev=>setEmpForm(p=>({...p,burden_rate:ev.target.value}))}/>
                </div>
              </div>
              <div style={{background:'#eff6ff',borderRadius:8,padding:'8px 14px',fontSize:12,color:'#1e40af',marginBottom:14}}>
                Burden rate covers employer taxes, insurance, and benefits on top of gross pay. Typical range: 20–35%.
              </div>
              <div style={{display:'flex',gap:10}}>
                <button onClick={saveEmployee} disabled={saving} style={{flex:1,padding:11,background:'var(--navy)',color:'#fff',border:'none',borderRadius:8,fontWeight:700,cursor:'pointer'}}>{saving?'Saving...':editEmp?'Save Changes':'Add Employee'}</button>
                <button onClick={()=>{setShowAddEmp(false);setEditEmp(null);setEmpForm({});}} style={{flex:1,padding:11,background:'var(--surface)',color:'var(--sub)',border:'1px solid var(--border)',borderRadius:8,fontWeight:700,cursor:'pointer'}}>Cancel</button>
              </div>
            </div>
          )}

          {employees.length===0 ? (
            <div className="card" style={{padding:40,textAlign:'center',color:'var(--sub)'}}>
              <div style={{fontSize:28,marginBottom:10}}>👷</div>
              <div style={{fontWeight:700,color:'var(--navy)',marginBottom:4}}>No employees yet</div>
              <div style={{fontSize:13}}>Add employees so they can log hours in JobHub</div>
            </div>
          ) : (
            <div className="card" style={{overflow:'hidden'}}>
              {employees.map((e,i)=>(
                <div key={e.id} style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',padding:'14px 18px',borderBottom:i<employees.length-1?'1px solid var(--divider)':'none',flexWrap:'wrap',gap:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,color:'var(--navy)',fontSize:13}}>{e.name} {!e.is_active&&<span style={{fontSize:10,color:'var(--sub)',fontWeight:400}}>(inactive)</span>}</div>
                   <div style={{fontSize:12,color:'var(--sub)',marginTop:2}}>{e.role||'Employee'} · {e.pay_type==='salary'?`$${parseFloat(e.annual_salary||0).toLocaleString()}/yr ($${parseFloat(e.hourly_rate).toFixed(2)}/hr eff.)`:`$${parseFloat(e.hourly_rate).toFixed(2)}/hr`} · {Math.round(parseFloat(e.burden_rate)*100)}% burden</div>
                    <div style={{fontSize:11,color:'var(--sub)',marginTop:1}}>{e.email}</div>
                    <CRMToggle employee={e} onUpdate={(id,val)=>setEmployees(prev=>prev.map(x=>x.id===id?{...x,crm_access:val}:x))}/>
                  </div>
                  <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
                    <button onClick={()=>{setEditEmp(e);setEmpForm({});setShowAddEmp(false);}} style={{padding:'5px 12px',background:'var(--surface)',color:'var(--navy)',border:'1px solid var(--border)',borderRadius:6,fontWeight:700,cursor:'pointer',fontSize:11}}>Edit</button>
                    <button onClick={async()=>{await api.patch(`/time/employees/${e.id}`,{is_active:!e.is_active});loadEmployees();}} style={{padding:'5px 12px',background:e.is_active?'#fef2f2':'#f0fdf4',color:e.is_active?'var(--red)':'var(--green)',border:'none',borderRadius:6,fontWeight:700,cursor:'pointer',fontSize:11}}>
                      {e.is_active?'Deactivate':'Reactivate'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Login instructions */}
          <div style={{marginTop:14,padding:'14px 18px',background:'#eff6ff',borderRadius:12,fontSize:13,color:'#1e40af',lineHeight:1.7}}>
            <strong>How employees log in:</strong> Direct them to <strong>app.jobhubco.com/employee</strong> and have them sign in with their email and password. No Company ID needed — JobHub finds their account automatically from their email.
          </div>
        </div>
      )}

      {editEntry && <EntryReviewModal entry={editEntry} jobs={jobs} saving={saving} mobile={mobile} onApprove={approve} onReject={reject} onUnapprove={unapprove} onClose={()=>setEditEntry(null)}/>}
    </div>
  );
}

// ─── EMPLOYEE TIME ENTRY (separate login flow) ────────────────────────────────
const EmpAuthCtx = createContext({});
const useEmpAuth = () => useContext(EmpAuthCtx);

function EmpAuthProvider({ children }) {
  const [empToken, setEmpToken] = useState(() => localStorage.getItem('jhEmpToken'));
  const [employee, setEmployee] = useState(() => { try { return JSON.parse(localStorage.getItem('jhEmpData')||'null'); } catch{ return null; }});
  const empLogin  = (t, e) => { localStorage.setItem('jhEmpToken', t); localStorage.setItem('jhEmpData', JSON.stringify(e)); setEmpToken(t); setEmployee(e); };
  const empLogout = ()     => { localStorage.removeItem('jhEmpToken'); localStorage.removeItem('jhEmpData'); setEmpToken(null); setEmployee(null); };
  return <EmpAuthCtx.Provider value={{ empToken, employee, empLogin, empLogout }}>{children}</EmpAuthCtx.Provider>;
}

function EmployeeLogin() {
  const { empLogin } = useEmpAuth();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/time/employee/login`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      empLogin(data.token, data.employee);
    } catch(err) { setError(err.message); }
    finally { setLoading(false); }
  }

  return (
    <div style={{minHeight:'100vh',background:'linear-gradient(135deg,#1a2535 0%,#1a3a5c 100%)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{width:'100%',maxWidth:380}}>
        <div style={{textAlign:'center',marginBottom:28}}>
          <div style={{width:52,height:52,borderRadius:14,background:'var(--accent)',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:26,marginBottom:12}}>⚡</div>
          <h1 style={{color:'#fff',fontSize:24,fontWeight:800,margin:'0 0 4px'}}>JobHub</h1>
          <p style={{color:'rgba(255,255,255,.55)',fontSize:13}}>Employee Time Entry</p>
        </div>
        <div className="card" style={{padding:28}}>
          {error && <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:8,padding:'10px 14px',color:'var(--red)',fontSize:13,marginBottom:16}}>{error}</div>}
          <form onSubmit={handleSubmit}>
            <div style={{marginBottom:14}}>
              <label className="fl" htmlFor="emp-email">Email</label>
              <input id="emp-email" className="field" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" required/>
            </div>
            <div style={{marginBottom:20}}>
              <label className="fl" htmlFor="emp-pw">Password</label>
              <input id="emp-pw" className="field" type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required/>
            </div>
            <button type="submit" disabled={loading} style={{width:'100%',padding:13,background:loading?'#9ca3af':'var(--navy)',color:'#fff',border:'none',borderRadius:10,fontSize:15,fontWeight:800,cursor:loading?'not-allowed':'pointer'}}>
              {loading?'Signing in...':'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function EmployeePortal() {
  const { employee, empLogout, empToken } = useEmpAuth();
  const mobile = window.innerWidth < 768;
  const [jobs, setJobs]           = useState([]);
  const [myEntries, setMyEntries] = useState([]);
  const [mySchedule, setMySchedule] = useState([]);
  const [tab, setTab]             = useState('log'); // 'log' | 'history' | 'calendar'
  const [form, setForm]           = useState({ job_id:'', work_date: new Date().toISOString().split('T')[0], hours_regular:'', hours_overtime:'0', notes:'' });
  const [saving, setSaving]       = useState(false);
  const [success, setSuccess]     = useState(false);
  const [crmAccess, setCrmAccess] = useState(() => { try { return JSON.parse(localStorage.getItem('jhEmpData')||'{}').crm_access || false; } catch { return false; } });
  const [calMonth, setCalMonth]   = useState(new Date());
  const toast = useToast();

  const empApi = {
    get:  (url)       => fetch(`${process.env.REACT_APP_API_URL}/api${url}`, { headers:{ Authorization:`Bearer ${empToken}` }}).then(r=>r.json()),
    post: (url, body) => fetch(`${process.env.REACT_APP_API_URL}/api${url}`, { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${empToken}` }, body:JSON.stringify(body) }).then(async r=>{ const d=await r.json(); if(!r.ok)throw new Error(d.error); return d; }),
  };

  const reload = () => Promise.all([
  empApi.get('/time/my/jobs'),
  empApi.get('/time/my/entries'),
  empApi.get('/schedule/my').catch(()=>[]),
  empApi.get('/time/employee/me').catch(()=>null)
]).then(([j,e,s,me]) => {
  setJobs(Array.isArray(j) ? j : (j?.jobs || j?.data || []));
  setMyEntries(Array.isArray(e) ? e : (e?.entries || e?.data || []));
  setMySchedule(Array.isArray(s) ? s : (s?.data || []));
  if (me?.crm_access !== undefined) {
    setCrmAccess(me.crm_access);
  }
}).catch(()=>{});

  useEffect(() => { reload().finally(()=>setLoading(false)); }, []);

  async function submit(ev) {
    ev.preventDefault();
    if (!form.job_id) { toast.error('Please select a job'); return; }
    if (!form.hours_regular || parseFloat(form.hours_regular) <= 0) { toast.error('Enter hours worked'); return; }
    setSaving(true);
    try {
      await empApi.post('/time/my/entries', { ...form, hours_regular: parseFloat(form.hours_regular), hours_overtime: parseFloat(form.hours_overtime||0) });
      setSuccess(true);
      setForm(p => ({ ...p, job_id:'', hours_regular:'', hours_overtime:'0', notes:'' }));
      await reload();
      setTimeout(() => setSuccess(false), 3000);
    } catch(err) { toast.error(err.message||'Failed to submit'); }
    finally { setSaving(false); }
  }

  const SC = { pending:{ bg:'#fef3c7',color:'#92400e' }, approved:{ bg:'#d1fae5',color:'#065f46' }, rejected:{ bg:'#fee2e2',color:'#991b1b' } };

  // ── Calendar helpers ──────────────────────────────────────────────────────
  const entryByDate = myEntries.reduce((acc, e) => {
    const d = e.work_date?.slice(0,10);
    if (!acc[d]) acc[d] = [];
    acc[d].push(e);
    return acc;
  }, {});

  function CalendarView() {
    const year  = calMonth.getFullYear();
    const month = calMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month+1, 0).getDate();
    const today = new Date().toISOString().split('T')[0];
    const monthName = calMonth.toLocaleDateString('en-US', { month:'long', year:'numeric' });

    const cells = [];
    for (let i=0; i<firstDay; i++) cells.push(null);
    for (let d=1; d<=daysInMonth; d++) cells.push(d);

    return (
      <div>
        {/* Month nav */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
          <button onClick={()=>setCalMonth(new Date(year,month-1,1))} style={{background:'none',border:'1px solid var(--border)',borderRadius:8,padding:'6px 12px',cursor:'pointer',fontWeight:700,color:'var(--navy)'}}>‹</button>
          <div style={{fontWeight:700,color:'var(--navy)',fontSize:14}}>{monthName}</div>
          <button onClick={()=>setCalMonth(new Date(year,month+1,1))} style={{background:'none',border:'1px solid var(--border)',borderRadius:8,padding:'6px 12px',cursor:'pointer',fontWeight:700,color:'var(--navy)'}}>›</button>
        </div>
        {/* Day headers */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2,marginBottom:4}}>
          {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d=>(
            <div key={d} style={{textAlign:'center',fontSize:11,fontWeight:700,color:'var(--sub)',padding:'4px 0'}}>{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:2}}>
          {cells.map((d,i) => {
            if (!d) return <div key={`e${i}`}/>;
            const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
            const entries = entryByDate[dateStr] || [];
            const isToday = dateStr === today;
            const hasApproved = entries.some(e=>e.status==='approved');
            const hasPending  = entries.some(e=>e.status==='pending');
            const hasRejected = entries.some(e=>e.status==='rejected');
            const dotColor = hasApproved ? '#10b981' : hasPending ? '#f59e0b' : hasRejected ? '#ef4444' : null;
            const totalHrs = entries.reduce((s,e)=>s+parseFloat(e.hours_regular||0)+parseFloat(e.hours_overtime||0),0);
            return (
              <div key={d} style={{borderRadius:8,padding:'6px 4px',background:isToday?'#eff6ff':entries.length?'#f9fafb':'transparent',border:isToday?'1px solid var(--accent)':'1px solid transparent',minHeight:44,textAlign:'center',cursor:entries.length?'pointer':'default'}}
                onClick={()=>{ if(entries.length) setTab('history'); }}>
                <div style={{fontSize:12,fontWeight:isToday?800:500,color:isToday?'var(--accent)':'var(--navy)'}}>{d}</div>
                {dotColor && <div style={{width:6,height:6,borderRadius:3,background:dotColor,margin:'2px auto 0'}}/>}
                {totalHrs>0 && <div style={{fontSize:9,color:'var(--sub)',marginTop:1}}>{totalHrs}h</div>}
              </div>
            );
          })}
        </div>
        {/* Legend */}
        <div style={{display:'flex',gap:12,marginTop:12,justifyContent:'center',flexWrap:'wrap'}}>
          {[['#10b981','Approved'],['#f59e0b','Pending'],['#ef4444','Rejected']].map(([color,label])=>(
            <div key={label} style={{display:'flex',alignItems:'center',gap:4,fontSize:11,color:'var(--sub)'}}>
              <div style={{width:8,height:8,borderRadius:4,background:color}}/>
              {label}
            </div>
          ))}
        </div>
        {/* Monthly summary */}
        {myEntries.length>0 && (()=>{
          const monthEntries = myEntries.filter(e=>e.work_date?.startsWith(`${year}-${String(month+1).padStart(2,'0')}`));
          const totalReg = monthEntries.reduce((s,e)=>s+parseFloat(e.hours_regular||0),0);
          const totalOT  = monthEntries.reduce((s,e)=>s+parseFloat(e.hours_overtime||0),0);
          const approved = monthEntries.filter(e=>e.status==='approved').length;
          if (!monthEntries.length) return null;
          return (
            <div style={{marginTop:14,background:'var(--surface)',borderRadius:10,padding:'10px 14px',display:'flex',gap:16,justifyContent:'center',flexWrap:'wrap'}}>
              <div style={{textAlign:'center'}}><div style={{fontSize:10,color:'var(--sub)',fontWeight:700,textTransform:'uppercase'}}>Days Worked</div><div style={{fontWeight:800,color:'var(--navy)',fontSize:16}}>{monthEntries.length}</div></div>
              <div style={{textAlign:'center'}}><div style={{fontSize:10,color:'var(--sub)',fontWeight:700,textTransform:'uppercase'}}>Reg Hours</div><div style={{fontWeight:800,color:'var(--navy)',fontSize:16}}>{totalReg}h</div></div>
              {totalOT>0&&<div style={{textAlign:'center'}}><div style={{fontSize:10,color:'var(--sub)',fontWeight:700,textTransform:'uppercase'}}>OT Hours</div><div style={{fontWeight:800,color:'var(--amber)',fontSize:16}}>{totalOT}h</div></div>}
              <div style={{textAlign:'center'}}><div style={{fontSize:10,color:'var(--sub)',fontWeight:700,textTransform:'uppercase'}}>Approved</div><div style={{fontWeight:800,color:'var(--green)',fontSize:16}}>{approved}</div></div>
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <div style={{minHeight:'100vh',background:'var(--surface)',display:'flex',flexDirection:'column'}}>
      {/* Header */}
      <div style={{background:'var(--navy)',padding:'14px 20px',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{width:32,height:32,borderRadius:8,background:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16}}>⚡</div>
          <div>
            <div style={{color:'#fff',fontWeight:700,fontSize:13}}>{employee?.name}</div>
            <div style={{color:'rgba(255,255,255,.5)',fontSize:11}}>{employee?.role||'Employee'} · {employee?.companyName}</div>
          </div>
        </div>
        <button onClick={empLogout} style={{background:'rgba(255,255,255,.1)',border:'none',color:'rgba(255,255,255,.7)',padding:'6px 12px',borderRadius:8,cursor:'pointer',fontSize:12,fontWeight:600}}>Sign out</button>
      </div>

      {/* Tab bar */}
      <div style={{background:'#fff',borderBottom:'1px solid var(--border)',display:'flex',flexShrink:0}}>
        {([['log','Log Hours','⏱'],['crm','CRM','🤝'],['schedule','Schedule','📅'],['history','My Shifts',''],['calendar','Calendar','🗓']])
          .map(([key,label,icon])=>(
          <button key={key} onClick={()=>setTab(key)} style={{flex:1,padding:'12px 8px',background:'none',border:'none',borderBottom:`3px solid ${tab===key?'var(--accent)':'transparent'}`,color:tab===key?'var(--accent)':'var(--sub)',fontWeight:tab===key?700:400,cursor:'pointer',fontSize:12,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
            <span style={{fontSize:16}}>{icon}</span>
            {label}
          </button>
        ))}
      </div>

      <div style={{flex:1,overflowY:'auto',padding:16,maxWidth:520,width:'100%',margin:'0 auto'}}>

        {/* ── Log Hours tab ── */}
        {tab==='log' && (
          <div className="card" style={{padding:20}}>
            <div style={{fontWeight:800,color:'var(--navy)',fontSize:15,marginBottom:16}}>Log Hours</div>
            {success && (
              <div style={{background:'#d1fae5',border:'1px solid #6ee7b7',borderRadius:8,padding:'10px 14px',color:'#065f46',fontWeight:700,fontSize:13,marginBottom:14}}>
                ✅ Hours submitted! Awaiting review.
              </div>
            )}
            <form onSubmit={submit}>
              <div style={{marginBottom:14}}>
                <label className="fl" htmlFor="ep-job">Job *</label>
                <select id="ep-job" className="field" value={form.job_id} onChange={ev=>setForm(p=>({...p,job_id:ev.target.value}))} required>
                  <option value="">Select a job...</option>
                  {jobs.map(j=><option key={j.id} value={j.id}>{j.name} — {j.customer_name}</option>)}
                </select>
              </div>
              <div style={{marginBottom:14}}>
                <label className="fl" htmlFor="ep-date">Work Date *</label>
                <input id="ep-date" className="field" type="date" value={form.work_date} onChange={ev=>setForm(p=>({...p,work_date:ev.target.value}))} required/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
                <div>
                  <label className="fl" htmlFor="ep-reg">Regular Hours *</label>
                  <input id="ep-reg" className="field" type="number" step="0.25" min="0" max="24" placeholder="8" value={form.hours_regular} onChange={ev=>setForm(p=>({...p,hours_regular:ev.target.value}))} required/>
                </div>
                <div>
                  <label className="fl" htmlFor="ep-ot">Overtime Hours</label>
                  <input id="ep-ot" className="field" type="number" step="0.25" min="0" max="24" placeholder="0" value={form.hours_overtime} onChange={ev=>setForm(p=>({...p,hours_overtime:ev.target.value}))}/>
                </div>
              </div>
              <div style={{marginBottom:16}}>
                <label className="fl" htmlFor="ep-notes">Notes (optional)</label>
                <textarea id="ep-notes" className="field" rows={2} placeholder="What did you work on?" value={form.notes} onChange={ev=>setForm(p=>({...p,notes:ev.target.value}))}/>
              </div>
              <button type="submit" disabled={saving||loading} style={{width:'100%',padding:14,background:saving?'#9ca3af':'var(--accent)',color:'#fff',border:'none',borderRadius:10,fontWeight:800,fontSize:15,cursor:saving?'not-allowed':'pointer'}}>
                {saving?'Submitting...':'Submit Hours'}
              </button>
            </form>
            </div>
        )}

        {/* ── Schedule tab ── */}
        {tab==='schedule' && (
          <div>
            {mySchedule.length === 0 ? (
              <div className="card" style={{padding:40,textAlign:'center',color:'var(--sub)'}}>
                <div style={{fontSize:28,marginBottom:10}}>📅</div>
                <div style={{fontWeight:700,color:'var(--navy)',marginBottom:4}}>No upcoming assignments</div>
                <div style={{fontSize:13}}>Your scheduled jobs will appear here</div>
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {mySchedule.map(a => {
                  const isToday = a.assignment_date?.split('T')[0] === new Date().toISOString().split('T')[0];
                  return (
                    <div key={a.id} className="card" style={{padding:14,borderLeft:isToday?'3px solid var(--accent)':'3px solid transparent'}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                        <div>
                          <div style={{fontWeight:700,color:'var(--navy)',fontSize:14}}>{a.job_name}</div>
                          {a.customer_name && <div style={{fontSize:12,color:'var(--accent)',marginTop:2}}>{a.customer_name}</div>}
                        </div>
                        {isToday && <span style={{padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:700,background:'#dbeafe',color:'#1e40af'}}>Today</span>}
                      </div>
                      <div style={{fontSize:12,color:'var(--sub)',marginTop:6}}>
                        {new Date(a.assignment_date).toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'})}
                        {a.start_time && ` · ${a.start_time.slice(0,5)}`}{a.end_time && ` — ${a.end_time.slice(0,5)}`}
                      </div>
                      {a.notes && <div style={{fontSize:11,color:'var(--sub)',marginTop:4,fontStyle:'italic'}}>"{a.notes}"</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Shift History tab ── */}
        {tab==='history' && (
          <div>
            {/* Summary pills */}
            {myEntries.length>0 && (
              <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                {['all','pending','approved','rejected'].map(s=>{
                  const count = s==='all' ? myEntries.length : myEntries.filter(e=>e.status===s).length;
                  const colors = s==='all'?{bg:'var(--navy)',color:'#fff'}:SC[s];
                  return (
                    <span key={s} style={{padding:'4px 12px',borderRadius:20,fontSize:11,fontWeight:700,...(s==='all'?{background:'var(--navy)',color:'#fff'}:{background:colors.bg,color:colors.color})}}>
                      {s.charAt(0).toUpperCase()+s.slice(1)} ({count})
                    </span>
                  );
                })}
              </div>
            )}
            {myEntries.length===0 ? (
              <div className="card" style={{padding:40,textAlign:'center',color:'var(--sub)'}}>
                <div style={{fontSize:28,marginBottom:10}}></div>
                <div style={{fontWeight:700,color:'var(--navy)',marginBottom:4}}>No shifts yet</div>
                <div style={{fontSize:13}}>Your submitted hours will appear here</div>
              </div>
            ) : (
              <div className="card" style={{overflow:'hidden'}}>
                {myEntries.map((e,i)=>(
                  <div key={e.id} style={{padding:'14px 18px',borderBottom:i<myEntries.length-1?'1px solid var(--divider)':'none'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:6}}>
                      <div>
                        <div style={{fontWeight:700,color:'var(--navy)',fontSize:13}}>{e.job_name||'Unassigned'}</div>
                        <div style={{fontSize:12,color:'var(--sub)',marginTop:2}}>
                          {new Date(e.work_date).toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'})}
                        </div>
                      </div>
                      <span className="badge" style={{...SC[e.status]}}>{e.status}</span>
                    </div>
                    <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
                      <div style={{fontSize:12}}><span style={{color:'var(--sub)'}}>Reg: </span><strong>{e.hours_regular}h</strong></div>
                      {parseFloat(e.hours_overtime)>0 && <div style={{fontSize:12}}><span style={{color:'var(--sub)'}}>OT: </span><strong style={{color:'var(--amber)'}}>{e.hours_overtime}h</strong></div>}
                      {e.status==='approved' && e.gross_pay && <div style={{fontSize:12}}><span style={{color:'var(--sub)'}}>Gross: </span><strong style={{color:'var(--green)'}}>${parseFloat(e.gross_pay).toFixed(2)}</strong></div>}
                    </div>
                    {e.notes && <div style={{fontSize:11,color:'var(--sub)',marginTop:4,fontStyle:'italic'}}>"{e.notes}"</div>}
                    {e.status==='rejected' && <div style={{fontSize:11,color:'var(--red)',marginTop:4,fontWeight:600}}>⚠ Rejected — contact your supervisor</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Calendar tab ── */}
        {tab==='calendar' && (
          <div className="card" style={{padding:18}}>
            <CalendarView />
          </div>
        )}

        {/* ── CRM tab ── */}
        {tab==='crm' && crmAccess && (
          <TeamLeadCRM empToken={empToken} />
        )}

      </div>
    </div>
  );
}

function EmployeeApp() {
  const { empToken } = useEmpAuth();
  return empToken ? <EmployeePortal /> : <EmployeeLogin />;
}

// ─── ONBOARDING ───────────────────────────────────────────────────────────────
function Onboarding() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIndustryPicker, setShowIndustryPicker] = useState(false);
  const [allIndustries, setAllIndustries] = useState({});
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [currentIndustry, setCurrentIndustry] = useState('');
  const [saving, setSaving] = useState(false);
  const { reload: reloadIndustry } = useIndustry();
  const toast = useToast();
  const mobile = window.innerWidth < 768;

  useEffect(() => {
    api.get('/onboarding').then(r => setStatus(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/auth/industry').then(r => {
      setAllIndustries(r.data.allIndustries || {});
      setCurrentIndustry(r.data.industry || '');
      setSelectedIndustry(r.data.industry || '');
      if (!r.data.industry) setShowIndustryPicker(true);
    }).catch(() => {});
  }, []);

  async function saveIndustry() {
    if (!selectedIndustry) { toast.error('Please select your trade'); return; }
    setSaving(true);
    try {
      await api.patch('/auth/industry', { industry: selectedIndustry });
      setCurrentIndustry(selectedIndustry);
      setShowIndustryPicker(false);
      reloadIndustry();
      toast.success(`${allIndustries[selectedIndustry]?.label} selected — roles and categories updated`);
    } catch(e) { toast.error('Failed to save industry'); }
    finally { setSaving(false); }
  }

  const STEPS = [
    {
      key: 'connect_qbo',
      icon: '',
      title: 'Connect QuickBooks Online',
      desc: 'Link your QBO account so JobHub can pull in your clients, jobs, invoices, and bills automatically.',
      action: 'Connect QuickBooks',
      path: '/integrations',
      color: '#d97706',
    },
    {
      key: 'sync_data',
      icon: '',
      title: 'Sync your QuickBooks data',
      desc: 'Run your first sync to import clients, jobs, and bills from QuickBooks into JobHub.',
      action: 'Go to Integrations',
      path: '/integrations',
      color: '#0369a1',
    },
    {
      key: 'connect_payroll',
      icon: '',
      title: 'Connect your payroll system',
      desc: 'Payroll provider integrations are coming soon. For now, JobHub tracks and approves crew hours for job costing.',
      action: 'View Integrations',
      path: '/integrations',
      color: '#7c3aed',
    },
    {
      key: 'create_job',
      icon: '',
      title: 'Set up your first job budget',
      desc: 'Add budget amounts to a job so you can track costs against your estimate in real time.',
      action: 'Go to Jobs',
      path: '/jobs',
      color: '#16a34a',
    },
    {
      key: 'setup_reports',
      icon: '',
      title: 'Set up automated report emails',
      desc: 'Schedule weekly or monthly reports to go straight to your bookkeeper or CPA.',
      action: 'Set Up Reports',
      path: '/reports',
      color: '#1a2535',
    },
  ];

  if (loading) return <div className="card" style={{ padding: 20 }}>{[1,2,3,4,5].map(i => <Skel key={i} h={60} mb={8} />)}</div>;

  const completedCount = status ? Object.values(status.steps || {}).filter(Boolean).length : 0;
  const totalSteps = STEPS.length;
  const pct = Math.round((completedCount / totalSteps) * 100);
  const allDone = completedCount === totalSteps;

  return (
    <div>
      {/* ── Industry Picker ── */}
      <div className="card" style={{ padding: mobile ? 18 : 24, marginBottom: 16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: showIndustryPicker ? 16 : 0 }}>
          <div>
            <div style={{ fontWeight:800, color:'var(--navy)', fontSize:15 }}>
              {currentIndustry && allIndustries[currentIndustry]
                ? `${allIndustries[currentIndustry].icon} ${allIndustries[currentIndustry].label}`
                : ' Select Your Trade'}
            </div>
            <div style={{ fontSize:12, color:'var(--sub)', marginTop:2 }}>
              {currentIndustry ? 'Your roles and cost categories are set for this trade' : 'Required — sets your default roles and cost categories'}
            </div>
          </div>
          <button onClick={() => setShowIndustryPicker(!showIndustryPicker)} style={{ padding:'6px 14px', background:'var(--surface)', color:'var(--navy)', border:'1px solid var(--border)', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer' }}>
            {showIndustryPicker ? 'Cancel' : '✏️ Change'}
          </button>
        </div>

        {showIndustryPicker && (
          <div>
            <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(3,1fr)', gap:8, marginBottom:14 }}>
              {Object.entries(allIndustries).map(([key, ind]) => (
                <button key={key} onClick={() => setSelectedIndustry(key)} style={{
                  padding:'12px 10px', borderRadius:10, border:`2px solid ${selectedIndustry===key ? 'var(--accent)' : 'var(--border)'}`,
                  background: selectedIndustry===key ? '#eff6ff' : '#fff', cursor:'pointer', textAlign:'center',
                  color: selectedIndustry===key ? 'var(--accent)' : 'var(--navy)',
                }}>
                  <div style={{ fontSize:22, marginBottom:4 }}>{ind.icon}</div>
                  <div style={{ fontSize:12, fontWeight:700 }}>{ind.label}</div>
                </button>
              ))}
            </div>

            {selectedIndustry && allIndustries[selectedIndustry] && (
              <div style={{ background:'var(--surface)', borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--navy)', marginBottom:6 }}>Default roles for {allIndustries[selectedIndustry].label}:</div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                  {allIndustries[selectedIndustry].roles.map(r => (
                    <span key={r} style={{ padding:'3px 10px', background:'#fff', border:'1px solid var(--border)', borderRadius:20, fontSize:11, color:'var(--sub)' }}>{r}</span>
                  ))}
                </div>
              </div>
            )}

            <button onClick={saveIndustry} disabled={saving || !selectedIndustry} style={{ width:'100%', padding:11, background:'var(--navy)', color:'#fff', border:'none', borderRadius:9, fontWeight:700, cursor:'pointer', fontSize:14 }}>
              {saving ? 'Saving...' : `Set Trade to ${selectedIndustry ? allIndustries[selectedIndustry]?.label : '—'}`}
            </button>
          </div>
        )}
      </div>
      {/* Header card */}
      <div className="card" style={{ padding: mobile ? 18 : 24, marginBottom: 16, background: 'var(--navy)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', marginBottom: 4 }}>
              {allDone ? '🎉 You\'re all set!' : 'Get started with JobHub'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              {allDone ? 'All setup steps complete. Your account is fully configured.' : `${completedCount} of ${totalSteps} steps complete — finish setup to get the most out of JobHub`}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>{pct}%</div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>complete</div>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ marginTop: 16, height: 6, background: 'rgba(255,255,255,.15)', borderRadius: 3 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: 'var(--accent)', borderRadius: 3, transition: 'width .5s ease' }} />
        </div>
      </div>

      {/* Steps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {STEPS.map((step, i) => {
          const done = status?.steps?.[step.key];
          const isNext = !done && STEPS.slice(0, i).every(s => status?.steps?.[s.key]);
          return (
            <div key={step.key} className="card" style={{ padding: mobile ? 16 : 20, opacity: done ? 0.7 : 1, borderLeft: `4px solid ${done ? '#10b981' : isNext ? step.color : 'var(--border)'}` }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                {/* Icon */}
                <div style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, background: done ? '#dcfce7' : `${step.color}18`, border: `2px solid ${done ? '#86efac' : step.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: done ? 18 : 20 }}>
                  {done ? '✓' : step.icon}
                </div>
                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <div style={{ fontWeight: 700, color: done ? 'var(--sub)' : 'var(--navy)', fontSize: 14, textDecoration: done ? 'line-through' : 'none' }}>{step.title}</div>
                    {done && <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>Done</span>}
                    {isNext && !done && <span style={{ fontSize: 10, background: step.color, color: '#fff', padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>Up next</span>}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--sub)', lineHeight: 1.5, marginBottom: done ? 0 : 12 }}>{step.desc}</div>
                  {!done && (
                    <button onClick={() => navigate(step.path)} style={{ padding: '8px 16px', background: isNext ? step.color : 'var(--surface)', color: isNext ? '#fff' : 'var(--navy)', border: `1px solid ${isNext ? step.color : 'var(--border)'}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      {step.action} →
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Done state */}
      {allDone && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <button onClick={() => navigate('/')} style={{ padding: '12px 28px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            Go to Dashboard →
          </button>
        </div>
      )}
    </div>
  );
}

// ─── SETTINGS ─────────────────────────────────────────────────────────────────
function Settings() {
  const { company, login, logout } = useAuth();
  const toast = useToast();
  const mobile = window.innerWidth < 768;
  const [tab, setTab] = useState('company');
  const [compForm, setCompForm] = useState({ name: company?.name || '', email: company?.email || '' });
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [indConfig, setIndConfig] = useState(null);
  const [selectedInd, setSelectedInd] = useState('');
  const [editRoles, setEditRoles] = useState([]);
  const [editCats, setEditCats] = useState([]);
  const [newRole, setNewRole] = useState('');
  const [newCat, setNewCat] = useState('');
  const { reload: reloadIndustry } = useIndustry();
  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const isAdmin = ['peyton@jobhubco.com'].includes(company?.email);
  const isSolo = company?.plan === 'solo';
  useEffect(() => {
    if (tab === 'billing' && !billing) {
      setBillingLoading(true);
      api.get('/billing/status').then(r => setBilling(r.data)).catch(() => {}).finally(() => setBillingLoading(false));
    }
    if (tab === 'industry' && !indConfig) {
      api.get('/auth/industry').then(r => {
        setIndConfig(r.data);
        setSelectedInd(r.data.industry || 'electrical');
        setEditRoles(r.data.roles || []);
        setEditCats(r.data.costCategories || []);
      }).catch(() => {});
    }
  if (tab === 'referrals' && isAdmin && !referrals.length) {
  setReferralsLoading(true);
  api.get('/admin/referrals')
    .then(r => setReferrals(r.data.referrals || []))
    .catch(() => {})
    .finally(() => setReferralsLoading(false));
}
}, [tab]);

  async function saveIndustry() {
    setSaving(true);
    try {
      await api.patch('/auth/industry', { industry: selectedInd, customRoles: editRoles, customCategories: editCats });
      reloadIndustry();
      toast.success('Trade settings saved');
    } catch(e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }

  function resetToDefaults() {
    if (!indConfig?.allIndustries?.[selectedInd]) return;
    const def = indConfig.allIndustries[selectedInd];
    setEditRoles([...def.roles]);
    setEditCats([...def.costCategories]);
  }

  async function saveCompany() {
    setSaving(true);
    try {
      const r = await api.patch('/auth/company', compForm);
      const stored = JSON.parse(localStorage.getItem('jobhubCompany') || '{}');
      const updated = { ...stored, ...r.data };
      localStorage.setItem('jobhubCompany', JSON.stringify(updated));
      login(localStorage.getItem('jobhubToken'), updated);
      toast.success('Company info updated');
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  async function changePassword() {
    if (!pwForm.current) { toast.error('Enter your current password'); return; }
    if (pwForm.next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.post('/auth/change-password', { currentPassword: pwForm.current, newPassword: pwForm.next });
      toast.success('Password changed successfully');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  async function openPortal() {
    setPortalLoading(true);
    try {
      const r = await api.post('/billing/portal', {});
      window.location.href = r.data.url;
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setPortalLoading(false); }
  }

  async function startCheckout(tier, interval) {
    setSaving(true);
    try {
      const r = await api.post('/billing/checkout', { tier, interval });
      window.location.href = r.data.url;
    } catch(e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  }

  const STATUS_STYLES = {
    active:       { bg: '#d1fae5', color: '#065f46', label: 'Active' },
    trialing:     { bg: '#dbeafe', color: '#1e40af', label: 'Trial' },
    past_due:     { bg: '#fee2e2', color: '#991b1b', label: 'Past Due' },
    grace_period: { bg: '#fef3c7', color: '#92400e', label: 'Grace Period' },
    locked:       { bg: '#f3f4f6', color: '#374151', label: 'Locked' },
    canceled:     { bg: '#f3f4f6', color: '#374151', label: 'Canceled' },
  };

  return (
    <div>
      {/* Tabs */}
      <div data-tour="settings-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(isAdmin ? [['company', 'Company'], ['password', 'Password'], ['billing', 'Billing'], ['industry', 'Trade & Roles'], ['referrals', 'Partner Referrals']] : [['company', 'Company'], ['password', 'Password'], ['billing', 'Billing'], ['industry', 'Trade & Roles']]).map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{ padding: '8px 18px', borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: 'pointer', background: tab === key ? 'var(--navy)' : 'var(--surface)', color: tab === key ? '#fff' : 'var(--sub)', border: `1px solid ${tab === key ? 'var(--navy)' : 'var(--border)'}` }}>{label}</button>
        ))}
      </div>

      {/* Company tab */}
      {tab === 'company' && (
        <div className="card" style={{ padding: mobile ? 18 : 24 }}>
          <div className="sh" style={{ marginBottom: 4 }}>Company Info</div>
          <div className="ss" style={{ marginBottom: 18 }}>{isSolo ? 'This name appears on your reports' : 'This name appears on reports and employee login'}</div>
          <div style={{ marginBottom: 14 }}>
            <label className="fl" htmlFor="s-name">Company Name</label>
            <input id="s-name" className="field" value={compForm.name} onChange={e => setCompForm(p => ({...p, name: e.target.value}))} />
          </div>
          <div style={{ marginBottom: 18 }}>
            <label className="fl" htmlFor="s-email">Account Email</label>
            <input id="s-email" className="field" type="email" value={compForm.email} onChange={e => setCompForm(p => ({...p, email: e.target.value}))} />
          </div>
          <button onClick={saveCompany} disabled={saving} style={{ padding: '11px 24px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13, marginBottom: 12 }}>Account Details</div>
            {[
              ['Plan', { solo: 'Solo', founder: 'Founder', standard: 'Standard' }[company?.plan] || company?.plan || 'Beta'],
              ['Company ID', company?.id || '—'],
              ['Member Since', company?.createdAt ? new Date(company.createdAt).toLocaleDateString() : '—'],
            ].map(([label, val]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--divider)', fontSize: 13 }}>
                <span style={{ color: 'var(--sub)' }}>{label}</span>
                <span style={{ fontWeight: 600, color: 'var(--navy)', fontFamily: label === 'Company ID' ? 'monospace' : 'inherit', fontSize: label === 'Company ID' ? 11 : 13 }}>{val}</span>
              </div>
            ))}
            {/* Support */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13, marginBottom: 8 }}>Support</div>
              <div style={{ fontSize: 13, color: 'var(--sub)', marginBottom: 6 }}>Need help? We typically respond within one business day.</div>
              <a href="mailto:support@jobhubco.com" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: 'var(--surface)', color: 'var(--navy)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                ✉️ support@jobhubco.com
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Password tab */}
      {tab === 'password' && (
        <div className="card" style={{ padding: mobile ? 18 : 24 }}>
          <div className="sh" style={{ marginBottom: 4 }}>Change Password</div>
          <div className="ss" style={{ marginBottom: 18 }}>Minimum 8 characters</div>
          <div style={{ marginBottom: 14 }}>
            <label className="fl" htmlFor="pw-cur">Current Password</label>
            <input id="pw-cur" className="field" type="password" value={pwForm.current} onChange={e => setPwForm(p => ({...p, current: e.target.value}))} placeholder="••••••••" />
            <div style={{ marginTop: 6, fontSize: 12 }}>
              <a href="/forgot-password" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>Forgot your password?</a>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label className="fl" htmlFor="pw-new">New Password</label>
            <input id="pw-new" className="field" type="password" value={pwForm.next} onChange={e => setPwForm(p => ({...p, next: e.target.value}))} placeholder="••••••••" />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label className="fl" htmlFor="pw-conf">Confirm New Password</label>
            <input id="pw-conf" className="field" type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({...p, confirm: e.target.value}))} placeholder="••••••••" />
          </div>
          <button onClick={changePassword} disabled={saving} style={{ padding: '11px 24px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            {saving ? 'Saving...' : 'Change Password'}
          </button>
        </div>
      )}

      {/* Billing tab */}
      {tab === 'billing' && (
        <div>
          {billingLoading ? (
            <div className="card" style={{ padding: 20 }}>{[1,2,3].map(i => <Skel key={i} h={44} mb={8} />)}</div>
          ) : billing ? (
            <>
              {/* Status card */}
              <div className="card" style={{ padding: mobile ? 18 : 24, marginBottom: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                  <div>
                    <div className="sh" style={{ marginBottom: 4 }}>Subscription</div>
                    <div className="ss">Your current plan and billing details</div>
                  </div>
                  {billing.status && (
                    <span className="badge" style={{ ...(STATUS_STYLES[billing.status] || STATUS_STYLES.active), fontSize: 12, padding: '4px 12px' }}>
                      {STATUS_STYLES[billing.status]?.label || billing.status}
                    </span>
                  )}
                </div>

                {/* Billing details */}
                {[
                  ['Plan', (() => {
                    const tierLabel = { solo: 'Solo', founder: 'Founder', standard: 'Standard' }[billing.tier];
                    const price = PLAN_PRICES[billing.tier]?.[billing.plan];
                    if (!tierLabel) return billing.plan || 'Beta';
                    return price ? `${tierLabel} — ${billing.plan === 'annual' ? 'Annual' : 'Monthly'} (${price})` : tierLabel;
                  })()],
                  ['Status', STATUS_STYLES[billing.status]?.label || billing.status || '—'],
                  billing.currentPeriodEnd && billing.hasSubscription && ['Next Bill Date', new Date(billing.currentPeriodEnd).toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' })],
                                    billing.trialEndsAt && billing.status === 'trialing' && ['Trial Ends', `${new Date(billing.trialEndsAt).toLocaleDateString()} (${Math.max(0, Math.ceil((new Date(billing.trialEndsAt) - Date.now()) / 86400000))} days left)`],
                  billing.cancelAtPeriodEnd && ['Cancels On', new Date(billing.currentPeriodEnd).toLocaleDateString()],
                ].filter(Boolean).map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--divider)', fontSize: 13 }}>
                    <span style={{ color: 'var(--sub)' }}>{label}</span>
                    <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{val}</span>
                  </div>
                ))}

                {/* Alerts */}
                {billing.needsPayment && (
                  <div style={{ marginTop: 14, background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
                    ⚠️ Payment required — update your card to keep access
                  </div>
                )}
                {billing.status === 'trialing' && (
                  <div style={{ marginTop: 14, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1e40af' }}>
                                        You're on a free trial — {billing.trialEndsAt ? Math.max(0, Math.ceil((new Date(billing.trialEndsAt) - Date.now()) / 86400000)) : 0} days remaining before billing starts
                  </div>
                )}
{billing.status === 'active' && billing.currentPeriodStart && (() => {
                    const days = Math.floor((Date.now() - new Date(billing.currentPeriodStart)) / 86400000);
                    const months = Math.floor(days / 30);
                    const label = months >= 1 ? `${months} month${months > 1 ? 's' : ''}` : `${days} day${days !== 1 ? 's' : ''}`;
                    return (
                                          <div style={{ marginTop: 14, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#166534' }}>
                                                                                     🎉 You've been a Jobhub member for <strong>{label}</strong>
  </div>
                    );
})()}
              </div>

              {/* Solo upgrade prompt — shows for any active Solo account, not just
                  ones with a real Stripe subscription (e.g. a comped account) */}
              {isSolo && billing.isActive && (
                <div className="card" style={{ padding: mobile ? 18 : 24, marginBottom: 14, background: '#eff6ff', border: '1px solid #93c5fd' }}>
                  <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 15, marginBottom: 4 }}>⬆️ Need time tracking, scheduling, or payroll?</div>
                  <div style={{ fontSize: 13, color: 'var(--sub)', marginBottom: 14 }}>
                    Upgrade to Founder or Standard to unlock employees, time tracking, scheduling, the employee portal, and payroll integrations. Nothing you've entered on Solo is lost — it's all still there the moment you upgrade.
                  </div>
                  {billing.hasSubscription ? (
                    <button onClick={openPortal} disabled={portalLoading} style={{ padding: '10px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                      {portalLoading ? 'Opening...' : 'Upgrade Plan →'}
                    </button>
                  ) : (
                    <div style={{ fontSize: 12, color: 'var(--sub)' }}>Pick Founder or Standard below to upgrade ↓</div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="card" style={{ padding: mobile ? 18 : 24 }}>
                <div className="sh" style={{ marginBottom: 4 }}>Billing Actions</div>
                <div className="ss" style={{ marginBottom: 16 }}>Manage your payment method, view invoices, or update your plan</div>

                {billing.hasSubscription ? (
                  <button onClick={openPortal} disabled={portalLoading} style={{ width: '100%', padding: '12px 0', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14, marginBottom: 10 }}>
                    {portalLoading ? 'Opening...' : '💳 Manage Billing & Payment Method'}
                  </button>
                ) : (
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--sub)', marginBottom: 14 }}>
                      {billing.isActive && billing.status !== 'trialing' ? 'Upgrade your plan:' : 'Choose a plan to get started:'}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr 1fr', gap: 10 }}>
                      {[
                        { tier: 'solo',     label: 'Solo',     desc: 'Solo operators, no employees', color: 'var(--muted)' },
                        { tier: 'founder',  label: 'Founder',  desc: 'First 50 customers — locked-in rate', color: 'var(--accent)' },
                        { tier: 'standard', label: 'Standard', desc: 'Full JobHub platform', color: 'var(--green)' },
                      ].map(({ tier, label, desc, color }) => (
                        <div key={tier} className="card" style={{ padding: 18, border: `2px solid ${color}` }}>
                          <div style={{ fontWeight: 800, color: 'var(--navy)', fontSize: 15, marginBottom: 4 }}>{label}</div>
                          <div style={{ fontSize: 12, color: 'var(--sub)', marginBottom: 12 }}>{desc}</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color, marginBottom: 2 }}>{PLAN_PRICES[tier].monthly}</div>
                          <div style={{ fontSize: 11, color: 'var(--sub)', marginBottom: 12 }}>or {PLAN_PRICES[tier].annual} billed annually</div>
                          <button onClick={() => startCheckout(tier, 'monthly')} disabled={saving} style={{ width: '100%', padding: '9px 0', background: color, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', marginBottom: 6, fontSize: 13 }}>Monthly</button>
                          <button onClick={() => startCheckout(tier, 'annual')} disabled={saving} style={{ width: '100%', padding: '9px 0', background: 'var(--surface)', color, border: `1px solid ${color}`, borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>Annual</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--sub)', marginTop: 10, textAlign: 'center' }}>
                  Billing managed securely by Stripe · JobHub never stores your card details
                </div>
              </div>
            </>
          ) : (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--sub)' }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>💳</div>
              <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>No billing info found</div>
              <div style={{ fontSize: 13 }}>Contact support@jobhubco.com for help</div>
            </div>
          )}
        </div>
      )}

      {/* Partner Referrals tab — admin only */}
{tab === 'referrals' && isAdmin && (
  <div>
    <div className="card" style={{ padding: mobile ? 18 : 24, marginBottom: 16 }}>
      <div className="sh" style={{ marginBottom: 4 }}>Partner Referral Report</div>
      <div className="ss">Bookkeeper & accountant referral tracking</div>
    </div>
    {referralsLoading ? (
      <div className="card" style={{ padding: 20 }}>{[1,2,3].map(i => <div key={i} style={{ height: 14, background: 'var(--divider)', borderRadius: 4, marginBottom: 10, animation: 'shimmer 1.4s infinite' }} />)}</div>
    ) : referrals.length === 0 ? (
      <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--sub)' }}>No referral data yet.</div>
    ) : (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Bookkeeper / Accountant</th>
                <th style={{ textAlign: 'right' }}>Total Referrals</th>
                <th style={{ textAlign: 'right' }}>First Payout</th>
                <th style={{ textAlign: 'right' }}>Active</th>
                <th style={{ textAlign: 'right' }}>Payout Owed</th>
                <th style={{ textAlign: 'right' }}>Total Earned</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map(r => (
                <tr key={r.referred_by}>
                  <td style={{ fontWeight: 700, color: 'var(--navy)' }}>{r.referred_by}</td>
                  <td style={{ textAlign: 'right' }}>{r.total_referrals}</td>
                  <td style={{ textAlign: 'right' }}>{r.first_payout_referrals}</td>
                  <td style={{ textAlign: 'right', color: 'var(--accent)' }}>{r.active_referrals}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--navy)' }}>${parseFloat(r.unpaid_payout).toFixed(2)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--sub)' }}>${parseFloat(r.total_payout).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </div>
)}

{/* Industry / Trade tab */}
      {tab === 'industry' && (
        <div>
          {!indConfig ? (
            <div className="card" style={{ padding: 20 }}>{[1,2,3].map(i => <Skel key={i} h={44} mb={8} />)}</div>
          ) : (
            <>
              <div className="card" style={{ padding: mobile ? 18 : 24, marginBottom: 14 }}>
                <div className="sh" style={{ marginBottom: 4 }}>Your Trade</div>
                <div className="ss" style={{ marginBottom: 14 }}>Changing your trade resets roles and categories to their defaults</div>
                <div style={{ display:'grid', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(3,1fr)', gap:8, marginBottom:14 }}>
                  {Object.entries(indConfig.allIndustries || {}).map(([key, ind]) => (
                    <button key={key} onClick={() => { setSelectedInd(key); setEditRoles([...ind.roles]); setEditCats([...ind.costCategories]); }}
                      style={{ padding:'10px 8px', borderRadius:10, border:`2px solid ${selectedInd===key?'var(--accent)':'var(--border)'}`, background:selectedInd===key?'#eff6ff':'#fff', cursor:'pointer', textAlign:'center', color:selectedInd===key?'var(--accent)':'var(--navy)' }}>
                      <div style={{ fontSize:20, marginBottom:3 }}>{ind.icon}</div>
                      <div style={{ fontSize:11, fontWeight:700 }}>{ind.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="card" style={{ padding: mobile ? 18 : 24, marginBottom: 14 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                  <div><div className="sh" style={{ marginBottom:2 }}>Employee Roles</div><div className="ss">Used in time tracking and payroll reports</div></div>
                  <button onClick={resetToDefaults} style={{ fontSize:11, color:'var(--sub)', background:'none', border:'1px solid var(--border)', borderRadius:7, padding:'5px 10px', cursor:'pointer', fontWeight:600 }}>Reset defaults</button>
                </div>
                {editRoles.map((r,i) => (
                  <div key={i} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
                    <input className="field" value={r} onChange={e=>setEditRoles(p=>p.map((v,j)=>j===i?e.target.value:v))} style={{ flex:1, marginBottom:0 }} />
                    <button onClick={()=>setEditRoles(p=>p.filter((_,j)=>j!==i))} style={{ padding:'8px 10px', background:'#fee2e2', color:'var(--red)', border:'none', borderRadius:7, cursor:'pointer', fontWeight:700 }}>✕</button>
                  </div>
                ))}
                <div style={{ display:'flex', gap:8, marginTop:8 }}>
                  <input className="field" value={newRole} onChange={e=>setNewRole(e.target.value)} placeholder="Add role..." style={{ flex:1, marginBottom:0 }}
                    onKeyDown={e=>{if(e.key==='Enter'&&newRole.trim()){setEditRoles(p=>[...p,newRole.trim()]);setNewRole('');}}} />
                  <button onClick={()=>{if(newRole.trim()){setEditRoles(p=>[...p,newRole.trim()]);setNewRole('');}}} style={{ padding:'8px 14px', background:'var(--accent)', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer' }}>+ Add</button>
                </div>
              </div>

              <div className="card" style={{ padding: mobile ? 18 : 24, marginBottom: 14 }}>
                <div className="sh" style={{ marginBottom:2 }}>Cost Categories</div>
                <div className="ss" style={{ marginBottom:12 }}>Used in cost matching and reports</div>
                {editCats.map((c,i) => (
                  <div key={i} style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
                    <input className="field" value={c} onChange={e=>setEditCats(p=>p.map((v,j)=>j===i?e.target.value:v))} style={{ flex:1, marginBottom:0 }} />
                    <button onClick={()=>setEditCats(p=>p.filter((_,j)=>j!==i))} style={{ padding:'8px 10px', background:'#fee2e2', color:'var(--red)', border:'none', borderRadius:7, cursor:'pointer', fontWeight:700 }}>✕</button>
                  </div>
                ))}
                <div style={{ display:'flex', gap:8, marginTop:8 }}>
                  <input className="field" value={newCat} onChange={e=>setNewCat(e.target.value)} placeholder="Add category..." style={{ flex:1, marginBottom:0 }}
                    onKeyDown={e=>{if(e.key==='Enter'&&newCat.trim()){setEditCats(p=>[...p,newCat.trim()]);setNewCat('');}}} />
                  <button onClick={()=>{if(newCat.trim()){setEditCats(p=>[...p,newCat.trim()]);setNewCat('');}}} style={{ padding:'8px 14px', background:'var(--accent)', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer' }}>+ Add</button>
                </div>
              </div>

              <button onClick={saveIndustry} disabled={saving} style={{ width:'100%', padding:12, background:'var(--navy)', color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer', fontSize:14 }}>
                {saving ? 'Saving...' : '💾 Save Trade Settings'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Sign out — shown on mobile since sidebar isn't visible */}
      {mobile && (
        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
          <button onClick={logout} style={{ width: '100%', padding: 12, background: '#fef2f2', color: 'var(--red)', border: '1px solid #fca5a5', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            Sign Out
          </button>
        </div>
      )}

      <RequestFeatureBox />
    </div>
  );
}

// ─── INTEGRATIONS (lazy) ──────────────────────────────────────────────────────
const IntegrationsPage = React.lazy(() => import('./pages/Integrations'));

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      login(data.token, data.company);
      window.location.href = '/';
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1a2535 0%,#1a3a5c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/jobhub-logo-dark.png" alt="JobHub" style={{ height: 100, width: 'auto', objectFit: 'contain', marginBottom: 12 }} />
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>Job costing for contractors</p>
        </div>
        <div className="card" style={{ padding: 32 }}>
          {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: 'var(--red)', fontSize: 13, marginBottom: 16 }}>{error}</div>}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}><label className="fl">Email</label><input className="field" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required /></div>
            <div style={{ marginBottom: 20 }}><label className="fl">Password</label><input className="field" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required /></div>
            <button type="submit" disabled={loading} style={{ width: '100%', padding: 13, background: loading ? '#9ca3af' : 'var(--navy)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Signing in...' : 'Sign in →'}
            </button>
          </form>
          <div style={{ textAlign: 'center', marginTop: 16 }}><a href="/forgot-password" style={{ fontSize: 13, color: 'var(--sub)', textDecoration: 'none' }}>Forgot password?</a></div>
          <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 16, lineHeight: 1.6 }}>Demo: demo@jobhub.com / DemoPassword123!</p>
        </div>
      </div>
    </div>
  );
}

// ─── Setup Password (post-payment) ────────────────────────────────────────────
function SetupPassword() {
  const { login } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id');

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (password !== confirm) return setError('Passwords do not match');
    if (!sessionId) return setError('Invalid setup link. Please contact support@jobhubco.com');
    setLoading(true); setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/setup-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Setup failed');
      login(data.token, data.company);
      setDone(true);
      setTimeout(() => { window.location.href = '/onboarding'; }, 1500);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  if (!sessionId) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1a2535 0%,#1a3a5c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div className="card" style={{ padding: 32, maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ color: '#111827', marginBottom: 8 }}>Invalid Setup Link</h2>
        <p style={{ color: '#6b7280', fontSize: 14 }}>This link is missing required information. Please contact <a href="mailto:support@jobhubco.com">support@jobhubco.com</a> and we'll get you set up.</p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1a2535 0%,#1a3a5c 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--accent)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, marginBottom: 12 }}>🎉</div>
          <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: '0 0 4px' }}>Welcome to JobHub!</h1>
          <p style={{ color: 'var(--muted)', fontSize: 13 }}>Payment confirmed — set your password to get started</p>
        </div>
        <div className="card" style={{ padding: 32 }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
              <div style={{ fontWeight: 700, color: '#111827', marginBottom: 4 }}>Password set!</div>
              <div style={{ color: '#6b7280', fontSize: 13 }}>Taking you to onboarding...</div>
            </div>
          ) : (
            <>
              {error && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: 'var(--red)', fontSize: 13, marginBottom: 16 }}>{error}</div>}
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                  <label className="fl">Password</label>
                  <input className="field" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" required />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label className="fl">Confirm password</label>
                  <input className="field" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" required />
                </div>
                <button type="submit" disabled={loading} style={{ width: '100%', padding: 13, background: loading ? '#9ca3af' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer' }}>
                  {loading ? 'Setting up...' : 'Set Password & Enter JobHub →'}
                </button>
              </form>
              <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 16 }}>Having trouble? Email <a href="mailto:support@jobhubco.com" style={{ color: '#9ca3af' }}>support@jobhubco.com</a></p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
function PrivateRoute({ children }) {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" />;
}

// Guards routes that don't exist on the Solo plan (Time Tracking, Schedule) —
// typing one of these URLs directly redirects to the dashboard instead of
// rendering a broken/403'd page.
function SoloBlockedRoute({ children }) {
  const { company } = useAuth();
  if (company?.plan === 'solo') return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <>
      <style>{G}</style>
      <AuthProvider>
        <EmpAuthProvider>
        <IndustryProvider>
        <ToastProvider>
          <BrowserRouter>
            <ScrollToTop />
            <ErrorBoundary>
              <Routes>
                <Route path="/login"          element={<Login />} />
                <Route path="/setup-password"  element={<SetupPassword />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password"  element={<ResetPassword />} />
                <Route path="/check-email"     element={<CheckEmail />} />
                <Route path="/verify-email"    element={<VerifyEmail />} />
                <Route path="/employee/setup" element={<EmployeeSetup />} />
                <Route path="/employee"        element={<EmployeeApp />} />
                <Route path="/affiliate/portal" element={<AffiliatePortal />} />
                <Route path="/*" element={
                  <PrivateRoute>
                    <Shell>
                      <Suspense fallback={<div style={{ padding: 40, color: 'var(--sub)', fontSize: 14 }}>Loading...</div>}>
                        <Routes>
                          <Route path="/"              element={<Dashboard />} />
                          <Route path="/jobs"          element={<Jobs />} />
                          <Route path="/costs"         element={<CostMatching />} />
                          <Route path="/jobcost"       element={<JobCostSettings />} />
                          <Route path="/time"          element={<SoloBlockedRoute><TimeTracking /></SoloBlockedRoute>} />
                          <Route path="/schedule" element={<SoloBlockedRoute><Schedule /></SoloBlockedRoute>} />
                          <Route path="/clients"       element={<Clients />} />
                          <Route path="/reports"       element={<Reports />} />
                          <Route path="/integrations"  element={<IntegrationsPage />} />
                          <Route path="/settings"      element={<Settings />} />
                          <Route path="/onboarding"    element={<Onboarding />} />
                        </Routes>
                      </Suspense>
                    </Shell>
                  </PrivateRoute>
                } />
              </Routes>
            </ErrorBoundary>
          </BrowserRouter>
        </ToastProvider>
        </IndustryProvider>
        </EmpAuthProvider>
      </AuthProvider>
    </>
  );
}
